import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  AlertCircle,
  Apple,
  BarChart3,
  Boxes,
  Camera,
  CreditCard,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Compass,
  Copy,
  Cpu,
  Grid,
  Headphones,
  Heart,
  History,
  Home,
  Languages,
  LifeBuoy,
  Link,
  Mail,
  Menu,
  MessageCircle,
  Minus,
  MoreHorizontal,
  Maximize2,
  PackageCheck,
  Phone,
  Play,
  Plus,
  QrCode,
  RotateCcw,
  Search,
  Send,
  Settings,
  Share2,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Truck,
  UserRound,
  Volume2,
  VolumeX,
  Wallet,
  X,
  ZoomIn
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  completeOrderLogistics,
  confirmOrderPayment,
  createAdminProduct,
  createOrder as apiCreateOrder,
  fetchAdminProducts,
  fetchDashboard,
  fetchCustomerProfile,
  fetchProduct,
  fetchProducts,
  fetchPlatformOverview,
  fetchPlatformSecurity,
  formatMoney,
  lookupOrder,
  markReviewHelpful,
  registerCustomerProfile,
  reviewSellerApplication,
  clearAdminSession,
  confirmVerification,
  submitReview,
  submitSellerApplication,
  startVerification,
  storeAdminSession,
  track,
  updateAdminProduct,
  updateMerchantProfile,
  uploadMerchantQr,
  uploadOrderLogisticsQr,
  uploadPaymentProof,
  uploadProductImages,
  uploadProductVideo
} from "./lib/api";
import type {
  CustomerProfile,
  Dashboard,
  Merchant,
  Order,
  PlatformOverview,
  PlatformSecurityCenter,
  Product,
  ProductBlock,
  ProductSku,
  ProductSummary
} from "./types";

const sourceChannel = new URLSearchParams(window.location.search).get("channel") || "direct";
const requestedProductSlug = new URLSearchParams(window.location.search).get("product");
const requestedAccount = new URLSearchParams(window.location.search).get("account");
const requestedSearch = new URLSearchParams(window.location.search).get("search");
const requestedCart = new URLSearchParams(window.location.search).get("cart");
const initialProductSlug = requestedProductSlug || "iphone-16-pro-max";

type PortalKind = "customer" | "seller" | "admin";

function detectPortal(): PortalKind {
  const portal = new URLSearchParams(window.location.search).get("portal");
  const host = window.location.hostname.toLowerCase();
  if (portal === "seller" || host.startsWith("seller.")) return "seller";
  if (portal === "admin" || host.startsWith("admin.")) return "admin";
  return "customer";
}

const activePortal = detectPortal();
const initialPage =
  activePortal !== "customer"
    ? "home"
    : requestedAccount
      ? "account"
      : requestedProductSlug
        ? "product"
        : requestedSearch
          ? "listing"
          : requestedCart
            ? "cartConfirm"
            : "home";

const socialProfiles = {
  facebook: {
    label: "Facebook",
    color: "#1877f2",
    profileUrl: "https://www.facebook.com/yourbrand",
    shareUrl: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  },
  tiktok: {
    label: "TikTok",
    color: "#111111",
    profileUrl: "https://www.tiktok.com/@yourbrand"
  },
  messenger: {
    label: "Messenger",
    color: "#6c4cff",
    profileUrl: "https://m.me/yourbrand"
  },
  whatsapp: {
    label: "WhatsApp",
    color: "#24c35a",
    profileUrl: "https://wa.me/8562055551234"
  }
} as const;

type SocialChannel = keyof typeof socialProfiles;
type AccountOrderTab = "all" | "pay" | "share" | "ship" | "receive" | "review";
type AccountToolKey = "orders" | "logistics" | "addresses" | "wallet" | "saved" | "stores" | "history" | "refunds" | "settings";
type AccountEntry = "profile" | "orders" | "logistics" | "service";
type MenuTarget =
  | { type: "product"; slug: string }
  | { type: "listing"; query: string }
  | { type: "account"; entry: AccountEntry }
  | { type: "support"; channel: "whatsapp" | "messenger" | "phone" };
const accountIntentFor = (entry?: string): { tab: AccountOrderTab; tool: AccountToolKey | null; profileOpen: boolean } =>
  entry === "orders" ? { tab: "all", tool: "orders", profileOpen: false } :
  entry === "logistics" ? { tab: "receive", tool: "logistics", profileOpen: false } :
  entry === "service" ? { tab: "all", tool: "refunds", profileOpen: false } :
  { tab: "all", tool: null, profileOpen: false };

type CartItem = {
  id: string;
  productId: string;
  slug: string;
  title: string;
  subtitle: string;
  skuId: string;
  color: string;
  storage: string;
  unitPrice: number;
  currency: string;
  quantity: number;
  merchantName: string;
};

function cartItemFromProduct(product: Product, sku: ProductSku, quantity = 1): CartItem {
  return {
    id: `${product.id}:${sku.id}`,
    productId: product.id,
    slug: product.slug,
    title: product.title,
    subtitle: product.subtitle,
    skuId: sku.id,
    color: sku.color,
    storage: sku.storage,
    unitPrice: product.price + sku.price_delta,
    currency: product.currency,
    quantity,
    merchantName: product.merchant?.name || "YourBrand"
  };
}

function readStoredCart(key: string): CartItem[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function currentProductUrl(channel: SocialChannel, slug?: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("channel", channel);
  if (slug) url.searchParams.set("product", slug);
  return url.toString();
}

function productShareText(product: Product, channel: SocialChannel) {
  const url = currentProductUrl(channel, product.slug);
  return `${product.title} - ${product.subtitle}\n价格:${formatMoney(product.price, product.currency)}\n产品链接:${url}`;
}

async function copyProductInfo(product: Product, channel: SocialChannel) {
  await navigator.clipboard?.writeText(productShareText(product, channel));
}

type MenuLeaf = {
  label: string;
  slug: string;
  target?: MenuTarget;
};

type MenuChild = {
  label: string;
  detail: string;
  slug?: string;
  target?: MenuTarget;
  children: MenuLeaf[];
};

const nav: Array<{
  label: string;
  columns: Array<{
    title: string;
    children: MenuChild[];
  }>;
}> = [
  {
    label: "产品",
    columns: [
      {
        title: "手机数码",
        children: [
          {
            label: "旗舰手机",
            detail: "iOS / Android 旗舰",
            slug: "iphone-16-pro-max",
            children: [
              { label: "iPhone 16 Pro Max", slug: "iphone-16-pro-max" },
              { label: "iPhone 16 Pro", slug: "iphone-16-pro" },
              { label: "Galaxy S24 Ultra", slug: "galaxy-s24-ultra" },
              { label: "Xiaomi 14 Ultra", slug: "xiaomi-14-ultra" }
            ]
          },
          {
            label: "智能音频",
            detail: "耳机、音箱",
            slug: "airpods-pro-2",
            children: [
              { label: "AirPods Pro 2", slug: "airpods-pro-2" },
              { label: "降噪耳机", slug: "airpods-pro-2" },
              { label: "蓝牙音箱", slug: "airpods-pro-2" }
            ]
          },
          {
            label: "智能穿戴",
            detail: "手表与健康",
            slug: "apple-watch-series-10",
            children: [
              { label: "Apple Watch Series 10", slug: "apple-watch-series-10" },
              { label: "运动手表", slug: "apple-watch-series-10" },
              { label: "健康配件", slug: "apple-watch-series-10" }
            ]
          }
        ]
      },
      {
        title: "配件专区",
        children: [
          {
            label: "MagSafe",
            detail: "充电器、支架",
            slug: "magsafe-charger",
            children: [
              { label: "无线充", slug: "magsafe-charger", target: { type: "product", slug: "magsafe-charger" } },
              { label: "磁吸支架", slug: "magsafe-charger", target: { type: "listing", query: "磁吸支架" } },
              { label: "车载配件", slug: "magsafe-charger", target: { type: "listing", query: "车载配件" } }
            ]
          },
          {
            label: "保护配件",
            detail: "壳膜、挂绳",
            slug: "magsafe-charger",
            children: [
              { label: "手机壳", slug: "magsafe-charger" },
              { label: "钢化膜", slug: "magsafe-charger" },
              { label: "防摔套装", slug: "magsafe-charger" }
            ]
          }
        ]
      }
    ]
  },
  {
    label: "评价",
    columns: [
      {
        title: "评价管理",
        children: [
          { label: "真实买家秀", detail: "图片与渠道", slug: "iphone-16-pro-max", children: [{ label: "Facebook 好评", slug: "iphone-16-pro-max" }, { label: "TikTok 买家秀", slug: "airpods-pro-2" }, { label: "WhatsApp 评价", slug: "magsafe-charger" }] },
          { label: "评分分布", detail: "星级与标签", slug: "iphone-16-pro", children: [{ label: "五星产品", slug: "iphone-16-pro" }, { label: "高复购配件", slug: "magsafe-charger" }, { label: "待审核样例", slug: "apple-watch-series-10" }] }
        ]
      }
    ]
  },
  {
    label: "品牌故事",
    columns: [
      {
        title: "信任资产",
        children: [
          { label: "正品保障", detail: "授权、保修", slug: "iphone-16-pro-max", target: { type: "listing", query: "正品保障" }, children: [{ label: "验机流程", slug: "iphone-16-pro-max", target: { type: "listing", query: "验机流程" } }, { label: "发票", slug: "galaxy-s24-ultra", target: { type: "listing", query: "发票" } }, { label: "售后", slug: "xiaomi-14-ultra", target: { type: "account", entry: "service" } }] },
          { label: "本地服务", detail: "万象配送", slug: "magsafe-charger", target: { type: "account", entry: "logistics" }, children: [{ label: "同城", slug: "magsafe-charger", target: { type: "account", entry: "logistics" } }, { label: "跨区", slug: "apple-watch-series-10", target: { type: "account", entry: "logistics" } }, { label: "门店自提", slug: "airpods-pro-2", target: { type: "account", entry: "logistics" } }] }
        ]
      }
    ]
  },
  {
    label: "帮助中心",
    columns: [
      {
        title: "订单服务",
        children: [
          { label: "订单查询", detail: "订单号 + 手机后四位", slug: "iphone-16-pro-max", target: { type: "account", entry: "orders" }, children: [{ label: "物流时间线", slug: "iphone-16-pro-max", target: { type: "account", entry: "logistics" } }, { label: "退款状态", slug: "apple-watch-series-10", target: { type: "account", entry: "service" } }, { label: "售后单", slug: "airpods-pro-2", target: { type: "account", entry: "service" } }] },
          { label: "客服入口", detail: "聊天工具", slug: "magsafe-charger", target: { type: "account", entry: "service" }, children: [{ label: "WhatsApp", slug: "magsafe-charger", target: { type: "support", channel: "whatsapp" } }, { label: "Messenger", slug: "iphone-16-pro", target: { type: "support", channel: "messenger" } }, { label: "电话", slug: "galaxy-s24-ultra", target: { type: "support", channel: "phone" } }] }
        ]
      }
    ]
  }
];

function App() {
  const [page, setPage] = useState<"home" | "product" | "account" | "listing" | "cartConfirm">(initialPage);
  const [listingQuery, setListingQuery] = useState(requestedSearch || "gaming");
  const [product, setProduct] = useState<Product | null>(null);
  const [productList, setProductList] = useState<ProductSummary[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedStorage, setSelectedStorage] = useState("");
  const [activeMedia, setActiveMedia] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [sellerHomeOpen, setSellerHomeOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>(() => readStoredCart("lao_cart_items"));
  const [savedItems, setSavedItems] = useState<CartItem[]>(() => readStoredCart("lao_saved_items"));
  const [cartConfirmItem, setCartConfirmItem] = useState<CartItem | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [accountIntent, setAccountIntent] = useState(() => accountIntentFor(requestedAccount || undefined));
  const [toast, setToast] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProduct = async (slug: string, options: { showProduct?: boolean; syncUrl?: boolean } = {}) => {
    const { showProduct = true, syncUrl = true } = options;
    setLoadError(null);
    const data = await fetchProduct(slug);
    setProduct(data);
    setSelectedColor(data.skus[0]?.color ?? "");
    setSelectedStorage(data.skus[0]?.storage ?? "");
    setActiveMedia(0);
    setQuantity(1);
    setOrder(null);
    if (showProduct) setPage("product");
    if (syncUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set("product", data.slug);
      url.searchParams.delete("cart");
      url.searchParams.delete("account");
      url.searchParams.delete("search");
      window.history.replaceState({}, "", url);
    }
    track("product_view", sourceChannel, { slug: data.slug });
    return data;
  };

  const handleProductSelect = (slug: string) => {
    loadProduct(slug)
      .then((data) => {
        setToast(`已切换到 ${data.title}`);
      })
      .catch((error) => setLoadError(error.message));
  };

  const handleHome = () => {
    setPage("home");
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    url.searchParams.delete("account");
    url.searchParams.delete("search");
    url.searchParams.delete("cart");
    window.history.replaceState({}, "", url);
    setToast("已回到商城首页");
  };

  const handleAccountOpen = (entry: AccountEntry = "profile") => {
    setAccountIntent(accountIntentFor(entry));
    setPage("account");
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    url.searchParams.delete("search");
    url.searchParams.delete("cart");
    url.searchParams.set("account", entry);
    window.history.replaceState({}, "", url);
    fetchCustomerProfile().then(setCustomerProfile).catch(() => undefined);
  };

  const handleListingOpen = (query: string) => {
    const nextQuery = query.trim() || "全部商品";
    setListingQuery(nextQuery);
    setPage("listing");
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    url.searchParams.delete("account");
    url.searchParams.delete("cart");
    url.searchParams.set("search", nextQuery);
    window.history.replaceState({}, "", url);
    setToast(`已进入 ${nextQuery} 的商品列表`);
  };

  useEffect(() => {
    loadProduct(initialProductSlug, { showProduct: Boolean(requestedProductSlug), syncUrl: Boolean(requestedProductSlug) }).catch((error) => setLoadError(error.message));
    fetchProducts().then(setProductList).catch(() => undefined);
    fetchDashboard().then(setDashboard).catch(() => undefined);
    fetchCustomerProfile().then(setCustomerProfile).catch(() => undefined);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("lao_cart_items", JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem("lao_saved_items", JSON.stringify(savedItems));
  }, [savedItems]);

  const features = useMemo(() => product?.blocks.filter((block) => block.type === "feature") ?? [], [product]);
  const specs = useMemo(() => product?.blocks.find((block) => block.type === "specs"), [product]);
  const comparison = useMemo(() => product?.blocks.find((block) => block.type === "comparison"), [product]);
  const faqs = useMemo(() => product?.blocks.filter((block) => block.type === "faq") ?? [], [product]);
  const selectedSku = useMemo(() => {
    if (!product) return null;
    return (
      product.skus.find((sku) => sku.color === selectedColor && sku.storage === selectedStorage) ??
      product.skus.find((sku) => sku.storage === selectedStorage) ??
      product.skus.find((sku) => sku.color === selectedColor) ??
      product.skus[0] ??
      null
    );
  }, [product, selectedColor, selectedStorage]);

  if (loadError) {
    return (
      <div className="error-state">
        <AlertCircle />
        <h1>应用启动失败</h1>
        <p>{loadError}</p>
      </div>
    );
  }

  if (!product || !selectedSku) {
    return <Skeleton />;
  }

  const totalPrice = product.price + selectedSku.price_delta;
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const addCartItem = (item: CartItem) => {
    setCartItems((current) => {
      const existing = current.find((cartItem) => cartItem.id === item.id);
      if (existing) {
        return current.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: Math.min(99, cartItem.quantity + item.quantity) } : cartItem
        );
      }
      return [...current, item];
    });
    setSavedItems((current) => current.filter((saved) => saved.id !== item.id));
  };

  const saveCartItem = (item: CartItem) => {
    setSavedItems((current) => {
      if (current.some((saved) => saved.id === item.id)) return current;
      return [...current, { ...item, quantity: 1 }];
    });
    setCartItems((current) => current.filter((cartItem) => cartItem.id !== item.id));
  };

  const openCartConfirmation = (item: CartItem) => {
    setCartConfirmItem(item);
    setPage("cartConfirm");
    setCartOpen(false);
    window.scrollTo(0, 0);
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    url.searchParams.delete("account");
    url.searchParams.delete("search");
    url.searchParams.set("cart", "added");
    window.history.replaceState({}, "", url);
  };

  const handleAddCurrentToCart = () => {
    const item = cartItemFromProduct(product, selectedSku, quantity);
    addCartItem(item);
    openCartConfirmation(item);
    setToast(`${product.title} 已加入购物车`);
  };

  const handleSaveCurrent = () => {
    saveCartItem(cartItemFromProduct(product, selectedSku, 1));
    setToast(`${product.title} 已保存到稍后购买`);
  };

  const handleSummaryAction = async (slug: string, action: "view" | "cart" | "save" | "buy") => {
    const data = await loadProduct(slug, { showProduct: action === "view" || action === "buy", syncUrl: action === "view" || action === "buy" });
    const sku = data.skus[0];
    if (!sku) return;
    if (action === "view") {
      setSellerHomeOpen(false);
      return;
    }
    const item = cartItemFromProduct(data, sku, 1);
    if (action === "cart") {
      addCartItem(item);
      openCartConfirmation(item);
      setToast(`${data.title} 已加入购物车`);
      return;
    }
    if (action === "save") {
      saveCartItem(item);
      setToast(`${data.title} 已保存到稍后购买`);
      return;
    }
    setSelectedColor(sku.color);
    setSelectedStorage(sku.storage);
    setQuantity(1);
    setSellerHomeOpen(false);
    setCheckoutOpen(true);
  };

  const checkoutCartItem = async (item: CartItem) => {
    const data = await loadProduct(item.slug);
    const sku = data.skus.find((candidate) => candidate.id === item.skuId) ?? data.skus[0];
    if (!sku) return;
    setSelectedColor(sku.color);
    setSelectedStorage(sku.storage);
    setQuantity(item.quantity);
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?channel=share&share_id=demo-share`;
    await navigator.clipboard?.writeText(shareUrl);
    setToast("分享链接已复制");
    track("share_link_copy", sourceChannel, { shareUrl });
  };

  const openSocialChannel = async (channel: SocialChannel) => {
    const profile = socialProfiles[channel];
    const productUrl = currentProductUrl(channel);
    const text = productShareText(product, channel);
    await copyProductInfo(product, channel).catch(() => undefined);
    track("social_open", channel, { productUrl });

    if (channel === "facebook") {
      window.open(socialProfiles.facebook.shareUrl(productUrl), "_blank", "noopener,noreferrer");
      setToast("Facebook 分享页已打开,产品信息已复制");
      return;
    }

    if (channel === "whatsapp") {
      window.open(`${profile.profileUrl}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      setToast("WhatsApp 已打开,产品信息已带入");
      return;
    }

    if (channel === "messenger") {
      window.open(`${profile.profileUrl}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      setToast("Messenger 已打开,产品信息已复制");
      return;
    }

    window.open(profile.profileUrl, "_blank", "noopener,noreferrer");
    setToast("TikTok 主页已打开,产品信息已复制");
  };

  const openSupportChannel = (channel: "whatsapp" | "messenger" | "phone") => {
    if (channel === "phone") {
      window.open("tel:+8562055551234", "_self");
      setToast("客服电话已打开:+856 20 5555 1234");
      return;
    }
    void openSocialChannel(channel);
  };

  const confirmedCartItem = cartConfirmItem ?? cartItems[cartItems.length - 1] ?? cartItemFromProduct(product, selectedSku, 1);
  const cartSubtotal = cartItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const isMarketplaceShell = page === "home" || page === "listing" || page === "cartConfirm";

  if (activePortal === "seller") {
    return (
      <SellerPortalApp
        product={product}
        selectedSku={selectedSku}
        totalPrice={totalPrice}
        dashboard={dashboard}
        onProductPublished={(published) => {
          setProduct(published);
          setSelectedColor(published.skus[0]?.color ?? "");
          setSelectedStorage(published.skus[0]?.storage ?? "");
          setActiveMedia(0);
          fetchProducts().then(setProductList).catch(() => undefined);
        }}
        onMerchantUpdated={(merchant) => {
          setProduct((current) => (current ? { ...current, merchant } : current));
        }}
      />
    );
  }

  if (activePortal === "admin") {
    return (
      <PlatformPortalApp
        product={product}
        selectedSku={selectedSku}
        totalPrice={totalPrice}
        dashboard={dashboard}
        onProductPublished={(published) => {
          setProduct(published);
          setSelectedColor(published.skus[0]?.color ?? "");
          setSelectedStorage(published.skus[0]?.storage ?? "");
          setActiveMedia(0);
          fetchProducts().then(setProductList).catch(() => undefined);
        }}
        onMerchantUpdated={(merchant) => {
          setProduct((current) => (current ? { ...current, merchant } : current));
        }}
      />
    );
  }

  return (
    <div className={isMarketplaceShell ? "app-shell home-shell" : "app-shell"}>
      <main className={isMarketplaceShell ? "store-board home-board" : "store-board"}>
        <Header
          productList={productList}
          onHome={handleHome}
          onProductSelect={handleProductSelect}
          onSearchResults={handleListingOpen}
          onAccountOpen={handleAccountOpen}
          onSupportOpen={openSupportChannel}
          onCartOpen={() => setCartOpen(true)}
          onMenuOpen={() => setMobileMenuOpen(true)}
          cartCount={cartCount}
        />

        {page === "account" ? (
          <CustomerAccountPage
            profile={customerProfile}
            products={productList}
            savedItems={savedItems}
            cartItems={cartItems}
            initialTab={accountIntent.tab}
            initialTool={accountIntent.tool}
            initialProfileOpen={accountIntent.profileOpen}
            onRegister={async (payload) => {
              const updated = await registerCustomerProfile(payload);
              setCustomerProfile(updated);
              setToast("顾客账号资料已保存");
            }}
            onProductSelect={handleProductSelect}
            onOpenCart={() => setCartOpen(true)}
          />
        ) : page === "listing" ? (
          <MarketplaceListingPage
            query={listingQuery}
            products={productList}
            onProductSelect={handleProductSelect}
            onProductAction={handleSummaryAction}
          />
        ) : page === "cartConfirm" ? (
          <AddedToCartPage
            item={confirmedCartItem}
            cartItems={cartItems}
            subtotal={cartSubtotal}
            products={productList}
            onCheckout={() => checkoutCartItem(cartItems.find((item) => item.id === confirmedCartItem.id) ?? confirmedCartItem)}
            onOpenCart={() => setCartOpen(true)}
            onProductSelect={handleProductSelect}
            onProductAction={handleSummaryAction}
            onQuantityChange={(id, nextQuantity) =>
              setCartItems((current) =>
                current.map((item) => (item.id === id ? { ...item, quantity: Math.max(1, Math.min(99, nextQuantity)) } : item))
              )
            }
            onRemove={(id) => setCartItems((current) => current.filter((item) => item.id !== id))}
          />
        ) : page === "home" ? (
          <MarketplaceHome
            products={productList}
            featuredProduct={product}
            cartCount={cartCount}
            onProductSelect={handleProductSelect}
            onProductAction={handleSummaryAction}
            onOpenCart={() => setCartOpen(true)}
            onAccountOpen={handleAccountOpen}
          />
        ) : (
          <>
            <AmazonProductDetailPage
              product={product}
              productList={productList}
              selectedSku={selectedSku}
              selectedColor={selectedColor}
              selectedStorage={selectedStorage}
              activeMedia={activeMedia}
              quantity={quantity}
              totalPrice={totalPrice}
              specs={specs}
              faqs={faqs}
              onMediaChange={setActiveMedia}
              onColorChange={setSelectedColor}
              onStorageChange={setSelectedStorage}
              onQuantityChange={setQuantity}
              onCheckout={() => setCheckoutOpen(true)}
              onAddToCart={handleAddCurrentToCart}
              onSaveForLater={handleSaveCurrent}
              onSellerHomeOpen={() => setSellerHomeOpen(true)}
              onProductSelect={handleProductSelect}
              onProductUpdate={setProduct}
              onOrder={setOrder}
              onShare={handleShare}
              onSocialOpen={openSocialChannel}
            />
        <section className="social-strip" id="section-2">
          {(Object.keys(socialProfiles) as SocialChannel[]).map((channel) => (
            <button
              className="social-pill"
              style={{ "--accent": socialProfiles[channel].color } as React.CSSProperties}
              key={channel}
              onClick={() => openSocialChannel(channel)}
            >
              <span>{socialProfiles[channel].label.slice(0, 1)}</span>
              {socialProfiles[channel].label}
            </button>
          ))}
          <div className="share-actions">
            <span>分享此产品</span>
            <button onClick={handleShare} aria-label="复制分享链接">
              <Link size={16} />
            </button>
            <button aria-label="生成二维码" onClick={() => setToast("二维码已生成")}>
              <QrCode size={16} />
            </button>
            <button aria-label="更多分享方式">
              <MoreHorizontal size={16} />
            </button>
          </div>
        </section>

        <section className="hero-grid" id="section-3">
          <ProductGallery product={product} activeMedia={activeMedia} onMediaChange={setActiveMedia} />
          <ProductPanel
            product={product}
            selectedSku={selectedSku}
            selectedColor={selectedColor}
            selectedStorage={selectedStorage}
            onColorChange={setSelectedColor}
            onStorageChange={setSelectedStorage}
            totalPrice={totalPrice}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onCheckout={() => setCheckoutOpen(true)}
            onAddToCart={handleAddCurrentToCart}
            onSaveForLater={handleSaveCurrent}
            onSellerHomeOpen={() => setSellerHomeOpen(true)}
          />
        </section>

        <TrustStrip />
        <FeatureGrid features={features} />

        <section className="content-grid" id="section-10">
          <ProductDetails product={product} />
          <VideoCard product={product} />
          {specs && <SpecsCard block={specs} />}
          {comparison && <ComparisonCard block={comparison} />}
        </section>

        <section className="review-order-grid" id="section-14">
          <RatingSummary product={product} />
          <ReviewList product={product} onProductUpdate={setProduct} />
          <FAQList faqs={faqs} />
          <OrderLookup onOrder={setOrder} />
          <ShareCard product={product} onShare={handleShare} onSocialOpen={openSocialChannel} />
        </section>

        <RecommendedProducts productList={productList} currentSlug={product.slug} onProductSelect={handleProductSelect} />
          </>
        )}
        <Footer />
      </main>

      {checkoutOpen && (
        <CheckoutDialog
          product={product}
          customerProfile={customerProfile}
          selectedSku={selectedSku}
          quantity={quantity}
          totalPrice={totalPrice}
          onClose={() => setCheckoutOpen(false)}
          onSellerHomeOpen={() => {
            setCheckoutOpen(false);
            setSellerHomeOpen(true);
          }}
          onCreated={(created) => {
            setOrder(created);
            setCheckoutOpen(false);
            setToast(`订单已创建:${created.order_number}`);
            fetchDashboard().then(setDashboard).catch(() => undefined);
          }}
        />
      )}
      {cartOpen && (
        <CartDrawer
          items={cartItems}
          savedItems={savedItems}
          onClose={() => setCartOpen(false)}
          onQuantityChange={(id, nextQuantity) =>
            setCartItems((current) => current.map((item) => (item.id === id ? { ...item, quantity: nextQuantity } : item)))
          }
          onRemove={(id) => setCartItems((current) => current.filter((item) => item.id !== id))}
          onSaveForLater={(item) => saveCartItem(item)}
          onMoveToCart={(item) => addCartItem(item)}
          onRemoveSaved={(id) => setSavedItems((current) => current.filter((item) => item.id !== id))}
          onCheckout={checkoutCartItem}
          onProductSelect={(slug) => {
            handleProductSelect(slug);
            setCartOpen(false);
          }}
        />
      )}
      {sellerHomeOpen && (
        <SellerStorefront
          merchant={product.merchant}
          products={productList}
          onClose={() => setSellerHomeOpen(false)}
          onProductAction={handleSummaryAction}
        />
      )}
      {mobileMenuOpen && (
        <MobileMenu
          onClose={() => setMobileMenuOpen(false)}
          onProductSelect={handleProductSelect}
          onSearchResults={handleListingOpen}
          onAccountOpen={handleAccountOpen}
          onSupportOpen={openSupportChannel}
        />
      )}
      {order && <OrderResult order={order} onClose={() => setOrder(null)} />}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

function PortalTopbar({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <header className="portal-topbar">
      <span className="brand brand-static">
        <Apple size={22} />
        LaoShopLocal
      </span>
      <div>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
    </header>
  );
}

function PortalMetric({ label, value, note, icon: Icon }: { label: string; value: string; note: string; icon: LucideIcon }) {
  return (
    <article className="portal-metric">
      <Icon size={22} />
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function SellerPortalApp({
  product,
  selectedSku,
  totalPrice,
  dashboard,
  onProductPublished,
  onMerchantUpdated
}: {
  product: Product;
  selectedSku: ProductSku;
  totalPrice: number;
  dashboard: Dashboard | null;
  onProductPublished: (product: Product) => void;
  onMerchantUpdated: (merchant: Merchant) => void;
}) {
  const [products, setProducts] = useState<Product[]>([product]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    fetchAdminProducts()
      .then((items) => setProducts(items.length ? items : [product]))
      .catch((error) => setStatus(error instanceof Error ? error.message : "无法加载店家商品"));
  }, [product]);

  const orders = dashboard?.orders ?? [];
  const paymentReviewOrders = orders.filter((orderItem) => orderItem.payment_ai_status === "passed" && orderItem.seller_payment_status !== "confirmed");
  const shipmentOrders = orders.filter((orderItem) => orderItem.payment_status === "confirmed" && orderItem.logistics_status !== "completed");
  const lowStock = products.filter((item) => item.skus.reduce((sum, sku) => sum + sku.stock_quantity, 0) <= 10).length;

  return (
    <div className="portal-root seller-portal">
      <PortalTopbar title="店家端工作台" subtitle="seller.laoshoplocal.com · 商品、订单、物流、店铺资料" />
      <main className="portal-main">
        <section className="portal-hero">
          <div>
            <span className="portal-kicker">Seller Center</span>
            <h1>店家电脑网页版运营中心</h1>
            <p>用于店家申请开店、商品上新、CMS 编辑、收款二维码、付款核查、发货和物流二维码上传。</p>
          </div>
          <div className="portal-actions">
            <button className="primary-button" onClick={() => setAdminOpen(true)}>打开商品 CMS</button>
            <button className="secondary-button" onClick={() => setRegistrationOpen(true)}>新店家申请</button>
          </div>
        </section>

        <section className="portal-grid metrics-grid">
          <PortalMetric label="访问量" value={dashboard?.metrics.visitors.toLocaleString() ?? "0"} note="+12.5% 最近 24 小时" icon={BarChart3} />
          <PortalMetric label="订单数" value={String(dashboard?.metrics.orders ?? 0)} note={`${paymentReviewOrders.length} 单待确认收款`} icon={ClipboardCheck} />
          <PortalMetric label="销售额" value={formatMoney(dashboard?.metrics.revenue ?? 0)} note="收款、发货闭环追踪" icon={Wallet} />
          <PortalMetric label="低库存" value={String(lowStock)} note="库存低于 10 件需补货" icon={Boxes} />
        </section>

        <section className="portal-panel portal-agent-panel">
          <div className="portal-section-head">
            <div>
              <h2>店家端智能体工作流</h2>
              <p>按拼多多式店家运营节奏拆分任务,从开店、选品、上新到履约售后一条线完成。</p>
            </div>
            <span>Seller agents</span>
          </div>
          <div className="portal-agent-grid">
            {[
              [Store, "开店资质智能体", "整理店家资料、收款二维码、经营分类和审核状态。"],
              [Sparkles, "AI 上新智能体", "按品类模板生成可编辑商品草稿、卖点、参数和素材清单。"],
              [MessageCircle, "顾客咨询智能体", "跟进 WhatsApp、Messenger 和电话咨询,沉淀常见问题。"],
              [PackageCheck, "订单履约智能体", "收款确认、拣货发货、物流二维码上传和签收闭环。"]
            ].map(([Icon, title, body]) => {
              const AgentIcon = Icon as LucideIcon;
              return (
                <article key={String(title)} className="portal-agent-card">
                  <AgentIcon size={22} />
                  <strong>{String(title)}</strong>
                  <span>{String(body)}</span>
                </article>
              );
            })}
          </div>
        </section>

        {status && <div className="portal-alert">{status}</div>}

        <section className="portal-panel">
          <div className="portal-section-head">
            <div>
              <h2>商品目录与上新</h2>
              <p>电脑端维护商品,顾客端手机浏览和下单。点击编辑会进入完整 CMS,包括模板上新、批量上新、图片、视频和 SKU。</p>
            </div>
            <button className="secondary-button" onClick={() => fetchAdminProducts().then(setProducts).catch(() => undefined)}>刷新</button>
          </div>
          <div className="portal-table">
            {products.slice(0, 8).map((item) => (
              <article className="portal-row" key={item.id}>
                <ImageCrop src={item.media[0]?.url} label={item.title} variant={0} />
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.subtitle}</span>
                  <small>{formatMoney(item.price, item.currency)} · {item.status || "published"} · 库存 {item.skus.reduce((sum, sku) => sum + sku.stock_quantity, 0)}</small>
                </div>
                <button onClick={() => setAdminOpen(true)}>编辑</button>
              </article>
            ))}
          </div>
        </section>

        <section className="portal-grid two-column">
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>订单与收款</h2>
              <span>{orders.length} 单</span>
            </div>
            <div className="portal-list">
              {orders.slice(0, 6).map((orderItem) => (
                <div className="portal-list-item" key={orderItem.id}>
                  <Truck size={18} />
                  <div>
                    <strong>{orderItem.order_number}</strong>
                    <span>{formatMoney(orderItem.total_amount)} · {orderItem.product_title || "商品订单"}</span>
                    <small>{orderItem.payment_ai_status} · {orderItem.seller_payment_status} · {orderItem.logistics_status}</small>
                  </div>
                </div>
              ))}
              {!orders.length && <div className="empty-account-state">暂无订单,顾客下单后会显示付款、收款确认和物流流程。</div>}
            </div>
          </div>
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>履约提醒</h2>
              <span>{shipmentOrders.length} 单待处理</span>
            </div>
            <div className="portal-checklist">
              <p><CheckCircle2 size={18} /> AI 核查付款截图后,卖家确认收款。</p>
              <p><CheckCircle2 size={18} /> 卖家核对商品 SKU、数量和顾客地址。</p>
              <p><CheckCircle2 size={18} /> 发货后上传物流二维码,顾客端可追踪签收。</p>
              <p><CheckCircle2 size={18} /> 完成物流后开放顾客评价。</p>
            </div>
          </div>
        </section>

      </main>
      {adminOpen && (
        <AdminDrawer
          scope="seller"
          dashboard={dashboard}
          product={product}
          selectedSku={selectedSku}
          totalPrice={totalPrice}
          onClose={() => setAdminOpen(false)}
          onProductPublished={(published) => {
            onProductPublished(published);
            fetchAdminProducts().then(setProducts).catch(() => undefined);
          }}
          onMerchantUpdated={onMerchantUpdated}
        />
      )}
      {registrationOpen && <SellerRegistrationModal onClose={() => setRegistrationOpen(false)} />}
    </div>
  );
}

function PlatformPortalApp({
  product,
  selectedSku,
  totalPrice,
  dashboard,
  onProductPublished,
  onMerchantUpdated
}: {
  product: Product;
  selectedSku: ProductSku;
  totalPrice: number;
  dashboard: Dashboard | null;
  onProductPublished: (product: Product) => void;
  onMerchantUpdated: (merchant: Merchant) => void;
}) {
  const [platform, setPlatform] = useState<PlatformOverview | null>(null);
  const [securityCenter, setSecurityCenter] = useState<PlatformSecurityCenter | null>(null);
  const [platformToken, setPlatformToken] = useState(() => window.localStorage.getItem("lao_platform_admin_token") || "");
  const [adminOpen, setAdminOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loginForm, setLoginForm] = useState({
    email: "platform@laoshoplocal.com",
    phone: "+8562055551200",
    code: ""
  });
  const [loginStep, setLoginStep] = useState<"contact" | "code">("contact");
  const [demoCode, setDemoCode] = useState("");

  const refreshPlatform = () => {
    if (!platformToken) return;
    fetchPlatformOverview()
      .then(setPlatform)
      .catch((error) => setMessage(error instanceof Error ? error.message : "无法加载平台数据"));
    fetchPlatformSecurity()
      .then(setSecurityCenter)
      .catch(() => undefined);
  };

  useEffect(refreshPlatform, [platformToken]);

  const sendLoginCode = async () => {
    setMessage("");
    try {
      const result = await startVerification({
        purpose: "platform_login",
        email: loginForm.email,
        phone: loginForm.phone
      });
      setDemoCode(result.demoCode || "");
      setLoginStep("code");
      setMessage("验证码已生成。测试环境会直接显示验证码,接入短信/邮件服务后会隐藏。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法发送验证码");
    }
  };

  const confirmLogin = async () => {
    setMessage("");
    try {
      const result = await confirmVerification({
        purpose: "platform_login",
        email: loginForm.email,
        phone: loginForm.phone,
        code: loginForm.code
      });
      if (!result.token) throw new Error("平台登录会话创建失败");
      storeAdminSession("platform", result.token);
      setPlatformToken(result.token);
      setMessage("平台端登录成功,已开启 12 小时安全会话。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "验证码校验失败");
    }
  };

  const logoutPlatform = () => {
    clearAdminSession("platform");
    setPlatformToken("");
    setPlatform(null);
    setSecurityCenter(null);
    setLoginStep("contact");
    setMessage("已退出平台端安全会话。");
  };

  const reviewApplication = async (id: string, decision: "approved" | "rejected") => {
    setMessage("");
    try {
      const overview = await reviewSellerApplication(id, decision, decision === "approved" ? "平台审核通过,开通店家端。" : "资料不足,退回补充。");
      setPlatform(overview);
      setMessage(decision === "approved" ? "店家申请已通过,已创建店家账号与店铺资料。" : "店家申请已驳回,等待补充资料。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "审核操作失败");
    }
  };

  if (!platformToken) {
    return (
      <div className="portal-root admin-portal">
        <PortalTopbar title="平台端安全登录" subtitle="admin.laoshoplocal.com · 审核、风控、数据和权限管理" />
        <main className="portal-main platform-login-main">
          <section className="portal-hero">
            <div>
              <span className="portal-kicker">Security First</span>
              <h1>平台端需要邮箱或手机号验证后进入</h1>
              <p>顾客浏览平台不需要先注册;申请开店、顾客下单和平台管理等关键动作再触发注册或验证,降低购物阻力,同时保护后台权限。</p>
            </div>
            <ShieldCheck size={54} />
          </section>
          {message && <div className="portal-alert">{message}</div>}
          <section className="portal-panel platform-login-card">
            <div className="portal-section-head">
              <div>
                <h2>平台管理员登录</h2>
                <p>默认测试账号已绑定邮箱和手机号,生产环境可接入短信、邮件或 Authenticator。</p>
              </div>
            </div>
            <div className="admin-form-grid">
              <label>
                邮箱
                <input value={loginForm.email} onChange={(event) => setLoginForm((current) => ({ ...current, email: event.target.value }))} />
              </label>
              <label>
                手机号
                <input value={loginForm.phone} onChange={(event) => setLoginForm((current) => ({ ...current, phone: event.target.value }))} />
              </label>
              {loginStep === "code" && (
                <label>
                  验证码
                  <input value={loginForm.code} onChange={(event) => setLoginForm((current) => ({ ...current, code: event.target.value }))} />
                </label>
              )}
            </div>
            {demoCode && <div className="portal-alert">测试验证码:<strong>{demoCode}</strong></div>}
            <div className="portal-actions">
              <button className="primary-button" onClick={loginStep === "contact" ? sendLoginCode : confirmLogin}>
                {loginStep === "contact" ? "发送验证码" : "登录平台端"}
              </button>
              {loginStep === "code" && <button className="secondary-button" onClick={sendLoginCode}>重新发送</button>}
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="portal-root admin-portal">
      <PortalTopbar title="平台端管理中心" subtitle="admin.laoshoplocal.com · 店家、顾客、订单、审核、风控" />
      <main className="portal-main">
        <section className="portal-hero">
          <div>
            <span className="portal-kicker">Marketplace Ops</span>
            <h1>平台统一管理顾客端与店家端</h1>
            <p>平台端负责店家入驻审核、商品和订单监管、付款风险核查、物流履约、顾客服务与数据备份安全。</p>
          </div>
          <div className="portal-actions">
            <button className="primary-button" onClick={() => setAdminOpen(true)}>打开运营后台</button>
            <button className="secondary-button" onClick={refreshPlatform}>刷新平台数据</button>
          </div>
        </section>

        <section className="portal-grid metrics-grid">
          <PortalMetric label="待审店家" value={String(platform?.counts.pendingApplications ?? 0)} note="新店家申请注册" icon={Store} />
          <PortalMetric label="店家数量" value={String(platform?.counts.merchants ?? 0)} note="已入驻与认证店铺" icon={Boxes} />
          <PortalMetric label="顾客数量" value={String(platform?.counts.customers ?? 0)} note="注册、地址、钱包资料" icon={UserRound} />
          <PortalMetric label="付款复核" value={String(platform?.counts.paymentReviews ?? 0)} note="AI 初审后待卖家确认" icon={ShieldCheck} />
        </section>

        <section className="portal-panel portal-agent-panel">
          <div className="portal-section-head">
            <div>
              <h2>平台端智能体协作</h2>
              <p>平台端不直接承担店家日常上新,重点管理审核、权限、支付风控、物流协同、数据备份和安全审计。</p>
            </div>
            <span>Platform agents</span>
          </div>
          <div className="portal-agent-grid">
            {[
              [ShieldCheck, "入驻审核智能体", "核查身份、店铺分类、收款账户和履约能力。"],
              [CreditCard, "支付风控智能体", "比对付款截图中的店家名称、账号、订单号和金额。"],
              [Truck, "物流协同智能体", "追踪待发货、运输中、签收和异常售后状态。"],
              [Settings, "权限安全智能体", "管理平台登录验证、角色权限、审计日志和数据备份。"]
            ].map(([Icon, title, body]) => {
              const AgentIcon = Icon as LucideIcon;
              return (
                <article key={String(title)} className="portal-agent-card">
                  <AgentIcon size={22} />
                  <strong>{String(title)}</strong>
                  <span>{String(body)}</span>
                </article>
              );
            })}
          </div>
        </section>

        {message && <div className="portal-alert">{message}</div>}
        <div className="portal-actions portal-session-actions">
          <button className="secondary-button" onClick={logoutPlatform}>退出平台端登录</button>
        </div>

        <section className="portal-panel">
          <div className="portal-section-head">
            <div>
              <h2>店家入驻审核</h2>
              <p>参考大型电商平台生态,平台先审核身份、主营类目、收款账户、联系方式和履约能力,再开放店家端。</p>
            </div>
            <span>{platform?.applications.length ?? 0} 条申请</span>
          </div>
          <div className="portal-table">
            {(platform?.applications ?? []).slice(0, 8).map((application) => (
              <article className="portal-row" key={application.id}>
                <div className={`portal-status-dot ${application.status}`} />
                <div>
                  <strong>{application.store_name}</strong>
                  <span>{application.owner_name} · {application.phone} · {application.category}</span>
                  <small>{application.status} · 风险分 {application.risk_score} · {application.created_at}</small>
                </div>
                <div className="portal-inline-actions">
                  <button disabled={application.status !== "pending"} onClick={() => reviewApplication(application.id, "approved")}>通过</button>
                  <button disabled={application.status !== "pending"} onClick={() => reviewApplication(application.id, "rejected")}>驳回</button>
                </div>
              </article>
            ))}
            {!(platform?.applications.length) && <div className="empty-account-state">暂无待审入驻申请。</div>}
          </div>
        </section>

        <section className="portal-grid two-column">
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>生态数据</h2>
              <span>端口协作</span>
            </div>
            <div className="portal-list">
              <div className="portal-list-item"><Home size={18} /><div><strong>顾客端</strong><span>laoshoplocal.com · 手机浏览、购物车、下单、付款、评价</span></div></div>
              <div className="portal-list-item"><Store size={18} /><div><strong>店家端</strong><span>seller.laoshoplocal.com · 上新、收款、发货、物流</span></div></div>
              <div className="portal-list-item"><Settings size={18} /><div><strong>平台端</strong><span>admin.laoshoplocal.com · 审核、风控、数据和权限</span></div></div>
            </div>
          </div>
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>最近审计</h2>
              <span>{platform?.auditLogs.length ?? 0} 条</span>
            </div>
            <div className="portal-list">
              {(platform?.auditLogs ?? []).slice(0, 6).map((log) => (
                <div className="portal-list-item" key={log.id}>
                  <ClipboardCheck size={18} />
                  <div>
                    <strong>{log.action}</strong>
                    <span>{log.target_type} · {log.target_id}</span>
                    <small>{log.created_at}</small>
                  </div>
                </div>
              ))}
              {!(platform?.auditLogs.length) && <div className="empty-account-state">暂无审计记录。</div>}
            </div>
          </div>
        </section>

        <section className="portal-grid two-column">
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>安全设置</h2>
              <span>{securityCenter?.security.sessionHours ?? 12} 小时会话</span>
            </div>
            <div className="portal-list">
              <div className="portal-list-item">
                <ShieldCheck size={18} />
                <div>
                  <strong>邮箱/手机号验证</strong>
                  <span>{securityCenter?.security.mfaRequired ? "已启用" : "未启用"} · 平台端关键操作需要安全会话</span>
                </div>
              </div>
              {(securityCenter?.sessions ?? []).slice(0, 4).map((session) => (
                <div className="portal-list-item" key={session.id}>
                  <UserRound size={18} />
                  <div>
                    <strong>{session.name}</strong>
                    <span>{session.role} · {session.purpose}</span>
                    <small>到期:{session.expires_at}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="portal-panel">
            <div className="portal-section-head">
              <h2>安全事件</h2>
              <span>{securityCenter?.events.length ?? 0} 条</span>
            </div>
            <div className="portal-list">
              {(securityCenter?.events ?? []).slice(0, 6).map((event) => (
                <div className="portal-list-item" key={event.id}>
                  <AlertCircle size={18} />
                  <div>
                    <strong>{event.event_name}</strong>
                    <span>{event.severity} · {event.created_at}</span>
                  </div>
                </div>
              ))}
              {!(securityCenter?.events.length) && <div className="empty-account-state">暂无安全事件。</div>}
            </div>
          </div>
        </section>
      </main>
      {adminOpen && (
        <AdminDrawer
          scope="platform"
          dashboard={dashboard}
          product={product}
          selectedSku={selectedSku}
          totalPrice={totalPrice}
          onClose={() => setAdminOpen(false)}
          onProductPublished={onProductPublished}
          onMerchantUpdated={onMerchantUpdated}
        />
      )}
    </div>
  );
}

function Header({
  productList,
  onHome,
  onProductSelect,
  onSearchResults,
  onAccountOpen,
  onSupportOpen,
  onCartOpen,
  onMenuOpen,
  cartCount
}: {
  productList: ProductSummary[];
  onHome: () => void;
  onProductSelect: (slug: string) => void;
  onSearchResults: (query: string) => void;
  onAccountOpen: (entry?: AccountEntry) => void;
  onSupportOpen: (channel: "whatsapp" | "messenger" | "phone") => void;
  onCartOpen: () => void;
  onMenuOpen: () => void;
  cartCount: number;
}) {
  const [query, setQuery] = useState("");
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [activeRow, setActiveRow] = useState<string | null>(null);
  const searchResults = productList.filter((item) => `${item.title} ${item.subtitle}`.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  const closeMenus = () => {
    setActiveNav(null);
    setActiveRow(null);
  };
  const openMenuCategory = (label: string) => {
    closeMenus();
    onSearchResults(label);
  };
  const openMenuTarget = (target: MenuTarget | undefined, fallbackQuery: string) => {
    if (!target) {
      openMenuCategory(fallbackQuery);
      return;
    }
    closeMenus();
    if (target.type === "product") {
      onProductSelect(target.slug);
      return;
    }
    if (target.type === "account") {
      onAccountOpen(target.entry);
      return;
    }
    if (target.type === "support") {
      onSupportOpen(target.channel);
      return;
    }
    onSearchResults(target.query);
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    onSearchResults(query.trim() || "全部商品");
  };

  return (
    <header className="topbar" id="section-1">
      <button className="icon-button mobile-only" onClick={onMenuOpen} aria-label="打开菜单">
        <Menu size={20} />
      </button>
      <button className="brand brand-button" onClick={onHome}>
        <Apple size={20} fill="currentColor" />
        <span>YourBrand</span>
      </button>
      <nav className="desktop-nav" aria-label="主导航">
        {nav.map((item) => (
          <div
            className={`nav-item ${activeNav === item.label ? "open" : ""}`}
            key={item.label}
            onMouseEnter={() => setActiveNav(item.label)}
            onMouseLeave={closeMenus}
          >
            <button
              type="button"
              onClick={() => {
                setActiveNav((current) => (current === item.label ? null : item.label));
                setActiveRow(null);
              }}
              onFocus={() => setActiveNav(item.label)}
              aria-expanded={activeNav === item.label}
            >
              {item.label}
              <ChevronDown size={14} />
            </button>
            <div className="mega-menu">
              {item.columns.map((column) => (
                <div key={column.title}>
                  <button type="button" className="mega-column-title" onClick={() => openMenuCategory(column.title)}>
                    {column.title}
                  </button>
                  {column.children.map((child) => (
                    <div
                      className={`mega-row ${activeRow === `${item.label}:${child.label}` ? "open" : ""}`}
                      key={child.label}
                      onMouseEnter={() => setActiveRow(`${item.label}:${child.label}`)}
                      onFocusCapture={() => setActiveRow(`${item.label}:${child.label}`)}
                    >
                      <button className="mega-row-main" onClick={() => openMenuTarget(child.target, child.label)}>
                        <strong>{child.label}</strong>
                        <small>{child.detail}</small>
                      </button>
                      <ChevronRight size={14} />
                      <div className="third-menu">
                        {child.children.map((third) => (
                          <button
                            type="button"
                            onClick={() => openMenuTarget(third.target ?? { type: "product", slug: third.slug }, third.label)}
                            key={third.label}
                            aria-label={`进入 ${third.label} 对应商品`}
                          >
                            {third.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <form className="search-box" onSubmit={submitSearch}>
        <Search size={16} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索产品..." />
        {query && (
          <div className="search-results">
            {searchResults.length ? (
              searchResults.map((item) => (
                <button
                  type="button"
                  key={item.slug}
                  onClick={() => {
                    onSearchResults(item.title);
                    setQuery("");
                  }}
                >
                  <strong>{item.title}</strong>
                  <span>{formatMoney(item.price, item.currency)}</span>
                </button>
              ))
            ) : (
              <span>未找到产品</span>
            )}
          </div>
        )}
      </form>
      <div className="top-actions">
        <div className="account-menu">
          <button className="icon-button" aria-label="账号" onClick={() => onAccountOpen("profile")}>
            <UserRound size={18} />
          </button>
          <div className="account-panel">
            <strong>账户中心</strong>
            <button type="button" onClick={() => onAccountOpen("profile")}>
              <UserRound size={15} />
              个人资料
            </button>
            <button type="button" onClick={() => onAccountOpen("orders")}>
              <ClipboardCheck size={15} />
              我的订单
            </button>
            <button type="button" onClick={() => onAccountOpen("logistics")}>
              <Truck size={15} />
              物流追踪
            </button>
            <button type="button" onClick={() => onAccountOpen("service")}>
              <LifeBuoy size={15} />
              售后服务
            </button>
          </div>
        </div>
        <button className="cart-button" aria-label="购物车" onClick={onCartOpen}>
          <ShoppingCart size={19} />
          <span>{cartCount}</span>
        </button>
      </div>
    </header>
  );
}

function SellerRegistrationModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    storeName: "",
    ownerName: "",
    phone: "",
    email: "",
    address: "",
    businessType: "公司",
    category: "数码店",
    level: "认证卖家",
    facebook: "",
    messenger: "",
    whatsapp: "",
    bankAccount: "",
    intro: ""
  });
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [qrPreview, setQrPreview] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    return () => {
      if (qrPreview) URL.revokeObjectURL(qrPreview);
    };
  }, [qrPreview]);

  const chooseQr = (file: File | null) => {
    if (qrPreview) URL.revokeObjectURL(qrPreview);
    if (!file || !file.type.startsWith("image/")) {
      setQrPreview("");
      setQrFile(null);
      return;
    }
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  };

  const update = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    try {
      const application = await submitSellerApplication({ ...form, qrFile });
      setSubmitted(true);
      setMessage(`入驻申请已进入平台审核队列:${application.id}。平台管理员审核通过后会自动开通店家后台。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "入驻申请提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="店家注册入驻">
      <section className="seller-registration">
        <button className="modal-close" type="button" onClick={onClose} aria-label="关闭店家注册">
          <X size={18} />
        </button>
        <header className="seller-registration-hero">
          <div>
            <span>Seller Central</span>
            <h2>店家注册 / 免费入驻</h2>
            <p>按照主流 marketplace 卖家标准提交身份、联系方式、经营分类、收款资料和店铺主页信息。</p>
          </div>
          <div className="seller-registration-steps">
            <span>1 资料提交</span>
            <span>2 平台审核</span>
            <span>3 开通店铺</span>
          </div>
        </header>
        <form className="seller-registration-grid" onSubmit={submit}>
          <label>
            店铺名称
            <input required value={form.storeName} onChange={(event) => update("storeName", event.target.value)} placeholder="例如:TEP国际电工" />
          </label>
          <label>
            联系人 / 法人
            <input required value={form.ownerName} onChange={(event) => update("ownerName", event.target.value)} />
          </label>
          <label>
            电话
            <input required value={form.phone} onChange={(event) => update("phone", event.target.value)} />
          </label>
          <label>
            邮箱
            <input required type="email" value={form.email} onChange={(event) => update("email", event.target.value)} />
          </label>
          <label className="admin-wide">
            地址
            <input required value={form.address} onChange={(event) => update("address", event.target.value)} />
          </label>
          <label>
            经营类型
            <select value={form.businessType} onChange={(event) => update("businessType", event.target.value)}>
              <option>公司</option>
              <option>个体商户</option>
              <option>品牌代理</option>
              <option>工厂直营</option>
            </select>
          </label>
          <label>
            主营分类
            <select value={form.category} onChange={(event) => update("category", event.target.value)}>
              <option>数码店</option>
              <option>家居建材</option>
              <option>电工照明</option>
              <option>服饰百货</option>
              <option>食品健康</option>
            </select>
          </label>
          <label>
            店家等级申请
            <select value={form.level} onChange={(event) => update("level", event.target.value)}>
              <option>认证卖家</option>
              <option>旗舰店</option>
              <option>品牌认证</option>
              <option>本地优选</option>
            </select>
          </label>
          <label>
            Facebook
            <input value={form.facebook} onChange={(event) => update("facebook", event.target.value)} placeholder="https://facebook.com/..." />
          </label>
          <label>
            Messenger
            <input value={form.messenger} onChange={(event) => update("messenger", event.target.value)} placeholder="https://m.me/..." />
          </label>
          <label>
            WhatsApp
            <input value={form.whatsapp} onChange={(event) => update("whatsapp", event.target.value)} placeholder="+856..." />
          </label>
          <label>
            店家银行账户
            <input required value={form.bankAccount} onChange={(event) => update("bankAccount", event.target.value)} placeholder="BCEL / JDB / LDB 账户" />
          </label>
          <label className="admin-wide">
            店家介绍
            <textarea required value={form.intro} onChange={(event) => update("intro", event.target.value)} placeholder="说明主营商品、发货能力、售后承诺和认证资质。" />
          </label>
          <label className="admin-wide seller-registration-upload">
            店家收款二维码
            <input type="file" accept="image/*" onChange={(event) => chooseQr(event.target.files?.[0] ?? null)} />
            {qrPreview ? <img src={qrPreview} alt="收款二维码预览" /> : <span>上传后用于顾客扫码付款,系统会在订单二维码中带入订单总金额。</span>}
          </label>
          <label className="seller-registration-consent admin-wide">
            <input type="checkbox" required />
            我确认店铺身份、收款账户和联系方式真实有效,同意平台进行入驻审核。
          </label>
          <div className="seller-registration-actions admin-wide">
            {message && <p className={submitted ? "form-success" : "form-error"}>{message}</p>}
            <button className="primary-button" type="submit" disabled={submitting}>{submitting ? "提交中..." : "提交入驻申请"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function CustomerAccountPage({
  profile,
  products,
  savedItems,
  cartItems,
  initialTab,
  initialTool,
  initialProfileOpen,
  onRegister,
  onProductSelect,
  onOpenCart
}: {
  profile: CustomerProfile | null;
  products: ProductSummary[];
  savedItems: CartItem[];
  cartItems: CartItem[];
  initialTab: AccountOrderTab;
  initialTool: AccountToolKey | null;
  initialProfileOpen: boolean;
  onRegister: (payload: { name: string; phone: string; email: string; address: string; bankAccount: string }) => Promise<void>;
  onProductSelect: (slug: string) => void;
  onOpenCart: () => void;
}) {
  const customer = profile?.customer;
  const [form, setForm] = useState({
    name: customer?.name || "",
    phone: customer?.phone || "",
    email: customer?.email || "",
    address: customer?.address || "",
    bankAccount: customer?.bank_account || ""
  });
  const [tab, setTab] = useState<AccountOrderTab>(initialTab);
  const [activeTool, setActiveTool] = useState<AccountToolKey | null>(initialTool);
  const [profileModalOpen, setProfileModalOpen] = useState(initialProfileOpen);
  const [orderListExpanded, setOrderListExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!customer) return;
    setForm({
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      bankAccount: customer.bank_account
    });
  }, [customer?.id]);

  useEffect(() => {
    setTab(initialTab);
    setActiveTool(initialTool);
    setProfileModalOpen(initialProfileOpen);
  }, [initialTab, initialTool, initialProfileOpen]);

  useEffect(() => {
    setOrderListExpanded(false);
  }, [tab]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await onRegister(form);
      setMessage("资料已保存,手机号、邮箱、地址和银行账号已绑定。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "资料保存失败");
    } finally {
      setSaving(false);
    }
  };

  const buckets = profile?.orders.buckets;
  const orders =
    tab === "pay" ? buckets?.pendingPayment ?? [] :
    tab === "share" ? buckets?.pendingShare ?? [] :
    tab === "ship" ? buckets?.pendingShipment ?? [] :
    tab === "receive" ? buckets?.pendingReceipt ?? [] :
    tab === "review" ? buckets?.pendingReview ?? [] :
    profile?.orders.all ?? [];
  const latestOrder = orders[0];
  const savedProducts = [
    ...(profile?.savedProducts ?? []),
    ...savedItems.map((item) => ({
      id: item.productId,
      slug: item.slug,
      title: item.title,
      subtitle: item.subtitle,
      price: item.unitPrice,
      compare_at_price: item.unitPrice,
      currency: item.currency,
      rating: 4.8,
      review_count: 0,
      category_id: "",
      merchant_name: item.merchantName
    }))
  ];
  const recommendationProducts = products.slice(0, 7);
  const primaryAddress = profile?.addresses[0];
  const latestSavedProduct = savedProducts[0];
  const latestMerchant = profile?.savedMerchants[0];
  const latestHistory = profile?.history[0];
  const latestRefund = profile?.refunds[0];

  return (
    <section className="account-page">
      <div className="account-hero">
        <div className="account-avatar">{customer?.name?.slice(0, 1) || "新"}</div>
        <div>
          <h1>{customer?.name || "注册平台个人账号"}</h1>
          <p>{customer?.level || "绑定手机号、邮箱、地址和个人银行账号后,可管理订单、收藏、历史浏览和退款售后。"}</p>
        </div>
        <button className="primary-button" onClick={onOpenCart}>
          <ShoppingCart size={18} />
          购物车 {cartItems.length}
        </button>
      </div>

      <div className="account-layout">
        <button
          className="account-card account-profile-entry account-profile-toggle"
          type="button"
          onClick={() => setProfileModalOpen(true)}
          aria-haspopup="dialog"
        >
          <span>
            <span className="account-profile-entry-title">
              <UserRound size={18} />
              <strong>个人资料</strong>
            </span>
            <small>{customer ? "查看和更新账号资料" : "注册平台个人账号"}</small>
          </span>
          <ChevronDown size={18} />
        </button>

        {profileModalOpen && (
          <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="个人资料">
            <form className="profile-modal-card account-profile-form" onSubmit={submit}>
              <button className="modal-close" type="button" onClick={() => setProfileModalOpen(false)} aria-label="关闭个人资料窗口">
                <X size={18} />
              </button>
              <header className="profile-modal-head">
                <div className="account-avatar small">{customer?.name?.slice(0, 1) || "新"}</div>
                <div>
                  <h2>个人资料</h2>
                  <p>{customer ? "查看和更新账号资料" : "注册平台个人账号"}</p>
                </div>
              </header>
              <label><UserRound size={16} /> 名字<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
              <label><Phone size={16} /> 电话<input required value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
              <label><Mail size={16} /> 邮箱<input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
              <label><Home size={16} /> 地址<textarea required value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} /></label>
              <label><CreditCard size={16} /> 个人银行账号<input required value={form.bankAccount} onChange={(event) => setForm({ ...form, bankAccount: event.target.value })} /></label>
              <button className="primary-button" disabled={saving}>{saving ? "保存中..." : "注册/更新资料"}</button>
              {message && <p className="form-success">{message}</p>}
          </form>
          </div>
        )}

        <div className="account-card order-module">
          <div className="account-card-head">
            <h2>我的订单</h2>
            <button className="text-link-button" type="button" onClick={() => setActiveTool("orders")}>查看全部</button>
          </div>
          <div className="order-tabs">
            {[
              ["all", "全部", profile?.orders.all.length ?? 0],
              ["pay", "待付款", buckets?.pendingPayment.length ?? 0],
              ["share", "待分享", buckets?.pendingShare.length ?? 0],
              ["ship", "待发货", buckets?.pendingShipment.length ?? 0],
              ["receive", "待收货", buckets?.pendingReceipt.length ?? 0],
              ["review", "评价", buckets?.pendingReview.length ?? 0]
            ].map(([key, label, count]) => (
              <button className={tab === key ? "active" : ""} type="button" key={String(key)} onClick={() => setTab(key as typeof tab)}>
                {label}<small>{count}</small>
              </button>
            ))}
          </div>
          {latestOrder ? (
            <article className="customer-order-summary">
              <div>
                <strong>{latestOrder.order_number}</strong>
                <span>{latestOrder.product_title || "最新订单"} · {latestOrder.status} · {latestOrder.payment_status} · {latestOrder.logistics_status}</span>
              </div>
              <b>{formatMoney(latestOrder.total_amount)}</b>
              <button type="button" onClick={() => setActiveTool("orders")}>查看订单</button>
              <button
                className="order-fold-button"
                type="button"
                onClick={() => setOrderListExpanded((expanded) => !expanded)}
                aria-expanded={orderListExpanded}
              >
                {orderListExpanded ? "收起" : "展开全部"}
                <ChevronDown size={16} />
              </button>
            </article>
          ) : (
            <div className="empty-account-state compact">暂无该状态订单</div>
          )}
          {orderListExpanded && (
            <div className="customer-order-list">
              {orders.map((item) => (
              <article className="customer-order" key={item.id}>
                <ImageCrop variant={1} label={item.order_number} />
                <div>
                  <strong>{item.order_number}</strong>
                  <span>{item.status} · {item.payment_status} · {item.logistics_status}</span>
                  <b>{formatMoney(item.total_amount)}</b>
                </div>
                <button type="button" onClick={() => setActiveTool("orders")}>查看</button>
              </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="account-shortcuts">
        {([
          [Wallet, "个人钱包", "银行卡与先用后付"],
          [Heart, "商品收藏", `${savedProducts.length} 个商品`],
          [Store, "店铺关注", `${profile?.savedMerchants.length ?? 0} 个店家`],
          [History, "历史浏览", `${profile?.history.length ?? 0} 条记录`],
          [LifeBuoy, "退款/售后", `${profile?.refunds.length ?? 0} 条记录`],
          [Settings, "设置与帮助", "安全、免密、客服"]
        ] as Array<[LucideIcon, string, string]>).map(([Icon, title, body], index) => (
          <button
            className="account-shortcut"
            key={String(title)}
            type="button"
            onClick={() => setActiveTool((["wallet", "saved", "stores", "history", "refunds", "settings"] as const)[index])}
          >
            <Icon size={22} />
            <strong>{title}</strong>
            <span>{body}</span>
          </button>
        ))}
      </div>

      <section className="account-card account-addresses">
        <div className="account-card-head">
          <button className="section-title-button" type="button" onClick={() => setActiveTool("addresses")}>
            收货地址
          </button>
          <button className="section-fold-button" type="button" onClick={() => setActiveTool("addresses")}>
            展开全部
            <ChevronDown size={16} />
          </button>
        </div>
        {primaryAddress ? (
          <button className="account-one-line-row" key={primaryAddress.id} type="button" onClick={() => setActiveTool("addresses")}>
            <Home size={20} />
            <div>
              <strong>{primaryAddress.receiver_name},{primaryAddress.phone}</strong>
              <p>{primaryAddress.address_line}</p>
            </div>
            <span>{primaryAddress.is_default ? "默认" : "备用"}</span>
          </button>
        ) : (
          <div className="empty-account-state compact">暂无收货地址</div>
        )}
      </section>

      <section className="account-card saved-products-module">
        <div className="account-card-head">
          <button className="section-title-button" type="button" onClick={() => setActiveTool("saved")}>
            商品收藏
          </button>
          <button className="section-fold-button" type="button" onClick={() => setActiveTool("saved")}>
            展开全部
            <ChevronDown size={16} />
          </button>
        </div>
        {latestSavedProduct ? (
          <button className="account-one-line-row media" key={latestSavedProduct.slug} type="button" onClick={() => setActiveTool("saved")}>
            <ImageCrop variant={0} label={latestSavedProduct.title} />
            <div>
              <span>{latestSavedProduct.merchant_name}</span>
              <strong>{latestSavedProduct.title}</strong>
              <strong>{formatMoney(latestSavedProduct.price, latestSavedProduct.currency)}</strong>
            </div>
          </button>
        ) : (
          <div className="empty-account-state compact">暂无收藏商品</div>
        )}
      </section>

      <section className="account-card merchant-follow-module">
        <div className="account-card-head">
          <button className="section-title-button" type="button" onClick={() => setActiveTool("stores")}>
            店铺关注
          </button>
          <button className="section-fold-button" type="button" onClick={() => setActiveTool("stores")}>
            展开全部
            <ChevronDown size={16} />
          </button>
        </div>
        {latestMerchant ? (
          <button className="account-one-line-row media" key={latestMerchant.id} type="button" onClick={() => setActiveTool("stores")}>
            <ImageCrop variant={0} src={latestMerchant.payment_qr_url || "/assets/lao-online-shopping.png"} label={latestMerchant.name} />
            <div>
              <span>{latestMerchant.level} · {latestMerchant.category}</span>
              <strong>{latestMerchant.name}</strong>
              <p>{latestMerchant.phone}</p>
            </div>
          </button>
        ) : (
          <div className="empty-account-state compact">暂无关注店铺</div>
        )}
      </section>

      <section className="account-card browsing-module">
        <div className="account-card-head">
          <button className="section-title-button" type="button" onClick={() => setActiveTool("history")}>
            历史浏览
          </button>
          <button className="section-fold-button" type="button" onClick={() => setActiveTool("history")}>
            展开全部
            <ChevronDown size={16} />
          </button>
        </div>
        {latestHistory ? (
          <button className="account-one-line-row media" key={latestHistory.id} type="button" onClick={() => setActiveTool("history")}>
            <ImageCrop variant={0} label={latestHistory.title} />
            <div>
              <small>{new Date(latestHistory.viewed_at).toLocaleDateString()}</small>
              <strong>{latestHistory.title}</strong>
              <span>{latestHistory.merchant_name} · {formatMoney(latestHistory.price, latestHistory.currency)}</span>
            </div>
          </button>
        ) : (
          <div className="empty-account-state compact">暂无浏览记录</div>
        )}
      </section>

      <section className="account-card refund-module">
        <div className="account-card-head">
          <button className="section-title-button" type="button" onClick={() => setActiveTool("refunds")}>
            退款/售后
          </button>
          <button className="section-fold-button" type="button" onClick={() => setActiveTool("refunds")}>
            展开全部
            <ChevronDown size={16} />
          </button>
        </div>
        {latestRefund ? (
          <button className="account-one-line-row" key={latestRefund.id} type="button" onClick={() => setActiveTool("refunds")}>
            <LifeBuoy size={20} />
            <div>
              <strong>{latestRefund.product_title}</strong>
              <p>{latestRefund.merchant_name} · {latestRefund.reason}</p>
            </div>
            <span>{latestRefund.status === "success" ? "退款成功" : "待处理"}</span>
          </button>
        ) : (
          <div className="empty-account-state compact">暂无待处理的退款/售后订单</div>
        )}
      </section>

      <section className="personalized-row">
        <div className="account-card-head">
          <h2>Customers who viewed items in your browsing history also viewed</h2>
          <span>Page 1 of 3</span>
        </div>
        <div className="recommendation-strip">
          {recommendationProducts.map((item, index) => (
            <article key={item.slug} onClick={() => onProductSelect(item.slug)}>
              <ImageCrop variant={index} label={item.title} />
              <button>{item.title}</button>
              <Stars value={item.rating} />
              <strong>{formatMoney(item.price, item.currency)}</strong>
            </article>
          ))}
        </div>
      </section>
      {activeTool && (
        <AccountToolModal
          tool={activeTool}
          profile={profile}
          savedProducts={savedProducts}
          cartItems={cartItems}
          onClose={() => setActiveTool(null)}
          onProductSelect={(slug) => {
            setActiveTool(null);
            onProductSelect(slug);
          }}
          onOpenCart={() => {
            setActiveTool(null);
            onOpenCart();
          }}
        />
      )}
    </section>
  );
}

function AccountToolModal({
  tool,
  profile,
  savedProducts,
  cartItems,
  onClose,
  onProductSelect,
  onOpenCart
}: {
  tool: AccountToolKey;
  profile: CustomerProfile | null;
  savedProducts: Array<ProductSummary & { merchant_name?: string }>;
  cartItems: CartItem[];
  onClose: () => void;
  onProductSelect: (slug: string) => void;
  onOpenCart: () => void;
}) {
  const [drill, setDrill] = useState<{ title: string; body: string; level: number; actions: string[] } | null>(null);
  const toolMeta = {
    orders: { title: "我的订单", size: "wide" },
    logistics: { title: "物流追踪", size: "wide" },
    addresses: { title: "收货地址", size: "medium" },
    wallet: { title: "个人钱包", size: "compact" },
    saved: { title: "商品收藏", size: "wide" },
    stores: { title: "店铺关注", size: "wide" },
    history: { title: "历史浏览", size: "wide" },
    refunds: { title: "退款/售后", size: "medium" },
    settings: { title: "设置与帮助", size: "medium" }
  }[tool];
  const customer = profile?.customer;
  const allOrders = profile?.orders.all ?? [];
  const logisticsOrders = [
    ...(profile?.orders.buckets.pendingReceipt ?? []),
    ...allOrders.filter((item) => item.logistics_status && item.logistics_status !== "not_started")
  ].filter((item, index, list) => list.findIndex((order) => order.id === item.id) === index);

  useEffect(() => {
    setDrill(null);
  }, [tool]);

  const openDrill = (title: string, body: string, level = 2, actions: string[] = []) => {
    setDrill({ title, body, level, actions });
  };

  const settingDetails: Record<string, { body: string; actions: string[] }> = {
    "绑定手机号": {
      body: `当前手机号:${customer?.phone || "未绑定"}。用于登录验证、订单通知、物流签收提醒和售后沟通。`,
      actions: ["发送短信验证码", "更换绑定手机号", "设置登录提醒", "同步订单收货电话"]
    },
    "绑定邮箱": {
      body: `当前邮箱:${customer?.email || "未绑定"}。用于接收订单凭证、退款结果、账号安全提醒和平台公告。`,
      actions: ["发送邮箱验证码", "更换绑定邮箱", "订阅订单通知", "下载账号资料"]
    },
    "账户与安全": {
      body: "统一管理登录密码、可信设备、异常登录提醒、支付保护和账号注销申请。",
      actions: ["修改登录密码", "查看登录设备", "开启支付保护", "导出安全日志"]
    },
    "免密支付设置": {
      body: "平台保留支付确认步骤。可配置小额订单快捷确认,但扫码付款前仍显示订单号、店家账号和最终金额。",
      actions: ["设置小额限额", "关闭快捷确认", "查看支付授权记录", "重置支付保护"]
    },
    "官方客服": {
      body: "为订单、物流、支付截图核查、退款售后和商家纠纷提供统一服务工单。",
      actions: ["创建客服工单", "上传沟通凭证", "查看处理进度", "联系平台客服"]
    },
    "商家免费入驻": {
      body: "打开商家入驻申请,提交店家身份、经营分类、联系方式、收款二维码和银行账户,等待平台审核。",
      actions: ["填写入驻申请", "上传收款二维码", "提交认证资料", "查看审核进度"]
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={toolMeta.title}>
      <section className={`account-tool-window ${toolMeta.size}`}>
        <button className="modal-close" type="button" onClick={onClose} aria-label="关闭窗口">
          <X size={18} />
        </button>
        <header className="account-tool-header">
          <h2>{toolMeta.title}</h2>
          <p>{customer?.name || "新顾客"} · {customer?.level || "平台注册用户"}</p>
        </header>

        <div className="account-tool-body">
        {drill && (
          <div className="account-drill-panel">
            <div>
              <span>{drill.level} 级功能窗口</span>
              <h3>{drill.title}</h3>
              <p>{drill.body}</p>
            </div>
            {drill.actions.length > 0 && (
              <div className="account-drill-feature-grid">
                {drill.actions.map((action) => (
                  <div className="account-drill-feature" key={action}>
                    <strong>{action}</strong>
                    <small>当前目录内容已合并到本窗口,滚动即可查看和处理。</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tool === "orders" && !drill && (
          <div className="account-tool-list order-workbench-list">
            {allOrders.map((item) => (
              <article key={item.id}>
                <PackageCheck size={24} />
                <div>
                  <span>{item.status} · {item.payment_status} · {item.logistics_status}</span>
                  <strong>{item.order_number}</strong>
                  <small>{formatMoney(item.total_amount)} · {item.product_title || "订单商品"} · {item.sku_color || ""} {item.sku_storage || ""}</small>
                </div>
                <button onClick={() => openDrill(`订单详情 ${item.order_number}`, `商品:${item.product_title || "订单商品"};规格:${item.sku_color || "-"} ${item.sku_storage || "-"};金额:${formatMoney(item.total_amount)};付款:${item.payment_status};物流:${item.logistics_status}。`)}>
                  查看订单
                </button>
              </article>
            ))}
            {!allOrders.length && <div className="empty-account-state">暂无订单记录</div>}
          </div>
        )}

        {tool === "logistics" && !drill && (
          <div className="account-tool-list order-workbench-list">
            {logisticsOrders.map((item) => (
              <article key={item.id}>
                <Truck size={24} />
                <div>
                  <span>{item.merchant_name || "认证店家"} · {item.logistics_status}</span>
                  <strong>{item.order_number}</strong>
                  <small>{item.logistics_qr_url ? "已上传物流二维码,可继续追踪签收" : "等待店家上传物流二维码"}</small>
                </div>
                <button onClick={() => openDrill(`物流节点 ${item.order_number}`, `当前状态:${item.logistics_status}。系统展示付款确认、拣货打包、物流二维码、运输节点、签收和评价入口。`)}>
                  追踪物流
                </button>
              </article>
            ))}
            {!logisticsOrders.length && <div className="empty-account-state">暂无可追踪物流订单</div>}
          </div>
        )}

        {tool === "addresses" && !drill && (
          <div className="account-tool-list address-tool-list">
            {(profile?.addresses ?? []).map((address) => (
              <article key={address.id}>
                <Home size={24} />
                <div>
                  <span>{address.is_default ? "默认收货地址" : "备用收货地址"}</span>
                  <strong>{address.receiver_name},{address.phone}</strong>
                  <small>{address.address_line}</small>
                </div>
                <button onClick={() => openDrill(`地址详情 ${address.receiver_name}`, `联系人:${address.receiver_name};电话:${address.phone};地址:${address.address_line};状态:${address.is_default ? "默认地址" : "备用地址"}。`)}>
                  查看地址
                </button>
              </article>
            ))}
            {!profile?.addresses.length && <div className="empty-account-state">暂无收货地址</div>}
          </div>
        )}

        {tool === "wallet" && !drill && (
          <div className="account-tool-grid wallet-panel">
            <article>
              <CreditCard size={22} />
              <strong>绑定银行账户</strong>
              <span>{customer?.bank_account || "未绑定"}</span>
            </article>
            <article>
              <Wallet size={22} />
              <strong>购物车待结算</strong>
              <span>{cartItems.length} 件商品</span>
              <button className="primary-button" onClick={onOpenCart}>打开购物车</button>
            </article>
            <article>
              <ShieldCheck size={22} />
              <strong>支付安全</strong>
              <span>付款二维码绑定订单金额,截图由系统保存并初审。</span>
            </article>
          </div>
        )}

        {tool === "saved" && !drill && (
          <div className="account-tool-list">
            {savedProducts.map((item, index) => (
              <article key={`${item.slug}-${index}`}>
                <ImageCrop variant={index} label={item.title} />
                <div>
                  <span>{item.merchant_name || "认证店家"}</span>
                  <strong>{item.title}</strong>
                  <small>{formatMoney(item.price, item.currency)}</small>
                </div>
                <button onClick={() => onProductSelect(item.slug)}>查看商品</button>
              </article>
            ))}
          </div>
        )}

        {tool === "stores" && !drill && (
          <div className="account-tool-store-grid">
            {(profile?.savedMerchants ?? []).map((merchant) => (
              <article key={merchant.id}>
                <ImageCrop src={merchant.payment_qr_url || "/assets/lao-online-shopping.png"} variant={0} label={merchant.name} />
                <strong>{merchant.name}</strong>
                <span>{merchant.level} · {merchant.category}</span>
                <small>{merchant.phone}</small>
              </article>
            ))}
          </div>
        )}

        {tool === "history" && !drill && (
          <div className="account-tool-list">
            {(profile?.history ?? []).map((item, index) => (
              <article key={item.id}>
                <ImageCrop variant={index} label={item.title} />
                <div>
                  <span>{new Date(item.viewed_at).toLocaleDateString()} · {item.merchant_name}</span>
                  <strong>{item.title}</strong>
                  <small>{formatMoney(item.price, item.currency)}</small>
                </div>
                <button onClick={() => onProductSelect(item.slug)}>再次查看</button>
              </article>
            ))}
          </div>
        )}

        {tool === "refunds" && !drill && (
          <div className="account-tool-list service-list">
            {(profile?.refunds ?? []).map((refund) => (
              <article key={refund.id}>
                <LifeBuoy size={24} />
                <div>
                  <span>{refund.merchant_name} · {refund.reason}</span>
                  <strong>{refund.product_title}</strong>
                  <small>{refund.status === "success" ? "退款成功" : "待处理"} · {formatMoney(refund.amount)}</small>
                </div>
                <button onClick={() => openDrill(`售后详情 ${refund.product_title}`, `售后原因:${refund.reason};处理状态:${refund.status === "success" ? "退款成功" : "待处理"};退款金额:${formatMoney(refund.amount)};店家:${refund.merchant_name}。`)}>
                  售后详情
                </button>
              </article>
            ))}
            {!profile?.refunds.length && <div className="empty-account-state">暂无退款/售后记录</div>}
          </div>
        )}

        {tool === "settings" && !drill && (
          <div className="settings-menu">
            {([
              [Phone, "绑定手机号", customer?.phone || "未绑定"],
              [Mail, "绑定邮箱", customer?.email || "未绑定"],
              [ShieldCheck, "账户与安全", "密码、登录设备、支付保护"],
              [CreditCard, "免密支付设置", "付款前仍需确认金额"],
              [MessageCircle, "官方客服", "订单、物流、售后咨询"],
              [Store, "商家免费入驻", "提交店家资料和收款二维码"]
            ] as Array<[LucideIcon, string, string]>).map(([Icon, title, body]) => (
              <button
                type="button"
                key={String(title)}
                onClick={() => openDrill(title, settingDetails[title]?.body || body, 2, settingDetails[title]?.actions || [])}
              >
                <Icon size={20} />
                <span>
                  <strong>{title}</strong>
                  <small>{body}</small>
                </span>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        )}
        </div>
      </section>
    </div>
  );
}

function MarketplaceHome({
  products,
  featuredProduct,
  cartCount,
  onProductSelect,
  onProductAction,
  onOpenCart,
  onAccountOpen
}: {
  products: ProductSummary[];
  featuredProduct: Product;
  cartCount: number;
  onProductSelect: (slug: string) => void;
  onProductAction: (slug: string, action: "view" | "cart" | "save" | "buy") => void;
  onOpenCart: () => void;
  onAccountOpen: () => void;
}) {
  const primaryProducts: ProductSummary[] = products.length
    ? products
    : [{
        id: featuredProduct.id,
        slug: featuredProduct.slug,
        title: featuredProduct.title,
        subtitle: featuredProduct.subtitle,
        price: featuredProduct.price,
        compare_at_price: featuredProduct.compare_at_price,
        currency: featuredProduct.currency,
        rating: featuredProduct.rating,
        review_count: featuredProduct.review_count,
        category_id: "cat_flagship"
      }];
  const [needFilters, setNeedFilters] = useState<string[]>([]);
  const [priceFilter, setPriceFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [serviceFilters, setServiceFilters] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState("featured");
  const dealCards = [
    { title: "手机数码旗舰", body: "iOS / Android 热门机型", cta: "查看旗舰手机", slug: "iphone-16-pro-max" },
    { title: "配件限时优惠", body: "充电、支架、车载套装", cta: "进入配件专区", slug: "magsafe-charger" },
    { title: "音频与穿戴", body: "耳机、手表、健康设备", cta: "发现智能设备", slug: "airpods-pro-2" },
    { title: "本地认证卖家", body: "正品、收款透明、售后可追踪", cta: "查看店铺商品", slug: primaryProducts[0]?.slug || featuredProduct.slug }
  ];
  const productAt = (index: number) => primaryProducts[index % primaryProducts.length];
  const categoryDecks = [
    { title: "家用电器热门分类", link: "探索家用和厨房好物", labels: ["厨房", "咖啡", "锅具", "热水壶"] },
    { title: "节日礼物与折扣", link: "查看更多礼物", labels: ["服饰", "个护", "数码", "鞋包"] },
    { title: "你可能喜欢的潮流", link: "查看个性化推荐", labels: ["连衣裙", "针织", "外套", "配饰"] },
    { title: "轻松升级你的空间", link: "选购家居商品", labels: ["收纳", "五金", "家具", "墙面"] }
  ];
  const homeShelves = [
    { title: "服饰、鞋包与配饰热卖", itemOffset: 0 },
    { title: "老挝人民民主共和国优选商品", itemOffset: 2 },
    { title: "运动与户外热门排行", itemOffset: 4 },
    { title: "家居国际热卖榜", itemOffset: 1 },
    { title: "电脑、手机与配件", itemOffset: 3 },
    { title: "厨房与日用必备", itemOffset: 5 }
  ];
  const needOptions = [
    { id: "phone", label: "手机数码", detail: "旗舰手机、系统生态", keywords: ["iphone", "galaxy", "xiaomi", "pro max", "手机"], categories: ["cat_flagship"] },
    { id: "charge", label: "充电配件", detail: "无线充、磁吸、车载", keywords: ["magsafe", "充电", "磁吸", "支架", "无线"], categories: ["cat_magsafe"] },
    { id: "audio", label: "音频穿戴", detail: "耳机、手表、健康", keywords: ["airpods", "watch", "耳机", "手表"], categories: ["cat_audio"] },
    { id: "home", label: "家居建材", detail: "照明、门窗、户外", keywords: ["门窗", "滑轮", "天幕", "照明", "户外"], categories: ["cat_home", "cat_lighting"] }
  ];
  const serviceOptions = [
    { id: "discount", label: "有优惠折扣" },
    { id: "popular", label: "热销/评价多" },
    { id: "trusted", label: "高评分优先" },
    { id: "ready", label: "适合快速下单" }
  ];
  const toggleFilter = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  };
  const clearFilters = () => {
    setNeedFilters([]);
    setPriceFilter("all");
    setRatingFilter("all");
    setServiceFilters([]);
    setSortMode("featured");
  };
  const filteredProducts = useMemo(() => {
    const matchesNeed = (item: ProductSummary) => {
      if (!needFilters.length) return true;
      const haystack = `${item.title} ${item.subtitle}`.toLowerCase();
      return needFilters.some((filter) => {
        const option = needOptions.find((candidate) => candidate.id === filter);
        if (!option) return true;
        return option.categories.includes(item.category_id) || option.keywords.some((keyword) => haystack.includes(keyword.toLowerCase()));
      });
    };
    const matchesPrice = (item: ProductSummary) => {
      const price = item.price / 100;
      if (priceFilter === "under500") return price < 500;
      if (priceFilter === "500to2000") return price >= 500 && price <= 2000;
      if (priceFilter === "over2000") return price > 2000;
      return true;
    };
    const matchesRating = (item: ProductSummary) => {
      if (ratingFilter === "4.5") return item.rating >= 4.5;
      if (ratingFilter === "4.0") return item.rating >= 4;
      return true;
    };
    const matchesService = (item: ProductSummary) => {
      if (!serviceFilters.length) return true;
      const discountRate = item.compare_at_price > item.price ? (item.compare_at_price - item.price) / item.compare_at_price : 0;
      return serviceFilters.every((filter) => {
        if (filter === "discount") return discountRate >= 0.05;
        if (filter === "popular") return item.review_count >= 100;
        if (filter === "trusted") return item.rating >= 4.5;
        if (filter === "ready") return item.price > 0;
        return true;
      });
    };
    const sorted = primaryProducts.filter((item) => matchesNeed(item) && matchesPrice(item) && matchesRating(item) && matchesService(item));
    return [...sorted].sort((a, b) => {
      if (sortMode === "priceAsc") return a.price - b.price;
      if (sortMode === "priceDesc") return b.price - a.price;
      if (sortMode === "rating") return b.rating - a.rating || b.review_count - a.review_count;
      if (sortMode === "discount") return (b.compare_at_price - b.price) - (a.compare_at_price - a.price);
      return b.review_count - a.review_count;
    });
  }, [needFilters, priceFilter, primaryProducts, ratingFilter, serviceFilters, sortMode]);
  const activeFilterCount = needFilters.length + serviceFilters.length + (priceFilter === "all" ? 0 : 1) + (ratingFilter === "all" ? 0 : 1);
  const resultTitle = needFilters.length
    ? needFilters.map((id) => needOptions.find((option) => option.id === id)?.label).filter(Boolean).join("、")
    : "全部商品";

  return (
    <div className="market-home">
      <section className="market-hero">
        <div className="market-hero-copy">
          <span>今天的本地数码优惠</span>
          <h1>为你的日常设备省更多</h1>
          <p>先浏览分类和活动,再进入商品详情页完成选择、保存、加购或立即购买。</p>
          <div>
            <button className="primary-button" onClick={() => onProductSelect(featuredProduct.slug)}>查看今日主推</button>
            <button className="secondary-button" onClick={onOpenCart}>
              <ShoppingCart size={17} />
              购物车 {cartCount}
            </button>
          </div>
        </div>
        <div className="market-hero-visual">
          <ProductVisual variant={0} />
        </div>
      </section>

      <section className="market-card-grid" aria-label="首页购物分类">
        {dealCards.map((card, index) => (
          <article className="market-card" key={card.title}>
            <h2>{card.title}</h2>
            <button className="market-card-image" onClick={() => onProductSelect(card.slug)} aria-label={card.cta}>
              <ImageCrop variant={index} label={card.title} />
            </button>
            <p>{card.body}</p>
            <button className="market-link-button" onClick={() => onProductSelect(card.slug)}>{card.cta}</button>
          </article>
        ))}
      </section>

      <section className="market-home-decks" aria-label="平台首页分类入口">
        {categoryDecks.map((deck, deckIndex) => (
          <article className="home-deck" key={deck.title}>
            <h2>{deck.title}</h2>
            <div className="home-deck-grid">
              {deck.labels.map((label, labelIndex) => {
                const productItem = productAt(deckIndex + labelIndex);
                return (
                  <button
                    type="button"
                    key={label}
                    onClick={() => onProductSelect(productItem.slug)}
                    aria-label={`查看${label}推荐`}
                  >
                    <ImageCrop variant={deckIndex + labelIndex} label={label} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
            <button className="market-link-button" type="button" onClick={() => onProductSelect(productAt(deckIndex).slug)}>
              {deck.link}
            </button>
          </article>
        ))}
      </section>

      <section className="market-wide-shelf" aria-label="热卖商品货架">
        <div className="section-title-row">
          <div>
            <h2>Best Sellers in Clothing, Shoes & Jewelry</h2>
            <p>横向浏览热卖商品,像 Amazon 首页一样快速进入商品主页。</p>
          </div>
          <button className="market-link-button" type="button" onClick={() => onProductSelect(productAt(0).slug)}>查看全部</button>
        </div>
        <div className="wide-product-strip">
          {Array.from({ length: 8 }, (_, index) => {
            const item = productAt(index);
            return (
              <button type="button" key={`${item.slug}-top-${index}`} onClick={() => onProductSelect(item.slug)} aria-label={`查看 ${item.title}`}>
                <ImageCrop variant={index} label={item.title} />
                <strong>{item.title}</strong>
                <span>{formatMoney(item.price, item.currency)}</span>
              </button>
            );
          })}
        </div>
      </section>

      {homeShelves.map((shelf) => (
        <section className="market-wide-shelf compact" key={shelf.title} aria-label={shelf.title}>
          <h2>{shelf.title}</h2>
          <div className="wide-product-strip">
            {Array.from({ length: 7 }, (_, index) => {
              const item = productAt(index + shelf.itemOffset);
              return (
                <button type="button" key={`${shelf.title}-${item.slug}-${index}`} onClick={() => onProductSelect(item.slug)} aria-label={`进入 ${item.title}`}>
                  <ImageCrop variant={index + shelf.itemOffset} label={item.title} />
                  <strong>{item.title}</strong>
                  <span>{formatMoney(item.price, item.currency)}</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      <section className="market-search-layout" aria-label="按需求筛选商品">
        <aside className="market-filter-sidebar">
          <div className="filter-sidebar-head">
            <h2>按需求筛选</h2>
            <button type="button" onClick={clearFilters}>清除</button>
          </div>
          <div className="filter-group">
            <h3>产品需求</h3>
            {needOptions.map((option) => (
              <label className="filter-check" key={option.id}>
                <input
                  type="checkbox"
                  checked={needFilters.includes(option.id)}
                  onChange={() => toggleFilter(option.id, setNeedFilters)}
                />
                <span>
                  <strong>{option.label}</strong>
                  <small>{option.detail}</small>
                </span>
              </label>
            ))}
          </div>
          <div className="filter-group">
            <h3>价格预算</h3>
            <div className="filter-chip-grid">
              {[
                ["all", "全部"],
                ["under500", "THB 500 以下"],
                ["500to2000", "THB 500-2,000"],
                ["over2000", "THB 2,000 以上"]
              ].map(([value, label]) => (
                <button className={priceFilter === value ? "active" : ""} type="button" key={value} onClick={() => setPriceFilter(value)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <h3>顾客评分</h3>
            <div className="filter-chip-grid">
              {[
                ["all", "全部评分"],
                ["4.5", "4.5 星以上"],
                ["4.0", "4 星以上"]
              ].map(([value, label]) => (
                <button className={ratingFilter === value ? "active" : ""} type="button" key={value} onClick={() => setRatingFilter(value)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <h3>服务承诺</h3>
            {serviceOptions.map((option) => (
              <label className="filter-check compact" key={option.id}>
                <input
                  type="checkbox"
                  checked={serviceFilters.includes(option.id)}
                  onChange={() => toggleFilter(option.id, setServiceFilters)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </aside>
        <section className="market-results-panel">
          <div className="market-results-toolbar">
            <div>
              <span>{filteredProducts.length ? `1-${filteredProducts.length}` : "0"} of {primaryProducts.length} results for</span>
              <h2>{resultTitle}</h2>
              <p>平台会根据顾客选择的需求、预算、评分和服务承诺实时筛选合适商品。</p>
            </div>
            <label>
              排序
              <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
                <option value="featured">综合推荐</option>
                <option value="rating">评分最高</option>
                <option value="priceAsc">价格从低到高</option>
                <option value="priceDesc">价格从高到低</option>
                <option value="discount">优惠力度</option>
              </select>
            </label>
          </div>
          <div className="filter-summary">
            <span>{activeFilterCount ? `已选择 ${activeFilterCount} 个筛选条件` : "未选择筛选条件,展示全部推荐商品"}</span>
            <button className="secondary-button" onClick={onOpenCart}>查看购物车</button>
          </div>
          {filteredProducts.length ? (
            <div className="market-product-row">
              {filteredProducts.map((item, index) => (
                <article className="market-product-card" key={item.slug}>
                  <button className="market-product-image" onClick={() => onProductAction(item.slug, "view")} aria-label={`进入 ${item.title} 商品主页`}>
                    <ImageCrop variant={index} label={item.title} />
                  </button>
                  <button className="market-product-title" onClick={() => onProductAction(item.slug, "view")}>
                    {item.title}
                  </button>
                  <span>{item.subtitle}</span>
                  <div className="market-stars">
                    <Stars value={item.rating} />
                    <small>{item.rating.toFixed(1)} · {item.review_count.toLocaleString()} 条评价</small>
                  </div>
                  <strong>{formatMoney(item.price, item.currency)}</strong>
                  <del>{formatMoney(item.compare_at_price, item.currency)}</del>
                  <div className="market-product-actions">
                    <button onClick={() => onProductAction(item.slug, "save")}>保存</button>
                    <button onClick={() => onProductAction(item.slug, "cart")}>加入购物车</button>
                    <button className="primary-button" onClick={() => onProductAction(item.slug, "buy")}>立即购买</button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="filter-empty-state">
              <Search size={32} />
              <strong>暂无完全匹配的商品</strong>
              <span>可以减少筛选条件,或先查看全部商品后再进入商品主页对比。</span>
              <button className="primary-button" onClick={clearFilters}>查看全部商品</button>
            </div>
          )}
        </section>
      </section>

      <section className="market-confidence">
        <div>
          <Truck size={22} />
          <strong>配送承诺</strong>
          <span>本地配送、物流可查询</span>
        </div>
        <div>
          <ShieldCheck size={22} />
          <strong>认证店家</strong>
          <span>收款信息与店家资料可查</span>
        </div>
        <div>
          <Heart size={22} />
          <strong>稍后购买</strong>
          <span>先保存,再比较</span>
        </div>
      </section>

      <section className="market-personalize" aria-label="个性化推荐登录入口">
        <h2>查看你的个性化推荐</h2>
        <button className="market-signin-button" type="button" onClick={onAccountOpen}>
          登录 / 注册个人账号
        </button>
        <p>新顾客可以先创建个人资料,购物时自动带入姓名、电话和收货地址。</p>
      </section>

      <section className="market-home-footer" aria-label="平台页脚导航">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</button>
        <div>
          {[
            ["了解我们", "平台招商", "职业与合作", "平台公告", "投资者关系"],
            ["卖家服务", "注册成为店家", "商品上新工具", "物流管理", "广告与推荐"],
            ["支付产品", "扫码支付", "银行账户", "余额与退款", "安全验证"],
            ["帮助中心", "我的账户", "我的订单", "退换货", "客服支持"]
          ].map(([title, ...links]) => (
            <article key={title}>
              <h3>{title}</h3>
              {links.map((link) => <a href="#" key={link}>{link}</a>)}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function MarketplaceListingPage({
  query,
  products,
  onProductSelect,
  onProductAction
}: {
  query: string;
  products: ProductSummary[];
  onProductSelect: (slug: string) => void;
  onProductAction: (slug: string, action: "view" | "cart" | "save" | "buy") => void;
}) {
  const fallbackProduct: ProductSummary = {
    id: "fallback-listing",
    slug: "iphone-16-pro-max",
    title: "iPhone 16 Pro Max",
    subtitle: "旗舰手机 · 本地认证卖家",
    price: 899900,
    compare_at_price: 999900,
    currency: "THB",
    rating: 4.7,
    review_count: 426,
    category_id: "cat_flagship"
  };
  const baseProducts = products.length ? products : [fallbackProduct];
  const productAt = (index: number) => baseProducts[index % baseProducts.length];
  const [ideaFilter, setIdeaFilter] = useState("全部");
  const [priceBand, setPriceBand] = useState("all");
  const [brandFilters, setBrandFilters] = useState<string[]>([]);
  const [conditionFilters, setConditionFilters] = useState<string[]>([]);
  const [sellerFilters, setSellerFilters] = useState<string[]>([]);
  const [ratingFloor, setRatingFloor] = useState("all");
  const [sortMode, setSortMode] = useState("featured");
  const ideas = ["Chair", "Headset", "Keyboard & Mouse", "Pc Speakers"];
  const brands = ["YourBrand", "ASUS", "Logitech", "msi", "SteelSeries", "Corsair", "Acer"];
  const conditions = ["New", "Renewed", "Used"];
  const sellers = ["YourBrand", "TEP国际电工", "本地认证卖家"];
  const listingItems = useMemo(() => {
    const allItems = Array.from({ length: 36 }, (_, index) => {
      const item = productAt(index);
      const priceShift = index % 5 === 0 ? 0 : (index % 5) * 12000;
      return {
        ...item,
        listKey: `${item.slug}-${index}`,
        displayTitle: index % 4 === 0 ? `${item.title} Gaming Bundle` : item.title,
        sponsored: index % 6 === 0,
        bought: index % 3 === 0 ? "500+" : index % 3 === 1 ? "50+" : "300+",
        brand: brands[index % brands.length],
        condition: conditions[index % conditions.length],
        seller: sellers[index % sellers.length],
        price: item.price + priceShift,
        compare_at_price: Math.max(item.compare_at_price + priceShift, item.price + priceShift + 8000)
      };
    });
    const filtered = allItems.filter((item) => {
      const price = item.price / 100;
      const matchesIdea = ideaFilter === "全部" || `${item.displayTitle} ${item.subtitle}`.toLowerCase().includes(ideaFilter.toLowerCase()) || item.category_id.includes("cat");
      const matchesPrice = priceBand === "all"
        || (priceBand === "under500" && price <= 500)
        || (priceBand === "500to2000" && price > 500 && price <= 2000)
        || (priceBand === "over2000" && price > 2000);
      const matchesBrand = !brandFilters.length || brandFilters.includes(item.brand);
      const matchesCondition = !conditionFilters.length || conditionFilters.includes(item.condition);
      const matchesSeller = !sellerFilters.length || sellerFilters.includes(item.seller);
      const matchesRating = ratingFloor === "all" || item.rating >= Number(ratingFloor);
      return matchesIdea && matchesPrice && matchesBrand && matchesCondition && matchesSeller && matchesRating;
    });
    return [...filtered].sort((a, b) => {
      if (sortMode === "priceAsc") return a.price - b.price;
      if (sortMode === "priceDesc") return b.price - a.price;
      if (sortMode === "rating") return b.rating - a.rating || b.review_count - a.review_count;
      return b.review_count - a.review_count;
    });
  }, [baseProducts, brandFilters, conditionFilters, ideaFilter, priceBand, ratingFloor, sellerFilters, sortMode]);
  const toggleListFilter = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  };
  const visibleItems = listingItems.slice(0, 30);

  return (
    <div className="listing-page">
      <section className="listing-summary-bar">
        <span>1-{Math.min(48, Math.max(visibleItems.length, 1))} of over 100,000 results for <strong>"{query}"</strong></span>
        <label>
          Sort by:
          <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
            <option value="featured">Featured</option>
            <option value="rating">Avg. Customer Review</option>
            <option value="priceAsc">Price: Low to High</option>
            <option value="priceDesc">Price: High to Low</option>
          </select>
        </label>
      </section>

      <section className="listing-layout">
        <aside className="listing-sidebar" aria-label="商品筛选">
          <div className="listing-filter-group">
            <h3>Popular Shopping Ideas</h3>
            {ideas.map((idea) => (
              <button className={ideaFilter === idea ? "active" : ""} key={idea} type="button" onClick={() => setIdeaFilter(idea)}>
                {idea}
              </button>
            ))}
          </div>
          <div className="listing-filter-group">
            <h3>Price</h3>
            <strong>$0 - $9,400+</strong>
            <div className="price-rail"><span /><span /></div>
            {[["all", "All prices"], ["under500", "Up to THB 500"], ["500to2000", "THB 500 to 2,000"], ["over2000", "THB 2,000 & above"]].map(([value, label]) => (
              <button className={priceBand === value ? "active" : ""} key={value} type="button" onClick={() => setPriceBand(value)}>
                {label}
              </button>
            ))}
          </div>
          <div className="listing-filter-group">
            <h3>Deals & Discounts</h3>
            {["All Discounts", "Buy More, Save More", "Coupons", "Today&apos;s Deals"].map((label) => <button type="button" key={label}>{label}</button>)}
          </div>
          <div className="listing-filter-group">
            <h3>Customer Reviews</h3>
            <button className={ratingFloor === "4" ? "active" : ""} type="button" onClick={() => setRatingFloor(ratingFloor === "4" ? "all" : "4")}>
              <Stars value={4} /> & Up
            </button>
          </div>
          <div className="listing-filter-group">
            <h3>Brands</h3>
            {brands.map((brand) => (
              <label className="listing-check" key={brand}>
                <input type="checkbox" checked={brandFilters.includes(brand)} onChange={() => toggleListFilter(brand, setBrandFilters)} />
                <span>{brand}</span>
              </label>
            ))}
          </div>
          <div className="listing-filter-group">
            <h3>Condition</h3>
            {conditions.map((condition) => (
              <label className="listing-check" key={condition}>
                <input type="checkbox" checked={conditionFilters.includes(condition)} onChange={() => toggleListFilter(condition, setConditionFilters)} />
                <span>{condition}</span>
              </label>
            ))}
          </div>
          <div className="listing-filter-group">
            <h3>Seller</h3>
            {sellers.map((seller) => (
              <label className="listing-check" key={seller}>
                <input type="checkbox" checked={sellerFilters.includes(seller)} onChange={() => toggleListFilter(seller, setSellerFilters)} />
                <span>{seller}</span>
              </label>
            ))}
          </div>
          <div className="listing-ad-tile">
            <ImageCrop variant={8} label="Sponsored monitor" />
            <a href="#">4K Monitor, Z-Edge 28 inch IPS Monitor, U...</a>
            <Stars value={4.5} />
            <strong>$229.99</strong>
            <button type="button">Shop now</button>
            <small>Sponsored</small>
          </div>
        </aside>

        <section className="listing-results" aria-label="搜索结果">
          <div className="listing-results-head">
            <h1>Results</h1>
            <p>Check each product page for other buying options. Price and other details may vary based on product size and color.</p>
          </div>
          <div className="listing-results-grid">
            {visibleItems.slice(0, 10).map((item, index) => (
              <ListingProductCard key={item.listKey} item={item} index={index} onProductSelect={onProductSelect} onProductAction={onProductAction} />
            ))}
          </div>
          <article className="listing-feature-ad">
            <div className="listing-video-ad">
              <ImageCrop variant={12} label="Gaming product video" />
              <span>Ⅱ</span>
              <button type="button" aria-label="静音广告">⌁</button>
            </div>
            <div>
              <span>Sponsored</span>
              <h2>HAYMAKER Noise Cancelling Bluetooth Gaming Headphones</h2>
              <Stars value={4.2} />
              <strong>$230.96</strong>
              <p>Only 18 left in stock - order soon.</p>
            </div>
          </article>
          <h2 className="more-results-title">More results</h2>
          <div className="listing-results-grid">
            {visibleItems.slice(10).map((item, index) => (
              <ListingProductCard key={item.listKey} item={item} index={index + 10} onProductSelect={onProductSelect} onProductAction={onProductAction} />
            ))}
          </div>
        </section>
      </section>
    </div>
  );
}

function ListingProductCard({
  item,
  index,
  onProductSelect,
  onProductAction
}: {
  item: ProductSummary & {
    listKey: string;
    displayTitle: string;
    sponsored: boolean;
    bought: string;
    brand: string;
    condition: string;
    seller: string;
  };
  index: number;
  onProductSelect: (slug: string) => void;
  onProductAction: (slug: string, action: "view" | "cart" | "save" | "buy") => void;
}) {
  return (
    <article className="listing-product-card">
      <button className="listing-product-image" type="button" onClick={() => onProductSelect(item.slug)} aria-label={`查看 ${item.displayTitle}`}>
        {index === 0 && <span className="overall-pick">Overall Pick</span>}
        <ImageCrop variant={index} label={item.displayTitle} />
      </button>
      {item.sponsored && <small>Sponsored</small>}
      <button className="listing-product-title" type="button" onClick={() => onProductSelect(item.slug)}>
        {item.displayTitle}, {item.subtitle}
      </button>
      <div className="listing-rating">
        <span>{item.rating.toFixed(1)}</span>
        <Stars value={item.rating} />
        <a href="#">({item.review_count.toLocaleString()})</a>
      </div>
      <p>{item.bought}+ bought in past month</p>
      <div className="listing-price">
        <span>{formatMoney(item.price, item.currency).replace("THB ", "฿")}</span>
        <del>{formatMoney(item.compare_at_price, item.currency)}</del>
      </div>
      <p className="listing-delivery">Ships to Lao People's Democratic Republic</p>
      {index % 4 === 2 ? (
        <button className="listing-option-button" type="button" onClick={() => onProductAction(item.slug, "view")}>See options</button>
      ) : (
        <button className="listing-cart-button" type="button" onClick={() => onProductAction(item.slug, "cart")}>Add to cart</button>
      )}
      <a className="buying-choices" href="#">More Buying Choices</a>
    </article>
  );
}

function AddedToCartPage({
  item,
  cartItems,
  subtotal,
  products,
  onCheckout,
  onOpenCart,
  onProductSelect,
  onProductAction,
  onQuantityChange,
  onRemove
}: {
  item: CartItem;
  cartItems: CartItem[];
  subtotal: number;
  products: ProductSummary[];
  onCheckout: () => void;
  onOpenCart: () => void;
  onProductSelect: (slug: string) => void;
  onProductAction: (slug: string, action: "view" | "cart" | "save" | "buy") => void;
  onQuantityChange: (id: string, nextQuantity: number) => void;
  onRemove: (id: string) => void;
}) {
  const visibleItems = cartItems.length > 0 ? cartItems : [item];
  const effectiveSubtotal = subtotal > 0 ? subtotal : item.unitPrice * item.quantity;
  const related = products.filter((product) => product.slug !== item.slug).slice(0, 6);
  const fallbackRelated = related.length ? related : products.slice(0, 6);

  return (
    <div className="cart-confirm-page">
      <div className="cart-confirm-main">
        <section className="cart-confirm-hero" aria-label="Added to cart confirmation">
          <div className="cart-confirm-product">
            <button className="cart-confirm-thumb" type="button" onClick={() => onProductSelect(item.slug)} aria-label={`View ${item.title}`}>
              <ImageCrop variant={1} label={item.title} />
            </button>
            <div className="cart-confirm-status">
              <CheckCircle2 size={34} />
              <div>
                <h1>Added to cart</h1>
                <p>
                  Style: {item.color} | {item.storage}
                </p>
              </div>
            </div>
          </div>
          <aside className="cart-confirm-subtotal" aria-label="Cart subtotal">
            <h2>
              Cart Subtotal: <strong>{formatMoney(effectiveSubtotal, item.currency)}</strong>
            </h2>
            <button className="cart-checkout-button" type="button" onClick={onCheckout}>
              Proceed to checkout ({visibleItems.length} item{visibleItems.length > 1 ? "s" : ""})
            </button>
            <button className="cart-go-button" type="button" onClick={onOpenCart}>
              Go to Cart
            </button>
            <p>
              For best experience <button type="button">sign in to your account</button>
            </p>
          </aside>
        </section>

        <section className="cart-accessories" aria-label="Accessories related to your item">
          <div className="cart-section-head">
            <h2>Accessories related to your item</h2>
            <span>Page 1 of 4</span>
          </div>
          <div className="cart-accessory-row">
            {fallbackRelated.map((product, index) => (
              <article className="cart-accessory-card" key={`${product.slug}-accessory`}>
                <button className="cart-accessory-image" type="button" onClick={() => onProductSelect(product.slug)} aria-label={`View ${product.title}`}>
                  <ImageCrop variant={index + 6} label={product.title} />
                </button>
                <button className="cart-accessory-title" type="button" onClick={() => onProductSelect(product.slug)}>
                  {product.title} | {product.subtitle}
                </button>
                <Stars value={product.rating} />
                <strong>{formatMoney(product.price, product.currency)}</strong>
                <button className="cart-accessory-button" type="button" onClick={() => onProductAction(product.slug, "cart")}>
                  Add to cart
                </button>
              </article>
            ))}
          </div>
        </section>
      </div>

      <aside className="cart-mini-rail" aria-label="Mini cart">
        <div className="cart-mini-total">
          <span>Subtotal</span>
          <strong>{formatMoney(effectiveSubtotal, item.currency)}</strong>
          <button className="cart-go-button" type="button" onClick={onOpenCart}>
            Go to Cart
          </button>
        </div>
        {visibleItems.map((cartItem, index) => (
          <article className="cart-mini-item" key={cartItem.id}>
            <button className="cart-mini-image" type="button" onClick={() => onProductSelect(cartItem.slug)} aria-label={`View ${cartItem.title}`}>
              <ImageCrop variant={index + 1} label={cartItem.title} />
            </button>
            <strong>{formatMoney(cartItem.unitPrice, cartItem.currency)}</strong>
            <div className="cart-mini-stepper" aria-label={`${cartItem.title} quantity`}>
              <button type="button" onClick={() => onRemove(cartItem.id)} aria-label={`Remove ${cartItem.title}`}>
                <X size={15} />
              </button>
              <button type="button" onClick={() => onQuantityChange(cartItem.id, cartItem.quantity - 1)} aria-label="Decrease quantity">
                <Minus size={15} />
              </button>
              <span>{cartItem.quantity}</span>
              <button type="button" onClick={() => onQuantityChange(cartItem.id, cartItem.quantity + 1)} aria-label="Increase quantity">
                <Plus size={15} />
              </button>
            </div>
          </article>
        ))}
      </aside>
    </div>
  );
}

function AmazonProductDetailPage({
  product,
  productList,
  selectedSku,
  selectedColor,
  selectedStorage,
  activeMedia,
  quantity,
  totalPrice,
  specs,
  faqs,
  onMediaChange,
  onColorChange,
  onStorageChange,
  onQuantityChange,
  onCheckout,
  onAddToCart,
  onSaveForLater,
  onSellerHomeOpen,
  onProductSelect,
  onProductUpdate,
  onOrder,
  onShare,
  onSocialOpen
}: {
  product: Product;
  productList: ProductSummary[];
  selectedSku: ProductSku;
  selectedColor: string;
  selectedStorage: string;
  activeMedia: number;
  quantity: number;
  totalPrice: number;
  specs?: ProductBlock;
  faqs: ProductBlock[];
  onMediaChange: (index: number) => void;
  onColorChange: (color: string) => void;
  onStorageChange: (storage: string) => void;
  onQuantityChange: (quantity: number) => void;
  onCheckout: () => void;
  onAddToCart: () => void;
  onSaveForLater: () => void;
  onSellerHomeOpen: () => void;
  onProductSelect: (slug: string) => void;
  onProductUpdate: (product: Product) => void;
  onOrder: (order: Order) => void;
  onShare: () => void;
  onSocialOpen: (channel: SocialChannel) => void;
}) {
  const mediaCount = product.media.length + 1;
  const colors = product.skus.filter((sku, index, list) => list.findIndex((item) => item.color === sku.color) === index);
  const storages = product.skus.filter((sku, index, list) => list.findIndex((item) => item.storage === sku.storage) === index);
  const related = productList.filter((item) => item.slug !== product.slug);
  const productAt = (index: number) => related[index % Math.max(related.length, 1)] ?? {
    id: product.id,
    slug: product.slug,
    title: product.title,
    subtitle: product.subtitle,
    price: product.price,
    compare_at_price: product.compare_at_price,
    currency: product.currency,
    rating: product.rating,
    review_count: product.review_count,
    category_id: "cat_flagship"
  };
  const details = [
    ["Brand", product.merchant?.name || "YourBrand"],
    ["Model Name", product.title],
    ["Screen Size", specs?.metadata?.screen || "6.9 Inches"],
    ["Color", selectedColor],
    ["Hard Disk Size", selectedStorage],
    ["CPU Model", specs?.metadata?.chip || "A18 Pro"],
    ["Ram Memory Installed", specs?.metadata?.memory || "8 GB"]
  ];
  const bullets = [
    "HIGH-LEVEL PERFORMANCE - responsive hardware, polished product page information, and verified seller service.",
    "FAST MEMORY AND STORAGE - selected options update independently so customers can compare variants clearly.",
    "DYNAMIC DISPLAY AND SMOOTH VISUALS - product media, video, and buyer reviews help customers make decisions.",
    "STATE-OF-THE-ART SERVICE - payment QR, order tracking, logistics upload, and after-sales flow remain connected.",
    "FULL-SURROUND SHOPPING EXPERIENCE - saved items, cart, store page, and recommendation shelves support repeat buying."
  ];

  return (
    <div className="amazon-product-page">
      <section className="amazon-sponsored-banner">
        <strong>{product.title}</strong>
        <div>
          {Array.from({ length: 4 }, (_, index) => (
            <button type="button" key={index} onClick={() => onMediaChange(index % mediaCount)}>
              <ImageCrop variant={index} label={`${product.title} banner ${index + 1}`} />
            </button>
          ))}
        </div>
        <span>Sponsored</span>
      </section>

      <nav className="amazon-breadcrumb" aria-label="商品路径">
        Electronics <ChevronRight size={14} /> Computers & Accessories <ChevronRight size={14} /> Computers & Tablets <ChevronRight size={14} /> Product Details
      </nav>

      <section className="amazon-detail-grid">
        <aside className="amazon-thumb-rail" aria-label="商品缩略图">
          {product.media.map((media, index) => (
            <button className={activeMedia === index ? "active" : ""} key={media.id} onClick={() => onMediaChange(index)}>
              <ImageCrop variant={index} label={media.label} src={media.url} />
              {media.type === "video" && <Play size={14} fill="currentColor" />}
            </button>
          ))}
          <button className={activeMedia === product.media.length ? "active video-thumb" : "video-thumb"} onClick={() => onMediaChange(product.media.length)}>
            <Play size={20} fill="currentColor" />
            <span>8 VIDEOS</span>
          </button>
        </aside>

        <section className="amazon-main-image">
          {activeMedia < product.media.length ? (
            <SelectedMedia
              variant={activeMedia}
              src={product.media[activeMedia]?.url}
              isVideo={product.media[activeMedia]?.type === "video"}
            />
          ) : (
            <ProductVisual variant={activeMedia} />
          )}
          <button type="button" onClick={onShare} aria-label="分享商品">
            <Share2 size={22} />
          </button>
          <a href="#product-video-section">Click to see full view</a>
        </section>

        <section className="amazon-product-core">
          <div className="amazon-brand-mark">{product.merchant?.name || "ASUS"}</div>
          <button className="amazon-store-link" type="button" onClick={onSellerHomeOpen}>Visit the Store</button>
          <h1>{product.title}, {product.subtitle}</h1>
          <div className="amazon-rating-line">
            <span>{product.rating.toFixed(1)}</span>
            <Stars value={product.rating} />
            <a href="#reviews">({product.review_count.toLocaleString()})</a>
          </div>
          <span className="overall-pick amazon-pick">Overall Pick</span>
          <p className="amazon-bought">500+ bought in past month</p>
          <div className="amazon-price">{formatMoney(totalPrice, product.currency)}</div>
          <p>No Import Charges & $49.41 Shipping to Lao People's Democratic Republic <a href="#">Details</a></p>
          <div className="amazon-savings-list">
            {["Lego Batman - Legacy of the Dark", "Get 1 Month of Xbox GamePass for new users", "Save $20 on M365 12Mo Personal"].map((saving) => (
              <div key={saving}>
                <span>Savings</span>
                <p>{saving}</p>
                <a href="#">Shop items ›</a>
              </div>
            ))}
          </div>
          <div className="amazon-style-picker">
            <strong>Style: {selectedColor} | {selectedStorage}</strong>
            <div>
              {storages.map((sku) => (
                <button className={selectedStorage === sku.storage ? "active" : ""} key={sku.storage} onClick={() => onStorageChange(sku.storage)}>
                  <b>{sku.storage}</b>
                  <span>{formatMoney(product.price + sku.price_delta, product.currency)}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="amazon-style-picker color-picker">
            <strong>Color: {selectedColor}</strong>
            <div>
              {colors.map((sku) => (
                <button className={selectedColor === sku.color ? "active" : ""} key={sku.color} onClick={() => onColorChange(sku.color)}>
                  <i style={{ backgroundColor: sku.color_hex }} />
                  <b>{sku.color}</b>
                </button>
              ))}
            </div>
          </div>
          <dl className="amazon-spec-list">
            {details.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <section className="amazon-about">
            <h2>About this item</h2>
            <ul>
              {bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
            </ul>
            <a href="#product-information">See more product details</a>
          </section>
        </section>

        <aside className="amazon-buy-box">
          <strong>{formatMoney(totalPrice, product.currency)}</strong>
          <p>No Import Charges & $49.41 Shipping to Lao People's Democratic Republic Details</p>
          <b>$49.41 delivery Tuesday, June 2. Order within 1 hr 44 mins</b>
          <span><Truck size={16} /> Deliver to Lao People's Democratic Republic</span>
          <h3>In Stock</h3>
          <div className="amazon-quantity">
            <button type="button" onClick={() => onQuantityChange(Math.max(1, quantity - 1))}>-</button>
            <span>{quantity}</span>
            <button type="button" onClick={() => onQuantityChange(Math.min(5, quantity + 1))}>+</button>
          </div>
          <button className="amazon-cart-button" type="button" onClick={onAddToCart}>Add to cart</button>
          <button className="amazon-buy-button" type="button" onClick={onCheckout}>Buy Now / 立即购买</button>
          <dl>
            <div><dt>Shipper / Seller</dt><dd>{product.merchant?.name || "YourBrand"}</dd></div>
            <div><dt>Returns</dt><dd>30-day refund / replacement</dd></div>
            <div><dt>Payment</dt><dd>Secure transaction</dd></div>
          </dl>
          <button className="amazon-list-button" type="button" onClick={onSaveForLater}>Add to List</button>
          <div className="amazon-social-mini">
            <button type="button" onClick={() => onSocialOpen("whatsapp")}>WhatsApp</button>
            <button type="button" onClick={() => onSocialOpen("messenger")}>Messenger</button>
          </div>
        </aside>
      </section>

      <FrequentlyBoughtTogether product={product} productList={productList} onAddToCart={onAddToCart} />
      <AmazonRecommendationShelf title="Customers frequently viewed" products={productList} onProductSelect={onProductSelect} />
      <section className="amazon-product-information" id="product-information">
        <h2>Product information</h2>
        <div className="amazon-info-grid">
          {["Additional details", "Display", "Connectivity", "Ports & Slots", "Processor", "Item details", "Memory", "Battery", "Input Devices"].map((title, index) => (
            <details key={title} open={index === 0}>
              <summary>{title}</summary>
              <p>{index === 0 ? "Warranty, support, seller service, payment protection, and logistics status are available through the platform." : `${title} specifications are maintained by the seller template and can be edited from admin CMS.`}</p>
            </details>
          ))}
        </div>
        <h2>Product Description</h2>
        <p>{product.blocks.find((block) => block.type === "feature")?.body || product.subtitle}</p>
      </section>
      <section className="amazon-video-wrap" id="product-video-section">
        <h2>Product Videos</h2>
        <VideoCard product={product} />
      </section>
      <section className="amazon-brand-story">
        <h2>From the brand</h2>
        <div>
          <strong>FOR THOSE WHO DARE</strong>
          <span>REPUBLIC OF GAMERS</span>
          <p>Shop verified products, compare real reviews, and enter the seller store for more choices.</p>
          <button type="button" onClick={onSellerHomeOpen}>Visit the Store</button>
        </div>
      </section>
      <section className="amazon-box-brand">
        <div>
          <h2>What's in the box</h2>
          <ul>
            <li>{selectedSku.sku}</li>
            <li>Powercord and Adapter</li>
            <li>User Guide</li>
          </ul>
        </div>
        <div>
          <h2>Top Brand: {product.merchant?.name || "YourBrand"}</h2>
          <div className="brand-score-grid">
            {["Highly Rated", "Trending", "Low Returns"].map((item) => (
              <article key={item}>
                <strong>{item}</strong>
                <span>100K+ customers trust products from this brand.</span>
              </article>
            ))}
          </div>
        </div>
      </section>
      <AmazonRecommendationShelf title="Products related to this item" products={productList} onProductSelect={onProductSelect} />
      <section className="review-order-grid amazon-review-grid" id="reviews">
        <RatingSummary product={product} />
        <ReviewList product={product} onProductUpdate={onProductUpdate} />
        <FAQList faqs={faqs} />
        <OrderLookup onOrder={onOrder} />
      </section>
    </div>
  );
}

function FrequentlyBoughtTogether({
  product,
  productList,
  onAddToCart
}: {
  product: Product;
  productList: ProductSummary[];
  onAddToCart: () => void;
}) {
  const addOn = productList.find((item) => item.slug !== product.slug) ?? productList[0];
  const total = product.price + (addOn?.price || 0);
  return (
    <section className="amazon-bought-together">
      <h2>Frequently bought together</h2>
      <div className="bought-combo">
        <article>
          <ImageCrop variant={1} label={product.title} />
          <strong>This item: {product.title}</strong>
          <span>{formatMoney(product.price, product.currency)}</span>
        </article>
        <b>+</b>
        <article>
          <ImageCrop variant={2} label={addOn?.title || "Accessory"} />
          <strong>{addOn?.title || "Cooling Pad Accessory"}</strong>
          <span>{addOn ? formatMoney(addOn.price, addOn.currency) : "$25.18"}</span>
        </article>
        <div>
          <span>Total price: {formatMoney(total, product.currency)}</span>
          <button type="button" onClick={onAddToCart}>Add both to Cart</button>
        </div>
      </div>
    </section>
  );
}

function AmazonRecommendationShelf({
  title,
  products,
  onProductSelect
}: {
  title: string;
  products: ProductSummary[];
  onProductSelect: (slug: string) => void;
}) {
  const shelfProducts = products.length ? products : [];
  return (
    <section className="amazon-related-shelf">
      <div className="section-title-row">
        <h2>{title}</h2>
        <span>Page 1 of 7</span>
      </div>
      <div className="amazon-related-row">
        {shelfProducts.slice(0, 7).map((item, index) => (
          <button type="button" key={`${title}-${item.slug}-${index}`} onClick={() => onProductSelect(item.slug)}>
            <ImageCrop variant={index + 3} label={item.title} />
            <strong>{item.title}</strong>
            <Stars value={item.rating} />
            <span>{formatMoney(item.price, item.currency)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function ProductGallery({
  product,
  activeMedia,
  onMediaChange
}: {
  product: Product;
  activeMedia: number;
  onMediaChange: (index: number) => void;
}) {
  const itemCount = product.media.length + 1;
  const showPrevious = () => onMediaChange((activeMedia - 1 + itemCount) % itemCount);
  const showNext = () => onMediaChange((activeMedia + 1) % itemCount);

  return (
    <div className="gallery-card">
      <div className="thumb-stack">
        {product.media.map((media, index) => (
          <button className={activeMedia === index ? "thumb active" : "thumb"} key={media.id} onClick={() => onMediaChange(index)}>
            <ImageCrop variant={index} label={media.label} src={media.url} />
            {media.type === "video" && <Play size={16} fill="currentColor" />}
          </button>
        ))}
        <button className={activeMedia === product.media.length ? "thumb cube active" : "thumb cube"} onClick={() => onMediaChange(product.media.length)}>
          <span>3D</span>
        </button>
      </div>
      <div className="product-canvas">
        <button className="canvas-arrow left" aria-label="上一张" onClick={showPrevious}>
          <ChevronRight size={18} />
        </button>
        {activeMedia < product.media.length ? (
          <SelectedMedia
            variant={activeMedia}
            src={product.media[activeMedia]?.url}
            isVideo={product.media[activeMedia]?.type === "video"}
          />
        ) : (
          <ProductVisual variant={activeMedia} />
        )}
        <button className="canvas-arrow" aria-label="下一张" onClick={showNext}>
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

function ImageCrop({ variant, label, src }: { variant: number; label: string; src?: string }) {
  return <span className={`image-crop crop-${variant}`} style={src ? { backgroundImage: `url("${src}")` } : undefined} aria-label={label} />;
}

function SelectedMedia({ variant, src, isVideo }: { variant: number; src?: string; isVideo: boolean }) {
  return (
    <div
      className={`selected-media selected-media-${variant}`}
      style={src ? { backgroundImage: `url("${src}")` } : undefined}
      aria-label={`产品图片 ${variant + 1}`}
    >
      {isVideo && (
        <button className="selected-media-play" aria-label="播放产品视频">
          <Play size={34} fill="currentColor" />
        </button>
      )}
    </div>
  );
}

function ProductVisual({ variant }: { variant: number }) {
  return (
    <div className={`product-visual visual-${variant}`}>
      <div className="phone phone-back">
        <span />
        <i />
      </div>
      <div className="phone phone-front">
        <b />
        <em />
      </div>
    </div>
  );
}

function ProductPanel({
  product,
  selectedSku,
  selectedColor,
  selectedStorage,
  onColorChange,
  onStorageChange,
  totalPrice,
  quantity,
  onQuantityChange,
  onCheckout,
  onAddToCart,
  onSaveForLater,
  onSellerHomeOpen
}: {
  product: Product;
  selectedSku: ProductSku;
  selectedColor: string;
  selectedStorage: string;
  onColorChange: (color: string) => void;
  onStorageChange: (storage: string) => void;
  totalPrice: number;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onCheckout: () => void;
  onAddToCart: () => void;
  onSaveForLater: () => void;
  onSellerHomeOpen: () => void;
}) {
  const colors = product.skus.filter((sku, index, list) => list.findIndex((item) => item.color === sku.color) === index);
  const storages = product.skus.filter((sku, index, list) => list.findIndex((item) => item.storage === sku.storage) === index);
  const merchant = product.merchant;
  const openMerchantChat = (channel: "whatsapp" | "messenger") => {
    const text = productShareText(product, channel);
    track(`consult_${channel}`, channel, { merchantId: merchant?.id });
    if (channel === "whatsapp") {
      const phone = (merchant?.whatsapp_number || "8562055551234").replace(/[^\d]/g, "");
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      return;
    }
    window.open(merchant?.messenger_url || socialProfiles.messenger.profileUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <article className="product-panel">
      <div className="badges">
        <span>新品</span>
        <span>热销</span>
      </div>
      <h1>{product.title}</h1>
      <p className="subtitle">{product.subtitle}</p>
      <div className="rating-line" id="section-4">
        <Stars value={product.rating} />
        <strong>{product.rating.toFixed(1)}</strong>
        <span>({product.review_count} 条评价)</span>
        <span>已售 4,062 件</span>
      </div>
      <div className="price-line" id="section-5">
        <strong>{formatMoney(totalPrice, product.currency)}</strong>
        <del>{formatMoney(product.compare_at_price, product.currency)}</del>
        <span>-10%</span>
      </div>
      <div className="timer">
        <span>活动倒计时</span>
        {["02", "14", "35", "28"].map((value) => (
          <b key={value}>{value}</b>
        ))}
      </div>

      <div className="choice-group" id="section-6">
        <p>颜色</p>
        <div className="swatches">
          {colors.map((sku) => (
            <button
              key={sku.color}
              className={selectedColor === sku.color ? "swatch active" : "swatch"}
              onClick={() => onColorChange(sku.color)}
            >
              <span style={{ backgroundColor: sku.color_hex }} />
              {sku.color}
            </button>
          ))}
        </div>
      </div>

      <div className="choice-group">
        <p>存储容量</p>
        <div className="storage-options">
          {storages.map((sku) => (
            <button
              key={sku.storage}
              className={selectedStorage === sku.storage ? "active" : ""}
              onClick={() => onStorageChange(sku.storage)}
            >
              {sku.storage}
            </button>
          ))}
        </div>
      </div>

      <div className="quantity-row">
        <span>数量</span>
        <div>
          <button onClick={() => onQuantityChange(Math.max(1, quantity - 1))} aria-label="减少数量">
            <Minus size={15} />
          </button>
          <b>{quantity}</b>
          <button onClick={() => onQuantityChange(Math.min(5, quantity + 1))} aria-label="增加数量">
            <Plus size={15} />
          </button>
        </div>
      </div>

      <div className="buy-grid" id="section-7">
        <button className="primary-button" onClick={onCheckout}>
          立即购买
        </button>
        <button className="secondary-button" onClick={onAddToCart}>
          <ShoppingCart size={18} />
          加入购物车
        </button>
        <button className="whatsapp" onClick={() => openMerchantChat("whatsapp")}>
          <MessageCircle size={17} />
          WhatsApp 咨询
        </button>
        <button className="messenger" onClick={() => openMerchantChat("messenger")}>
          <Send size={17} />
          Messenger 咨询
        </button>
      </div>
      <div className="seller-mini-card">
        <div>
          <span>店家主页</span>
          <strong>{merchant?.name || "YourBrand"}</strong>
          <small>{merchant?.level || "Verified Seller"} · {merchant?.category || "Digital Store"}</small>
        </div>
        <div className="seller-mini-actions">
          <button type="button" onClick={onSaveForLater}>
            <Heart size={16} />
            保存
          </button>
          <button type="button" onClick={onSellerHomeOpen} aria-label="查看店家信息主页">
            <Home size={16} />
            查看
          </button>
        </div>
      </div>
    </article>
  );
}

function TrustStrip() {
  const items: Array<[LucideIcon, string, string]> = [
    [Truck, "全国包邮", "订单满 199 元免运费"],
    [PackageCheck, "预计 1-3 天送达", "大陆与万象配送"],
    [RotateCcw, "7 天无理由退换", "买贵退差价"],
    [ShieldCheck, "官方正品保障", "100% 正品,假一赔十"]
  ];
  return (
    <section className="trust-strip" id="section-8">
      {items.map(([Icon, title, body]) => (
        <div key={String(title)}>
          <Icon size={22} />
          <strong>{title}</strong>
          <span>{body}</span>
        </div>
      ))}
    </section>
  );
}

function FeatureGrid({ features }: { features: ProductBlock[] }) {
  const icons = [Cpu, Sparkles, Camera, ZoomIn, ShieldCheck];
  return (
    <section className="section-card" id="section-9">
      <h2>核心卖点</h2>
      <div className="feature-grid">
        {features.map((feature, index) => {
          const Icon = icons[index] ?? Sparkles;
          return (
            <div className="feature-card" key={feature.id}>
              <Icon size={28} />
              <strong>{feature.title}</strong>
              <span>{feature.body}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProductDetails({ product }: { product: Product }) {
  const specRows = product.blocks.find((block) => block.type === "specs")?.body.split("\n").slice(0, 4) ?? [];
  return (
    <section className="section-card detail-card">
      <h2>产品详情</h2>
      <p>
        {product.title} 采用全新钛金属设计,搭载 A18 Pro 芯片、ProMotion 自适应刷新率和专业影像系统。
        页面按社交电商转化路径组织内容,顾客可以在同一页面完成了解、咨询、下单和售后查询。
      </p>
      <div className="mini-media-row">
        {["超视网膜 XDR", "ProMotion", "全天候电池", "USB-C"].map((label, index) => (
          <figure key={label}>
            <ImageCrop variant={index} label={label} />
            <figcaption>{label}</figcaption>
          </figure>
        ))}
      </div>
      <div className="product-info-grid">
        <strong>参数快览</strong>
        {specRows.map((row) => {
          const [label, value] = row.split(": ");
          return (
            <span key={row}>
              <b>{label}</b>
              {value}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function isPlayableVideo(url: string) {
  return /\.(mp4|webm|ogg|mov)$/i.test(url);
}

function formatDuration(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0:00";
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

type SubtitleLanguage = "th" | "lo";

const translatedCaptionSamples: Record<SubtitleLanguage, string[]> = {
  th: [
    "คำบรรยาย AI: แสดงรายละเอียดสินค้าและจุดเด่นแบบเรียลไทม์",
    "แปลเป็นภาษาไทย: คุณภาพสินค้า ราคา และบริการหลังการขายชัดเจน",
    "รองรับวิดีโอแนวนอน แนวตั้ง และสี่เหลี่ยมสำหรับทุกแพลตฟอร์ม"
  ],
  lo: [
    "ຄຳບັນຍາຍ AI: ສະແດງຈຸດເດັ່ນສິນຄ້າແບບເວລາຈິງ",
    "ແປເປັນພາສາລາວ: ລາຄາ ຄຸນນະພາບ ແລະບໍລິການຊັດເຈນ",
    "ຮອງຮັບວິດີໂອ 16:9, 9:16, 1:1 ແລະ 4:5"
  ]
};

function VideoCard({ product }: { product: Product }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [subtitleLanguage, setSubtitleLanguage] = useState<SubtitleLanguage>("lo");
  const [videoAspect, setVideoAspect] = useState({ ratio: 16 / 9, label: "16:9", css: "16 / 9" });
  const [isExpanded, setIsExpanded] = useState(false);
  const playableVideo = [...product.media].reverse().find((media) => media.type === "video" && isPlayableVideo(media.url));
  const poster = product.media.find((media) => media.type === "hero")?.url || "/assets/lao-online-shopping.png";
  const captionLines = translatedCaptionSamples[subtitleLanguage];
  const activeCaption = captionLines[Math.min(captionLines.length - 1, Math.floor((currentTime / Math.max(duration || 1, 1)) * captionLines.length))] || captionLines[0];
  const videoCardStyle = {
    "--video-aspect": videoAspect.css,
    "--video-ratio": String(videoAspect.ratio)
  } as CSSProperties;

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsExpanded(false);
    setVideoAspect({ ratio: 16 / 9, label: "16:9", css: "16 / 9" });
  }, [product.id, playableVideo?.url]);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video || !playableVideo) return;
    if (video.paused) {
      await video.play();
    } else {
      video.pause();
    }
  };

  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Number(event.target.value);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const changeVolume = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextVolume = Number(event.target.value);
    setVolume(nextVolume);
    if (videoRef.current) {
      videoRef.current.volume = nextVolume;
      if (nextVolume > 0 && videoRef.current.muted) {
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    }
  };

  const toggleMuted = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) videoRef.current.muted = nextMuted;
  };

  return (
    <section className={isExpanded ? "video-card expanded" : "video-card"} id="section-11" style={videoCardStyle}>
      <div
        className="video-frame"
        onClick={togglePlayback}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") togglePlayback();
        }}
        role="button"
        aria-label={isPlaying ? "暂停视频" : "播放视频"}
        tabIndex={0}
      >
        {playableVideo ? (
          <video
            ref={videoRef}
            src={playableVideo.url}
            poster={poster}
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              setDuration(video.duration);
              video.volume = volume;
              video.muted = isMuted;
              if (video.videoWidth && video.videoHeight) {
                const ratio = video.videoWidth / video.videoHeight;
                setVideoAspect({
                  ratio,
                  label: `${video.videoWidth}:${video.videoHeight}`,
                  css: `${video.videoWidth} / ${video.videoHeight}`
                });
              }
            }}
          />
        ) : (
          <ProductVisual variant={1} />
        )}
        <button
          className="video-play-toggle"
          aria-label={isPlaying ? "暂停视频" : "播放视频"}
          onClick={(event) => {
            event.stopPropagation();
            togglePlayback();
          }}
          disabled={!playableVideo}
        >
          {isPlaying ? <Minus size={30} /> : <Play fill="currentColor" />}
        </button>
        <button
          className="video-expand-button"
          type="button"
          aria-label={isExpanded ? "退出放大播放" : "放大播放"}
          onClick={(event) => {
            event.stopPropagation();
            setIsExpanded((value) => !value);
          }}
        >
          {isExpanded ? <X size={18} /> : <Maximize2 size={18} />}
        </button>
        <div className="video-title">
          <strong>{product.title}</strong>
          <span>{playableVideo ? "产品视频正在播放" : "后台上传 3 分钟内视频后可播放"}</span>
        </div>
        {captionsEnabled && playableVideo && (
          <div className="video-caption" aria-live="polite">
            <small>AI 翻译字幕 · {subtitleLanguage === "th" ? "泰语" : "老语"} · {videoAspect.label}</small>
            <p>{activeCaption}</p>
          </div>
        )}
      </div>
      <div className="video-controls" onClick={(event) => event.stopPropagation()}>
        <span>{formatDuration(currentTime)} / {formatDuration(duration || 180)}</span>
        <input
          type="range"
          min="0"
          max={duration || 180}
          value={Math.min(currentTime, duration || 180)}
          onChange={seek}
          disabled={!playableVideo}
          aria-label="视频进度"
        />
        <button
          className={isMuted ? "video-tool active" : "video-tool"}
          type="button"
          onClick={toggleMuted}
          disabled={!playableVideo}
          aria-label={isMuted ? "取消静音" : "静音"}
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <div className="volume-control">
          <Headphones size={15} />
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={changeVolume}
            disabled={!playableVideo}
            aria-label="视频音量"
          />
        </div>
        <button
          className={captionsEnabled ? "video-tool active" : "video-tool"}
          type="button"
          onClick={() => setCaptionsEnabled((value) => !value)}
          disabled={!playableVideo}
          aria-label={captionsEnabled ? "关闭字幕" : "打开字幕"}
        >
          字幕
        </button>
        <label className="subtitle-control">
          <Languages size={15} />
          <select
            value={subtitleLanguage}
            onChange={(event) => setSubtitleLanguage(event.target.value as SubtitleLanguage)}
            disabled={!playableVideo}
            aria-label="AI字幕目标语言"
          >
            <option value="th">泰语</option>
            <option value="lo">老语</option>
          </select>
        </label>
      </div>
    </section>
  );
}

function SpecsCard({ block }: { block: ProductBlock }) {
  return (
    <section className="section-card" id="section-12">
      <h2>{block.title}</h2>
      <table className="spec-table">
        <tbody>
          {block.body.split("\n").map((row) => {
            const [label, value] = row.split(": ");
            return (
              <tr key={label}>
                <th>{label}</th>
                <td>{value}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ComparisonCard({ block }: { block: ProductBlock }) {
  const rows = block.body.split("\n").map((row) => row.split("|"));
  const labels = ["机型", "芯片", "屏幕尺寸", "主摄像头", "电池容量", "防水防尘"];
  return (
    <section className="section-card" id="section-13">
      <h2>{block.title}</h2>
      <table className="compare-table">
        <thead>
          <tr>
            {labels.map((label) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              {row.map((cell) => (
                <td key={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RatingSummary({ product }: { product: Product }) {
  const stats = product.ratingStats;
  const total = stats?.total ?? product.review_count;
  const average = stats?.average || product.rating;
  return (
    <section className="section-card rating-card">
      <h2>顾客评分总览</h2>
      <div className="rating-big">{average.toFixed(1)}</div>
      <Stars value={average} />
      <span>基于 {total} 条已审核评价 · {stats?.verifiedTotal ?? 0} 条已验证购买</span>
      {[5, 4, 3, 2, 1].map((rating) => {
        const value = stats?.percentages?.[String(rating) as "1" | "2" | "3" | "4" | "5"] ?? 0;
        return (
        <div className="rating-bar" key={rating}>
          <small>{rating} 星</small>
          <span>
            <i style={{ width: `${value}%` }} />
          </span>
          <small>{value}%</small>
        </div>
      );})}
      <p className="rating-policy">评分由已审核评价实时计算,评价提交后保留内容哈希,前台不可编辑。</p>
    </section>
  );
}

function ReviewList({ product, onProductUpdate }: { product: Product; onProductUpdate: (product: Product) => void }) {
  const [filter, setFilter] = useState<"all" | "good" | "media">("all");
  const [form, setForm] = useState({ customerName: "", rating: 5, channel: "direct", body: "", orderNumber: "", phoneSuffix: "" });
  const [images, setImages] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filteredReviews = product.reviews.filter((review) => {
    if (filter === "good") return review.rating >= 4;
    if (filter === "media") return review.media?.length > 0;
    return true;
  });

  const chooseReviewImages = (files: FileList | null) => {
    const next = Array.from(files ?? []).filter((file) => file.type.startsWith("image/")).slice(0, 8);
    setImages(next);
    setError((files?.length ?? 0) > 8 ? "单个评价最多上传 8 张图片,已保留前 8 张。" : "");
  };

  const chooseReviewVideos = (files: FileList | null) => {
    const next = Array.from(files ?? []).filter((file) => file.type.startsWith("video/")).slice(0, 2);
    setVideos(next);
    setError((files?.length ?? 0) > 2 ? "单个评价最多上传 2 个视频,已保留前 2 个。" : "");
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const result = await submitReview(product.slug, form, images, videos);
      onProductUpdate(result.product);
      setMessage(result.status === "approved" ? "评价已发布,评分已实时更新。" : "评价已提交,订单验证失败,已进入审核。");
      setForm({ customerName: "", rating: 5, channel: "direct", body: "", orderNumber: "", phoneSuffix: "" });
      setImages([]);
      setVideos([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "评价提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  const helpful = async (reviewId: string) => {
    try {
      const updated = await markReviewHelpful(reviewId);
      onProductUpdate(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "操作失败");
    }
  };

  return (
    <section className="section-card review-list" id="section-15">
      <div className="section-title-row">
        <h2>顾客评价</h2>
        <div className="tabs">
          <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>全部({product.ratingStats.total})</button>
          <button className={filter === "good" ? "active" : ""} onClick={() => setFilter("good")}>好评</button>
          <button className={filter === "media" ? "active" : ""} onClick={() => setFilter("media")}>有图/视频</button>
        </div>
      </div>
      <form className="review-form" onSubmit={submit}>
        <div className="review-form-grid">
          <input required placeholder="你的姓名" value={form.customerName} onChange={(event) => setForm({ ...form, customerName: event.target.value })} />
          <select value={form.channel} onChange={(event) => setForm({ ...form, channel: event.target.value })}>
            <option value="direct">官网</option>
            <option value="facebook">Facebook</option>
            <option value="tiktok">TikTok</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
          </select>
          <select value={form.rating} onChange={(event) => setForm({ ...form, rating: Number(event.target.value) })}>
            {[5, 4, 3, 2, 1].map((rating) => <option value={rating} key={rating}>{rating} 星</option>)}
          </select>
          <input placeholder="订单号(可选,用于验证购买)" value={form.orderNumber} onChange={(event) => setForm({ ...form, orderNumber: event.target.value })} />
          <input placeholder="手机后四位(可选)" maxLength={4} value={form.phoneSuffix} onChange={(event) => setForm({ ...form, phoneSuffix: event.target.value })} />
        </div>
        <textarea required minLength={5} placeholder="写下真实使用体验,提交后前台不可编辑。" value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} />
        <div className="review-upload-row">
          <label>图片最多 8 张<input type="file" accept="image/*" multiple onChange={(event) => chooseReviewImages(event.target.files)} /></label>
          <label>视频最多 2 个<input type="file" accept="video/*" multiple onChange={(event) => chooseReviewVideos(event.target.files)} /></label>
          <button className="primary-button" disabled={submitting}>{submitting ? "提交中..." : "提交评价"}</button>
        </div>
        <p className="review-file-count">已选 {images.length}/8 张图片,{videos.length}/2 个视频</p>
        {message && <p className="form-success">{message}</p>}
        {error && <p className="form-error">{error}</p>}
      </form>
      {filteredReviews.map((review) => (
        <article className="review-item" key={review.id}>
          <div className="avatar">{review.customer_name.slice(0, 1)}</div>
          <div>
            <strong>{review.customer_name}</strong>
            <span>{review.channel} · {review.verified ? "已验证购买" : "已审核"} · #{review.content_hash?.slice(0, 8) ?? "immutable"}</span>
            <Stars value={review.rating} />
            <p>{review.body}</p>
            {review.media?.length > 0 && (
              <div className="review-photos">
                {review.media.map((media, index) =>
                  media.type === "video" ? (
                    <video key={media.id} src={media.url} controls preload="metadata" />
                  ) : (
                    <ImageCrop key={media.id} src={media.url} variant={index} label="买家秀" />
                  )
                )}
              </div>
            )}
          </div>
          <button aria-label="评价有帮助" onClick={() => helpful(review.id)}>
            <Heart size={16} />
            <span>{review.helpful_count ?? 0}</span>
          </button>
        </article>
      ))}
      {!filteredReviews.length && <p className="muted">暂无符合条件的评价。</p>}
    </section>
  );
}

function FAQList({ faqs }: { faqs: ProductBlock[] }) {
  return (
    <section className="section-card faq-card" id="section-16">
      <h2>常见问题</h2>
      {faqs.map((faq) => (
        <details key={faq.id}>
          <summary>
            {faq.title}
            <ChevronRight size={16} />
          </summary>
          <p>{faq.body}</p>
        </details>
      ))}
      <a href="#">查看更多问题</a>
    </section>
  );
}

function OrderLookup({ onOrder }: { onOrder: (order: Order) => void }) {
  const [orderNumber, setOrderNumber] = useState("");
  const [phoneSuffix, setPhoneSuffix] = useState("");
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const order = await lookupOrder(orderNumber, phoneSuffix);
      onOrder(order);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查询失败");
    }
  };

  return (
    <section className="section-card order-card" id="section-17">
      <h2>订单与物流查询</h2>
      <form onSubmit={submit}>
        <input value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} placeholder="请输入订单号" />
        <input value={phoneSuffix} onChange={(event) => setPhoneSuffix(event.target.value)} placeholder="手机后四位" maxLength={4} />
        <button>查询订单</button>
      </form>
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}

function ShareCard({
  product,
  onShare,
  onSocialOpen
}: {
  product: Product;
  onShare: () => void;
  onSocialOpen: (channel: SocialChannel) => void;
}) {
  return (
    <section className="section-card share-card" id="section-19">
      <h2>分享此产品</h2>
      <div className="share-platforms">
        {(Object.keys(socialProfiles) as SocialChannel[]).map((channel) => (
          <button key={channel} onClick={() => onSocialOpen(channel)}>
            {socialProfiles[channel].label}
          </button>
        ))}
      </div>
      <button onClick={onShare}>
        <Copy size={16} />
        复制链接
      </button>
      <button
        onClick={() => {
          copyProductInfo(product, "facebook").catch(() => undefined);
        }}
      >
        <QrCode size={16} />
        复制产品信息
      </button>
    </section>
  );
}

function RecommendedProducts({
  productList,
  currentSlug,
  onProductSelect
}: {
  productList: ProductSummary[];
  currentSlug: string;
  onProductSelect: (slug: string) => void;
}) {
  const products = productList.filter((product) => product.slug !== currentSlug).slice(0, 4);
  return (
    <section className="section-card recommend-card" id="section-18">
      <h2>推荐产品</h2>
      <div className="recommend-grid">
        {products.map((item, index) => (
          <article key={item.slug}>
            <button className="recommend-image-button" onClick={() => onProductSelect(item.slug)} aria-label={`查看 ${item.title}`}>
              <ImageCrop variant={index} label={item.title} />
            </button>
            <button className="recommend-title-button" onClick={() => onProductSelect(item.slug)}>
              <strong>{item.title}</strong>
            </button>
            <span>{formatMoney(item.price, item.currency)}</span>
            <del>{formatMoney(item.compare_at_price, item.currency)}</del>
          </article>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" id="section-20">
      <div>
        <a className="brand" href="#">
          <Apple size={18} fill="currentColor" />
          <span>YourBrand</span>
        </a>
        <p>致力于为用户提供高质量的产品和服务,让购物体验更安心。</p>
        <div className="footer-socials">
          <span>f</span>
          <span>t</span>
          <span>m</span>
          <span>w</span>
        </div>
      </div>
      {["购物指南", "帮助中心", "关于我们"].map((title) => (
        <div key={title}>
          <h3>{title}</h3>
          <a href="#">新手指南</a>
          <a href="#">支付方式</a>
          <a href="#">售后服务</a>
        </div>
      ))}
      <div className="newsletter">
        <h3>订阅我们的新品</h3>
        <p>获取新品、优惠和活动信息</p>
        <input placeholder="输入你的邮箱" />
        <button>订阅</button>
      </div>
    </footer>
  );
}

function MobilePreview({
  product,
  selectedSku,
  totalPrice,
  onCheckout
}: {
  product: Product;
  selectedSku: ProductSku;
  totalPrice: number;
  onCheckout: () => void;
}) {
  return (
    <aside className="mobile-preview" aria-label="移动端页面展示">
      <p className="rail-kicker">03 移动端页面展示</p>
      <div className="phone-preview">
        <div className="phone-status">9:41</div>
        <div className="phone-top">
          <ChevronRight size={17} />
          <div>
            <Search size={16} />
            <ShoppingCart size={16} />
          </div>
        </div>
        <ProductVisual variant={0} />
        <h3>{product.title}</h3>
        <p>{product.subtitle}</p>
        <Stars value={product.rating} />
        <strong>{formatMoney(totalPrice, product.currency)}</strong>
        <div className="mobile-storage">
          <button>{selectedSku.storage}</button>
          <button>512GB</button>
          <button>1TB</button>
        </div>
        <div className="mobile-buybar">
          <button>
            <MessageCircle size={16} />
            咨询
          </button>
          <button>
            <ShoppingCart size={16} />
            加购物车
          </button>
          <button onClick={onCheckout}>立即购买</button>
        </div>
        <nav className="mobile-bottom-nav">
          {[Home, Grid, Compass, ShoppingCart, UserRound].map((Icon, index) => (
            <Icon size={18} key={index} />
          ))}
        </nav>
      </div>
      <div className="social-entry-card">
        <p className="rail-kicker">04 社交平台入口效果</p>
        <button className="whatsapp">WhatsApp 咨询</button>
        <button className="messenger">Messenger 咨询</button>
        <button className="secondary-button">TikTok 短视频</button>
        <button className="facebook">分享至 Facebook</button>
      </div>
    </aside>
  );
}

function CheckoutDialog({
  product,
  customerProfile,
  selectedSku,
  quantity,
  totalPrice,
  onClose,
  onSellerHomeOpen,
  onCreated
}: {
  product: Product;
  customerProfile: CustomerProfile | null;
  selectedSku: ProductSku;
  quantity: number;
  totalPrice: number;
  onClose: () => void;
  onSellerHomeOpen: () => void;
  onCreated: (order: Order) => void;
}) {
  const customer = customerProfile?.customer;
  const defaultAddress = customerProfile?.addresses.find((address) => address.is_default)?.address_line || customer?.address || "";
  const [form, setForm] = useState({
    customerName: customer?.name || "",
    phone: customer?.phone || "",
    address: defaultAddress
  });
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);
  const [error, setError] = useState("");
  const merchant = product.merchant;
  const phoneSuffix = form.phone.replace(/\D/g, "").slice(-4);

  useEffect(() => {
    if (!customer || createdOrder) return;
    setForm((current) => ({
      customerName: current.customerName || customer.name || "",
      phone: current.phone || customer.phone || "",
      address: current.address || defaultAddress || ""
    }));
  }, [customer?.id, customer?.name, customer?.phone, defaultAddress, createdOrder]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const order = await apiCreateOrder({
        slug: product.slug,
        skuId: selectedSku.id,
        customerName: form.customerName,
        phone: form.phone,
        address: form.address,
        quantity,
        channel: sourceChannel,
        shareId: new URLSearchParams(window.location.search).get("share_id") || undefined,
        clientRequestId: `${Date.now()}-${form.phone}`
      });
      setCreatedOrder(order);
      setSubmitting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "提交失败");
      setSubmitting(false);
    }
  };

  const submitProof = async () => {
    if (!createdOrder || !proofFile) {
      setError("Please choose a payment screenshot first.");
      return;
    }
    setProofSubmitting(true);
    setError("");
    try {
      const verified = await uploadPaymentProof(createdOrder.order_number, phoneSuffix, proofFile);
      onCreated(verified);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment proof upload failed");
    } finally {
      setProofSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="提交订单">
      <form className="modal-card checkout-form" onSubmit={submit}>
        <button className="modal-close" type="button" onClick={onClose} aria-label="关闭">
          <X size={18} />
        </button>
        <h2>确认订单</h2>
        <p>{product.title} · {selectedSku.color} · {selectedSku.storage}</p>
        <div className="checkout-total">
          <span>合计</span>
          <strong>{formatMoney(totalPrice * quantity, product.currency)}</strong>
        </div>
        {createdOrder && (
          <section className="payment-step">
            <div className="payment-merchant-box dynamic-payment-box">
              <div className="payment-qr">
                <button className="qr-image-button" type="button" onClick={() => setQrExpanded(true)} aria-label="放大付款二维码">
                  <img src={createdOrder.payment_qr_data_url || merchant?.payment_qr_url || "/assets/lao-online-shopping.png"} alt="订单动态付款二维码" />
                </button>
                <span>已绑定订单金额</span>
              </div>
              <div className="payment-details">
                <strong>订单号:{createdOrder.order_number}</strong>
                <span>{merchant?.name || "YourBrand"} · {merchant?.level || "Verified Seller"}</span>
                <small>{merchant?.bank_account || "BCEL: YourBrand Trading | 010-12-9999999"}</small>
                <small>扫码后银行 App 应显示 {formatMoney(createdOrder.total_amount, product.currency)},无需手动输入金额。</small>
                <button type="button" onClick={onSellerHomeOpen}>查看店家主页</button>
              </div>
            </div>
            <label>
              上传付款截图
              <input type="file" accept="image/*" onChange={(event) => setProofFile(event.target.files?.[0] ?? null)} />
            </label>
            <div className="payment-proof-status">
              <span>{proofFile ? proofFile.name : "付款后上传截图,系统会保存并进行 AI 初审。"}</span>
              <button className="primary-button" type="button" disabled={proofSubmitting} onClick={submitProof}>
                {proofSubmitting ? "AI 验证中..." : "上传付款截图"}
              </button>
            </div>
          </section>
        )}
        {!createdOrder && (
          <>
        <section className="payment-merchant-box">
          <div className="payment-qr">
            <img src={merchant?.payment_qr_url || "/assets/lao-online-shopping.png"} alt={`${merchant?.name || "店家"}收款二维码`} />
            <span>扫码付款后提交订单</span>
          </div>
          <div className="payment-details">
            <strong>{merchant?.name || "YourBrand"}</strong>
            <span>{merchant?.level || "Verified Seller"} · {merchant?.category || "Digital Store"}</span>
            <small>{merchant?.bank_account || "BCEL: YourBrand Trading | 010-12-9999999"}</small>
            <button type="button" onClick={onSellerHomeOpen}>查看店家信息主页</button>
          </div>
        </section>
        <label>
          姓名
          <input required value={form.customerName} onChange={(event) => setForm({ ...form, customerName: event.target.value })} />
        </label>
        <p className={customer ? "checkout-prefill-note" : "checkout-prefill-note muted"}>
          {customer ? "已自动填入个人中心资料,可直接修改后提交订单。" : "未检测到已注册个人账户,请填写姓名、手机号和收货地址。"}
        </p>
        <label>
          手机号
          <input required value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
        </label>
        <label>
          收货地址
          <textarea required value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
        </label>
          </>
        )}
        {error && <p className="form-error">{error}</p>}
        {!createdOrder && (
        <button className="primary-button" disabled={submitting}>
          {submitting ? "提交中..." : "提交订单"}
        </button>
        )}
        {qrExpanded && createdOrder && (
          <div className="qr-zoom-backdrop" onClick={() => setQrExpanded(false)}>
            <div className="qr-zoom-card" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="modal-close" onClick={() => setQrExpanded(false)} aria-label="关闭放大二维码">
                <X size={18} />
              </button>
              <h3>扫码支付</h3>
              <img src={createdOrder.payment_qr_data_url || merchant?.payment_qr_url || "/assets/lao-online-shopping.png"} alt="放大付款二维码" />
              <strong>{formatMoney(createdOrder.total_amount, product.currency)}</strong>
              <span>订单号:{createdOrder.order_number}</span>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function CartDrawer({
  items,
  savedItems,
  onClose,
  onQuantityChange,
  onRemove,
  onSaveForLater,
  onMoveToCart,
  onRemoveSaved,
  onCheckout,
  onProductSelect
}: {
  items: CartItem[];
  savedItems: CartItem[];
  onClose: () => void;
  onQuantityChange: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onSaveForLater: (item: CartItem) => void;
  onMoveToCart: (item: CartItem) => void;
  onRemoveSaved: (id: string) => void;
  onCheckout: (item: CartItem) => void;
  onProductSelect: (slug: string) => void;
}) {
  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const currency = items[0]?.currency || savedItems[0]?.currency || "THB";
  return (
    <div className="drawer-backdrop" role="dialog" aria-modal="true" aria-label="购物车">
      <aside className="cart-drawer">
        <button className="modal-close" onClick={onClose} aria-label="关闭购物车">
          <X size={18} />
        </button>
        <div className="cart-header">
          <div>
            <h2>购物车</h2>
            <p>{items.length ? `${items.length} 个商品可结算` : "购物车为空"}</p>
          </div>
          <strong>{formatMoney(subtotal, currency)}</strong>
        </div>
        <section className="cart-section">
          <h3>购物车商品</h3>
          {items.length ? (
            items.map((item, index) => (
              <article className="cart-item" key={item.id}>
                <button className="cart-item-image" onClick={() => onProductSelect(item.slug)} aria-label={`查看 ${item.title}`}>
                  <ImageCrop variant={index} label={item.title} />
                </button>
                <div className="cart-item-main">
                  <button className="cart-item-title" onClick={() => onProductSelect(item.slug)}>
                    {item.title}
                  </button>
                  <span>{item.subtitle}</span>
                  <small>{item.color} · {item.storage} · {item.merchantName}</small>
                  <div className="cart-item-actions">
                    <label>
                      数量
                      <select value={item.quantity} onChange={(event) => onQuantityChange(item.id, Number(event.target.value))}>
                        {Array.from({ length: 10 }, (_, value) => value + 1).map((value) => (
                          <option value={value} key={value}>{value}</option>
                        ))}
                      </select>
                    </label>
                    <button onClick={() => onSaveForLater(item)}>稍后购买</button>
                    <button onClick={() => onRemove(item.id)}>删除</button>
                  </div>
                </div>
                <div className="cart-item-price">
                  <strong>{formatMoney(item.unitPrice * item.quantity, item.currency)}</strong>
                  <button className="primary-button" onClick={() => onCheckout(item)}>结算此商品</button>
                </div>
              </article>
            ))
          ) : (
            <p className="empty-cart-note">还没有商品。可以从产品页或店家主页加入购物车。</p>
          )}
        </section>
        <div className="cart-subtotal">
          <span>小计</span>
          <strong>{formatMoney(subtotal, currency)}</strong>
          <button className="primary-button" disabled={!items.length} onClick={() => items[0] && onCheckout(items[0])}>
            去结算
          </button>
        </div>
        <section className="cart-section saved-section">
          <h3>稍后购买</h3>
          {savedItems.length ? (
            savedItems.map((item, index) => (
              <article className="saved-item" key={item.id}>
                <ImageCrop variant={index + 1} label={item.title} />
                <div>
                  <button onClick={() => onProductSelect(item.slug)}>{item.title}</button>
                  <span>{formatMoney(item.unitPrice, item.currency)} · {item.color} · {item.storage}</span>
                </div>
                <button onClick={() => onMoveToCart(item)}>移入购物车</button>
                <button onClick={() => onRemoveSaved(item.id)}>删除</button>
              </article>
            ))
          ) : (
            <p className="empty-cart-note">保存的商品会显示在这里,可随时移回购物车。</p>
          )}
        </section>
      </aside>
    </div>
  );
}

function SellerStorefront({
  merchant,
  products,
  onClose,
  onProductAction
}: {
  merchant?: Merchant;
  products: ProductSummary[];
  onClose: () => void;
  onProductAction: (slug: string, action: "view" | "cart" | "save" | "buy") => void;
}) {
  const storeName = merchant?.name || "YourBrand";
  const storeProducts = products.slice(0, 12);

  return (
    <div className="modal-backdrop seller-home-backdrop" role="dialog" aria-modal="true" aria-label="店家主页">
      <section className="seller-home">
        <button className="modal-close" type="button" onClick={onClose} aria-label="关闭店家主页">
          <X size={18} />
        </button>
        <header className="seller-shop-hero">
          <div className="seller-shop-brand">
            <span>The Shop by {storeName}</span>
            <h2>{storeName}</h2>
            <p>A destination for trusted local shopping</p>
          </div>
          <div className="seller-shop-card">
            <strong>{merchant?.level || "Verified Seller"} · {merchant?.verification_status === "verified" ? "已认证" : "待审核"}</strong>
            <span>{merchant?.business_type || "Corporate"} · {merchant?.category || "Digital Store"}</span>
            <small>{merchant?.intro || "本地认证店家,提供正品保障、透明报价、快速配送与售后支持。"}</small>
          </div>
        </header>
        <nav className="seller-shop-nav" aria-label="店铺分类">
          {["店铺首页", "热销商品", "新品上架", "正品保障", "物流售后", "联系店家"].map((item, index) => (
            <button className={index === 0 ? "active" : ""} type="button" key={item}>{item}</button>
          ))}
        </nav>
        <div className="seller-shop-info">
          <span><Store size={16} /> {merchant?.store_slug || "yourbrand-store"}</span>
          <span><Phone size={16} /> {merchant?.phone || "+856 20 5555 1234"}</span>
          <span><Home size={16} /> {merchant?.address || "Vientiane Center, Vientiane, Laos"}</span>
        </div>
        <section className="seller-showcase">
          <div>
            <small>Featured storefront</small>
            <h3>{storeProducts[0]?.title || "精选商品"}</h3>
            <p>{storeProducts[0]?.subtitle || "清晰报价、可追踪物流、平台评价闭环。"}</p>
            {storeProducts[0] && <button className="primary-button" onClick={() => onProductAction(storeProducts[0].slug, "view")}>进入商品主页</button>}
          </div>
          <div className="seller-showcase-images">
            {storeProducts.slice(0, 4).map((item, index) => (
              <button type="button" onClick={() => onProductAction(item.slug, "view")} key={item.slug}>
                <ImageCrop variant={index} label={item.title} />
              </button>
            ))}
          </div>
        </section>
        <div className="seller-home-grid">
          {storeProducts.map((item, index) => (
            <article className="seller-product-card" key={item.slug}>
              <button className="seller-product-image" onClick={() => onProductAction(item.slug, "view")} aria-label={`进入 ${item.title} 主页`}>
                <ImageCrop variant={index} label={item.title} />
              </button>
              <div>
                <button className="seller-product-title" onClick={() => onProductAction(item.slug, "view")}>
                  {item.title}
                </button>
                <p>{item.subtitle}</p>
                <strong>{formatMoney(item.price, item.currency)}</strong>
                <del>{formatMoney(item.compare_at_price, item.currency)}</del>
              </div>
              <div className="seller-product-actions">
                <button onClick={() => onProductAction(item.slug, "save")}>
                  <Heart size={15} />
                  保存
                </button>
                <button onClick={() => onProductAction(item.slug, "cart")}>
                  <ShoppingCart size={15} />
                  加购
                </button>
                <button className="primary-button" onClick={() => onProductAction(item.slug, "buy")}>立即购买</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("无法读取视频时长"));
    };
    video.src = url;
  });
}

type ProductLaunchTemplate = {
  id: string;
  label: string;
  categoryId: string;
  subtitle: string;
  description: string;
  specs: string;
  sellingPoints: string;
  mediaChecklist: string[];
  contentChecklist: string[];
};

const productCategoryOptions = [
  { value: "cat_flagship", label: "旗舰手机" },
  { value: "cat_flagship_ios", label: "iOS 旗舰" },
  { value: "cat_android", label: "安卓旗舰" },
  { value: "cat_audio", label: "智能音频" },
  { value: "cat_wearable", label: "智能穿戴" },
  { value: "cat_magsafe", label: "MagSafe / 充电配件" },
  { value: "cat_protection", label: "保护配件" }
];

const productLaunchTemplates: ProductLaunchTemplate[] = [
  {
    id: "flagship-phone",
    label: "旗舰手机模板",
    categoryId: "cat_flagship",
    subtitle: "旗舰性能、高清影像、正品保障。",
    description: "适合手机类商品,重点展示芯片、屏幕、影像、电池、系统版本、网络制式、保修和配件清单。",
    specs: "芯片: A18 Pro / Snapdragon 旗舰平台\n屏幕: 6.7 英寸 OLED 高刷\n影像: 主摄 4800 万像素\n电池: 全天续航\n网络: 5G / 双卡\n系统: 原厂系统\n保修: 店家 7 天退换 + 官方保障\n包装: 主机、数据线、说明书",
    sellingPoints: "正品现货\n旗舰性能\n高清主摄\n大电池续航\n本地售后\n支持扫码付款",
    mediaChecklist: ["白底主图", "背面与镜头细节", "屏幕正面图", "包装与配件", "短视频开箱"],
    contentChecklist: ["规格参数表", "颜色与容量", "网络制式", "保修说明", "同级对比"]
  },
  {
    id: "audio-wearable",
    label: "耳机/穿戴模板",
    categoryId: "cat_audio",
    subtitle: "轻便连接、清晰音质、日常通勤适用。",
    description: "适合耳机、手表和穿戴设备,重点展示连接方式、续航、防水、降噪、健康功能和兼容系统。",
    specs: "连接: Bluetooth 5.3\n续航: 单次 6 小时 / 搭配充电盒 30 小时\n功能: 降噪 / 通透 / 麦克风通话\n防护: IPX4 生活防水\n兼容: iOS / Android\n包装: 主机、充电盒、线材、耳塞\n保修: 7 天退换",
    sellingPoints: "快速连接\n长续航\n通话清晰\n轻便佩戴\n通勤适用\n售后可追踪",
    mediaChecklist: ["佩戴效果图", "充电盒细节", "按键或触控区", "续航说明图", "使用场景短视频"],
    contentChecklist: ["续航说明", "兼容设备", "降噪/通话能力", "防水等级", "配件清单"]
  },
  {
    id: "magsafe-accessory",
    label: "充电/配件模板",
    categoryId: "cat_magsafe",
    subtitle: "磁吸稳固、桌面与车载都适用。",
    description: "适合充电器、磁吸支架、车载配件,重点展示功率、接口、兼容机型、安装方式、安全保护和使用场景。",
    specs: "功率: 15W / 20W / 30W 可选\n接口: USB-C\n材质: ABS + 铝合金\n适用: MagSafe 机型 / Qi 无线充设备\n安装: 桌面 / 车载 / 支架\n安全: 过流、过温、短路保护\n包装: 主机、线材、说明书",
    sellingPoints: "磁吸稳固\n快速充电\n多场景安装\n安全保护\n小体积便携\n适合加购",
    mediaChecklist: ["主图展示吸附状态", "接口特写", "车载/桌面场景", "兼容机型图", "安全认证说明"],
    contentChecklist: ["功率与接口", "兼容型号", "安装步骤", "安全保护", "使用注意事项"]
  },
  {
    id: "home-hardware",
    label: "家居建材模板",
    categoryId: "cat_protection",
    subtitle: "耐用材质、安装方便、适合家庭与店铺场景。",
    description: "适合家居建材、门窗五金、照明和户外用品,重点展示材质、尺寸、承重、安装方式、防水防锈和售后服务。",
    specs: "材质: 加厚金属 / ABS / 防锈涂层\n尺寸: 请按实际商品填写\n承重: 请按实际商品填写\n安装: 免打孔 / 螺丝固定 / 专业安装\n适用场景: 家庭、店铺、酒店、户外\n防护: 防水、防锈、耐磨\n售后: 7 天退换 / 安装咨询",
    sellingPoints: "加厚耐用\n安装方便\n场景清晰\n防水防锈\n价格透明\n支持批量采购",
    mediaChecklist: ["尺寸标注图", "材质细节", "安装步骤图", "使用前后对比", "真实场景短视频"],
    contentChecklist: ["尺寸参数", "安装方式", "适用场景", "材质说明", "售后与物流"]
  }
];

type CmsSkuForm = {
  id?: string;
  color: string;
  colorHex: string;
  storage: string;
  sku: string;
  priceDelta: string;
  stockQuantity: string;
};

type CmsProductForm = {
  title: string;
  subtitle: string;
  slug: string;
  price: string;
  compareAt: string;
  categoryId: string;
  stockStatus: string;
  status: string;
  seoTitle: string;
  seoDescription: string;
  featureTitle: string;
  featureBody: string;
  specs: string;
  skus: CmsSkuForm[];
};

function productToCmsForm(product: Product): CmsProductForm {
  const feature = product.blocks.find((block) => block.type === "feature");
  const specs = product.blocks.find((block) => block.type === "specs");
  return {
    title: product.title,
    subtitle: product.subtitle,
    slug: product.slug,
    price: String((product.price / 100).toFixed(0)),
    compareAt: String((product.compare_at_price / 100).toFixed(0)),
    categoryId: product.category_id || "cat_flagship",
    stockStatus: product.stock_status || "in_stock",
    status: product.status || "published",
    seoTitle: product.seo_title || `${product.title} - YourBrand`,
    seoDescription: product.seo_description || product.subtitle,
    featureTitle: feature?.title || "核心卖点",
    featureBody: feature?.body || product.subtitle,
    specs: specs?.body || "",
    skus: product.skus.length
      ? product.skus.map((sku) => ({
          id: sku.id,
          color: sku.color,
          colorHex: sku.color_hex,
          storage: sku.storage,
          sku: sku.sku,
          priceDelta: String((sku.price_delta / 100).toFixed(0)),
          stockQuantity: String(sku.stock_quantity)
        }))
      : [
          {
            color: "标准色",
            colorHex: "#d8d1c8",
            storage: "标准版",
            sku: `${product.slug.toUpperCase().slice(0, 8)}-STD`,
            priceDelta: "0",
            stockQuantity: "20"
          }
        ]
  };
}

function AdminDrawer({
  scope = "seller",
  dashboard,
  product,
  selectedSku,
  totalPrice,
  onClose,
  onProductPublished,
  onMerchantUpdated
}: {
  scope?: "seller" | "platform";
  dashboard: Dashboard | null;
  product: Product;
  selectedSku: ProductSku;
  totalPrice: number;
  onClose: () => void;
  onProductPublished: (product: Product) => void;
  onMerchantUpdated: (merchant: Merchant) => void;
}) {
  const isPlatformScope = scope === "platform";
  const drawerTitle = isPlatformScope ? "平台运营后台" : "店家运营中心";
  const drawerSubtitle = isPlatformScope
    ? "入驻审核、权限、安全、数据和平台协作统一管理"
    : "商品、上新、订单、收款、物流和店铺资料统一管理";
  const [platform, setPlatform] = useState<PlatformOverview | null>(null);
  const [newProduct, setNewProduct] = useState({
    title: "",
    subtitle: "",
    slug: "",
    price: "",
    compareAt: "",
    categoryId: "cat_flagship",
    templateId: "flagship-phone",
    description: productLaunchTemplates[0].description,
    specs: productLaunchTemplates[0].specs,
    sellingPoints: productLaunchTemplates[0].sellingPoints
  });
  const [batchSeed, setBatchSeed] = useState("静音门窗滑轮|升级款套装|138|168\n酒店浴室电液器|免打孔双头|89|129\n户外折叠天幕|阳台遮阳|2399|2499");
  const [batchDrafts, setBatchDrafts] = useState<Array<{
    id: string;
    selected: boolean;
    title: string;
    subtitle: string;
    price: string;
    compareAt: string;
    categoryId: string;
    description: string;
  }>>([]);
  const [merchantForm, setMerchantForm] = useState({
    name: product.merchant?.name || "",
    phone: product.merchant?.phone || "",
    address: product.merchant?.address || "",
    facebook_url: product.merchant?.facebook_url || "",
    messenger_url: product.merchant?.messenger_url || "",
    whatsapp_number: product.merchant?.whatsapp_number || "",
    intro: product.merchant?.intro || "",
    payment_qr_url: product.merchant?.payment_qr_url || "",
    bank_account: product.merchant?.bank_account || "",
    category: product.merchant?.category || "Digital Store",
    level: product.merchant?.level || "Verified Seller",
    business_type: product.merchant?.business_type || "Corporate",
    verification_status: product.merchant?.verification_status || "verified",
    store_slug: product.merchant?.store_slug || "yourbrand-store"
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [qrPreview, setQrPreview] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminSuccess, setAdminSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [adminOrders, setAdminOrders] = useState<Order[]>(dashboard?.orders ?? []);
  const [cmsProducts, setCmsProducts] = useState<Product[]>([product]);
  const [cmsSearch, setCmsSearch] = useState("");
  const [cmsStatusFilter, setCmsStatusFilter] = useState("all");
  const [cmsEditorOpen, setCmsEditorOpen] = useState(true);
  const [cmsEditingId, setCmsEditingId] = useState(product.id);
  const [cmsForm, setCmsForm] = useState<CmsProductForm>(() => productToCmsForm(product));
  const currentVideo = [...product.media].reverse().find((media) => media.type === "video" && isPlayableVideo(media.url));
  const cmsEditingProduct = cmsProducts.find((item) => item.id === cmsEditingId) ?? product;
  const cmsVisibleProducts = cmsProducts.filter((item) => {
    const haystack = `${item.title} ${item.subtitle} ${item.slug}`.toLowerCase();
    const matchesQuery = !cmsSearch.trim() || haystack.includes(cmsSearch.trim().toLowerCase());
    const matchesStatus = cmsStatusFilter === "all" || (item.status || "published") === cmsStatusFilter;
    return matchesQuery && matchesStatus;
  });
  const cmsStats = {
    total: cmsProducts.length,
    published: cmsProducts.filter((item) => (item.status || "published") === "published").length,
    draft: cmsProducts.filter((item) => item.status === "draft").length,
    lowStock: cmsProducts.filter((item) => item.skus.reduce((sum, sku) => sum + sku.stock_quantity, 0) <= 10).length
  };

  useEffect(() => {
    setAdminOrders(dashboard?.orders ?? []);
  }, [dashboard?.orders]);

  useEffect(() => {
    fetchPlatformOverview().then(setPlatform).catch(() => undefined);
  }, []);

  useEffect(() => {
    fetchAdminProducts()
      .then((items) => {
        setCmsProducts(items.length ? items : [product]);
        const current = items.find((item) => item.id === cmsEditingId) ?? items.find((item) => item.id === product.id) ?? product;
        setCmsEditingId(current.id);
        setCmsForm(productToCmsForm(current));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      if (qrPreview) URL.revokeObjectURL(qrPreview);
    };
  }, [imagePreviews, qrPreview]);

  useEffect(() => {
    setMerchantForm({
      name: product.merchant?.name || "",
      phone: product.merchant?.phone || "",
      address: product.merchant?.address || "",
      facebook_url: product.merchant?.facebook_url || "",
      messenger_url: product.merchant?.messenger_url || "",
      whatsapp_number: product.merchant?.whatsapp_number || "",
      intro: product.merchant?.intro || "",
      payment_qr_url: product.merchant?.payment_qr_url || "",
      bank_account: product.merchant?.bank_account || "",
      category: product.merchant?.category || "Digital Store",
      level: product.merchant?.level || "Verified Seller",
      business_type: product.merchant?.business_type || "Corporate",
      verification_status: product.merchant?.verification_status || "verified",
      store_slug: product.merchant?.store_slug || "yourbrand-store"
    });
  }, [product.merchant, product.merchant_id]);

  useEffect(() => {
    setCmsProducts((items) => {
      const exists = items.some((item) => item.id === product.id);
      return exists ? items.map((item) => (item.id === product.id ? product : item)) : [product, ...items];
    });
  }, [product]);

  const chooseVideo = async (file: File | null) => {
    setAdminError("");
    setVideoFile(null);
    setVideoDuration(0);
    if (!file) return;
    try {
      const duration = await readVideoDuration(file);
      if (duration > 180) {
        setAdminError("视频时长不能超过 3 分钟,请重新选择。");
        return;
      }
      setVideoFile(file);
      setVideoDuration(duration);
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "无法读取视频时长");
    }
  };

  const chooseImages = (files: FileList | null) => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    const nextFiles = Array.from(files ?? []).filter((file) => file.type.startsWith("image/")).slice(0, 8);
    setImageFiles(nextFiles);
    setImagePreviews(nextFiles.map((file) => URL.createObjectURL(file)));
  };

  const chooseQr = (file: File | null) => {
    if (qrPreview) URL.revokeObjectURL(qrPreview);
    if (!file || !file.type.startsWith("image/")) {
      setQrFile(null);
      setQrPreview("");
      return;
    }
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  };

  const openCmsEditor = (item: Product) => {
    setCmsEditingId(item.id);
    setCmsForm(productToCmsForm(item));
    setCmsEditorOpen(true);
  };

  const updateCmsSku = (index: number, patch: Partial<CmsSkuForm>) => {
    setCmsForm((current) => ({
      ...current,
      skus: current.skus.map((sku, skuIndex) => (skuIndex === index ? { ...sku, ...patch } : sku))
    }));
  };

  const addCmsSku = () => {
    setCmsForm((current) => ({
      ...current,
      skus: [
        ...current.skus,
        {
          color: "新颜色",
          colorHex: "#d8d1c8",
          storage: "新规格",
          sku: `${current.slug.toUpperCase().slice(0, 8) || "SKU"}-${current.skus.length + 1}`,
          priceDelta: "0",
          stockQuantity: "10"
        }
      ]
    }));
  };

  const saveCmsProduct = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await updateAdminProduct(cmsEditingId, {
        title: cmsForm.title,
        subtitle: cmsForm.subtitle,
        slug: cmsForm.slug,
        price: Math.round(Number(cmsForm.price) * 100),
        compareAt: Math.round(Number(cmsForm.compareAt || cmsForm.price) * 100),
        categoryId: cmsForm.categoryId,
        stockStatus: cmsForm.stockStatus,
        status: cmsForm.status,
        seoTitle: cmsForm.seoTitle,
        seoDescription: cmsForm.seoDescription,
        featureTitle: cmsForm.featureTitle,
        featureBody: cmsForm.featureBody,
        specs: cmsForm.specs,
        skus: cmsForm.skus.map((sku) => ({
          id: sku.id,
          color: sku.color,
          colorHex: sku.colorHex,
          storage: sku.storage,
          sku: sku.sku,
          priceDelta: Math.round(Number(sku.priceDelta || 0) * 100),
          stockQuantity: Math.max(0, Math.round(Number(sku.stockQuantity || 0)))
        }))
      });
      setCmsProducts((items) => items.map((item) => (item.id === updated.id ? updated : item)));
      setCmsForm(productToCmsForm(updated));
      onProductPublished(updated);
      setAdminSuccess("产品 CMS 已保存:基础信息、价格、SKU 库存、状态和前台内容已同步。");
      fetchAdminProducts().then(setCmsProducts).catch(() => undefined);
      fetchPlatformOverview().then(setPlatform).catch(() => undefined);
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "产品 CMS 保存失败");
    } finally {
      setSubmitting(false);
    }
  };

  const quickUpdateCmsStatus = async (item: Product, status: string) => {
    const form = productToCmsForm(item);
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await updateAdminProduct(item.id, {
        title: form.title,
        subtitle: form.subtitle,
        slug: form.slug,
        price: Math.round(Number(form.price) * 100),
        compareAt: Math.round(Number(form.compareAt || form.price) * 100),
        categoryId: form.categoryId,
        stockStatus: form.stockStatus,
        status,
        seoTitle: form.seoTitle,
        seoDescription: form.seoDescription,
        featureTitle: form.featureTitle,
        featureBody: form.featureBody,
        specs: form.specs,
        skus: form.skus.map((sku) => ({
          id: sku.id,
          color: sku.color,
          colorHex: sku.colorHex,
          storage: sku.storage,
          sku: sku.sku,
          priceDelta: Math.round(Number(sku.priceDelta || 0) * 100),
          stockQuantity: Math.max(0, Math.round(Number(sku.stockQuantity || 0)))
        }))
      });
      setCmsProducts((items) => items.map((candidate) => (candidate.id === updated.id ? updated : candidate)));
      if (updated.id === cmsEditingId) setCmsForm(productToCmsForm(updated));
      onProductPublished(updated);
      setAdminSuccess(status === "published" ? "商品已上架,前台可见。" : "商品状态已更新,目录和前台展示已同步。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "状态更新失败");
    } finally {
      setSubmitting(false);
    }
  };

  const saveMerchant = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await updateMerchantProfile(product.merchant_id, merchantForm);
      const merchant = qrFile ? await uploadMerchantQr(updated.id, qrFile) : updated;
      onMerchantUpdated(merchant);
      setMerchantForm({
        name: merchant.name || "",
        phone: merchant.phone || "",
        address: merchant.address || "",
        facebook_url: merchant.facebook_url || "",
        messenger_url: merchant.messenger_url || "",
        whatsapp_number: merchant.whatsapp_number || "",
        intro: merchant.intro || "",
        payment_qr_url: merchant.payment_qr_url || "",
        bank_account: merchant.bank_account || "",
        category: merchant.category || "Digital Store",
        level: merchant.level || "Verified Seller",
        business_type: merchant.business_type || "Corporate",
        verification_status: merchant.verification_status || "verified",
        store_slug: merchant.store_slug || "yourbrand-store"
      });
      setQrFile(null);
      if (qrPreview) URL.revokeObjectURL(qrPreview);
      setQrPreview("");
      setAdminSuccess("店家资料和收款二维码已保存,主页购买弹窗已同步。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "店家资料保存失败");
    } finally {
      setSubmitting(false);
    }
  };

  const applyLaunchTemplate = (templateId: string) => {
    const template = productLaunchTemplates.find((item) => item.id === templateId) ?? productLaunchTemplates[0];
    setNewProduct((current) => ({
      ...current,
      templateId: template.id,
      categoryId: template.categoryId,
      subtitle: current.subtitle || template.subtitle,
      description: template.description,
      specs: template.specs,
      sellingPoints: template.sellingPoints
    }));
    setAdminError("");
    setAdminSuccess(`已套用${template.label},参数、卖点和详情内容仍可编辑。`);
  };

  const publishProduct = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const created = await createAdminProduct({
        title: newProduct.title,
        subtitle: newProduct.subtitle,
        slug: newProduct.slug || undefined,
        price: Math.round(Number(newProduct.price) * 100),
        compareAt: Math.round(Number(newProduct.compareAt || newProduct.price) * 100),
        categoryId: newProduct.categoryId,
        description: [
          newProduct.description,
          newProduct.sellingPoints ? `核心卖点:\n${newProduct.sellingPoints}` : "",
          newProduct.specs ? `参数规格:\n${newProduct.specs}` : ""
        ].filter(Boolean).join("\n\n"),
        specs: newProduct.specs
      });
      const withImages = imageFiles.length ? await uploadProductImages(created.id, imageFiles) : created;
      const published = videoFile ? await uploadProductVideo(withImages.id, videoFile, videoDuration) : withImages;
      onProductPublished(published);
      setAdminSuccess("店家上新已发布,主页已同步切换。");
      setNewProduct({
        title: "",
        subtitle: "",
        slug: "",
        price: "",
        compareAt: "",
        categoryId: productLaunchTemplates[0].categoryId,
        templateId: productLaunchTemplates[0].id,
        description: productLaunchTemplates[0].description,
        specs: productLaunchTemplates[0].specs,
        sellingPoints: productLaunchTemplates[0].sellingPoints
      });
      setVideoFile(null);
      setVideoDuration(0);
      setImageFiles([]);
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      setImagePreviews([]);
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "发布失败");
    } finally {
      setSubmitting(false);
    }
  };

  const generateBatchDrafts = () => {
    const lines = batchSeed
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) {
      setAdminError("请先粘贴商品清单或输入上新需求。");
      return;
    }
    const drafts = lines.map((line, index) => {
      const parts = line.split(/[||,,\t]/).map((part) => part.trim()).filter(Boolean);
      const title = parts[0] || `AI批量商品 ${index + 1}`;
      const subtitle = parts[1] || "AI 自动生成卖点,可发布前编辑";
      const price = parts[2]?.replace(/[^\d.]/g, "") || "99";
      const compareAt = parts[3]?.replace(/[^\d.]/g, "") || String(Math.ceil(Number(price || 99) * 1.18));
      return {
        id: `draft-${Date.now()}-${index}`,
        selected: true,
        title,
        subtitle,
        price,
        compareAt,
        categoryId: index % 3 === 0 ? "cat_magsafe" : index % 3 === 1 ? "cat_flagship" : "cat_audio",
        description: `${title},${subtitle}。AI 已生成基础介绍,店家可继续补充材质、规格、售后和物流说明。`
      };
    });
    setBatchDrafts(drafts);
    setAdminError("");
    setAdminSuccess(`AI 已生成 ${drafts.length} 条可编辑上新草稿。`);
  };

  const updateBatchDraft = (id: string, patch: Partial<(typeof batchDrafts)[number]>) => {
    setBatchDrafts((drafts) => drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));
  };

  const publishBatchProducts = async () => {
    const selectedDrafts = batchDrafts.filter((draft) => draft.selected);
    if (!selectedDrafts.length) {
      setAdminError("请至少选择 1 个批量上新草稿。");
      return;
    }
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      let lastPublished: Product | null = null;
      for (const draft of selectedDrafts) {
        lastPublished = await createAdminProduct({
          title: draft.title,
          subtitle: draft.subtitle,
          price: Math.round(Number(draft.price) * 100),
          compareAt: Math.round(Number(draft.compareAt || draft.price) * 100),
          categoryId: draft.categoryId,
          description: draft.description
        });
      }
      if (lastPublished) onProductPublished(lastPublished);
      setBatchDrafts((drafts) => drafts.filter((draft) => !draft.selected));
      setAdminSuccess(`批量上新完成:已发布 ${selectedDrafts.length} 个商品,商品可继续单独编辑图片和视频。`);
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "批量发布失败");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadCurrentVideo = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!videoFile) {
      setAdminError("请先选择 3 分钟内的视频。");
      return;
    }
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await uploadProductVideo(product.id, videoFile, videoDuration);
      onProductPublished(updated);
      setAdminSuccess("当前产品视频已上传,前台播放器已更新。");
      setVideoFile(null);
      setVideoDuration(0);
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "上传失败");
    } finally {
      setSubmitting(false);
    }
  };

  const replaceOrder = (updated: Order) => {
    setAdminOrders((orders) => orders.map((order) => (order.id === updated.id ? updated : order)));
  };

  const confirmOrderPaymentAction = async (orderId: string) => {
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await confirmOrderPayment(orderId);
      replaceOrder(updated);
      setAdminSuccess("卖家已确认收款,订单已进入物流流程。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "确认收款失败");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadOrderLogisticsAction = async (orderId: string, file: File | null) => {
    if (!file) return;
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await uploadOrderLogisticsQr(orderId, file);
      replaceOrder(updated);
      setAdminSuccess("物流二维码已上传,顾客查询订单时可查看。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "上传物流二维码失败");
    } finally {
      setSubmitting(false);
    }
  };

  const completeOrderLogisticsAction = async (orderId: string) => {
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const updated = await completeOrderLogistics(orderId);
      replaceOrder(updated);
      setAdminSuccess("物流已完成,顾客现在可以评价产品与服务。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "完成物流失败");
    } finally {
      setSubmitting(false);
    }
  };

  const reviewApplicationAction = async (applicationId: string, decision: "approved" | "rejected") => {
    setSubmitting(true);
    setAdminError("");
    setAdminSuccess("");
    try {
      const overview = await reviewSellerApplication(
        applicationId,
        decision,
        decision === "approved" ? "平台审核通过,自动开通店家资料、店铺主页和店家后台。" : "平台审核驳回,需补充身份、收款或经营资料。"
      );
      setPlatform(overview);
      setAdminSuccess(decision === "approved" ? "店家申请已批准,店铺生态已开通。" : "店家申请已驳回,等待店家补充资料。");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "平台审核操作失败");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLaunchTemplate = productLaunchTemplates.find((template) => template.id === newProduct.templateId) ?? productLaunchTemplates[0];
  const materialTitle = newProduct.title || product.title;
  const materialSubtitle = newProduct.subtitle || product.subtitle;
  const materialPrice = newProduct.price ? formatMoney(Math.round(Number(newProduct.price) * 100), product.currency) : formatMoney(product.price, product.currency);
  const materialPrompt = [
    `商品主图生成:${materialTitle}`,
    `卖点:${materialSubtitle || "突出产品核心功能、真实材质和使用场景"}`,
    `上新模板:${selectedLaunchTemplate.label}`,
    `核心卖点:${newProduct.sellingPoints || selectedLaunchTemplate.sellingPoints}`,
    `店家:${merchantForm.name || product.merchant?.name || "本地认证店家"},等级:${merchantForm.level}`,
    "构图:电商白底主图 1 张,细节特写 3 张,生活场景 2 张,移动端详情页长图 1 张。",
    "风格:真实产品摄影,高对比但不过曝,保留价格标签、退换保障、正品认证信息区域。",
    "限制:不使用虚假品牌授权,不夸大功能,文字留白方便后期排版。"
  ].join("\n");
  const pageBrief = [
    `网页信息:${materialTitle}`,
    `标题:${materialTitle}`,
    `副标题:${materialSubtitle}`,
    `价格:${materialPrice}`,
    `模板:${selectedLaunchTemplate.label}`,
    `参数规格:${newProduct.specs || selectedLaunchTemplate.specs}`,
    `店家主页:/${merchantForm.store_slug || product.merchant?.store_slug || "store"}`,
    `店家介绍:${merchantForm.intro || product.merchant?.intro || "正品保障、透明收款、售后可追踪。"}`,
    "详情页模块:主图轮播、短视频、规格参数、买家评价、支付二维码、物流追踪、售后评价闭环。"
  ].join("\n");
  const copyMaterial = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setAdminSuccess("素材内容已复制,可以直接给图片生成工具或网页编辑使用。");
    } catch {
      setAdminError("浏览器暂不允许自动复制,请手动选择文本复制。");
    }
  };
  const awaitingPaymentReview = adminOrders.filter((order) => order.payment_ai_status === "passed" && order.seller_payment_status !== "confirmed");
  const readyToShip = adminOrders.filter((order) => order.payment_status === "confirmed" && !order.logistics_qr_url && order.logistics_status !== "completed");
  const inTransit = adminOrders.filter((order) => order.logistics_qr_url && order.logistics_status !== "completed");
  const deliveredOrders = adminOrders.filter((order) => order.logistics_status === "completed" || order.status === "delivered");

  return (
    <div className="drawer-backdrop admin-workbench-backdrop" role="dialog" aria-modal="true" aria-label={drawerTitle}>
      <aside className="admin-drawer">
        <button className="modal-close" onClick={onClose} aria-label={`关闭${drawerTitle}`}>
          <X size={18} />
        </button>
        <div className="admin-header">
          <LockBadge />
          <div>
            <h2>{drawerTitle}</h2>
            <p>{drawerSubtitle}</p>
          </div>
        </div>
        <div className="admin-metrics">
          <Metric label="访问量" value={dashboard?.metrics.visitors.toLocaleString() ?? "12,345"} delta="+12.5%" />
          <Metric label="订单数" value={String(dashboard?.metrics.orders ?? 0)} delta="+8.6%" />
          <Metric label="销售额" value={formatMoney(dashboard?.metrics.revenue ?? 0)} delta="+15.3%" />
        </div>
        {!isPlatformScope && (
        <section className="admin-section product-cms-section">
          <div className="admin-section-head">
            <div>
              <h3>产品 CMS</h3>
              <p className="seller-standard-note">统一管理商品目录、编辑基础信息、SKU/库存、图片视频、SEO、上下架状态,并同步到前台商品页、搜索列表和店铺主页。</p>
            </div>
            <span>{cmsStats.total} 个商品 · {cmsStats.published} 已上架</span>
          </div>
          <div className="cms-metrics">
            <Metric label="商品总数" value={String(cmsStats.total)} delta="目录池" />
            <Metric label="已上架" value={String(cmsStats.published)} delta="前台可见" />
            <Metric label="草稿" value={String(cmsStats.draft)} delta="待完善" />
            <Metric label="低库存" value={String(cmsStats.lowStock)} delta="需补货" />
          </div>
          <div className="cms-toolbar">
            <label>
              搜索商品
              <input value={cmsSearch} onChange={(event) => setCmsSearch(event.target.value)} placeholder="输入标题、卖点或 URL 标识" />
            </label>
            <label>
              状态筛选
              <select value={cmsStatusFilter} onChange={(event) => setCmsStatusFilter(event.target.value)}>
                <option value="all">全部状态</option>
                <option value="published">已上架</option>
                <option value="draft">草稿</option>
                <option value="paused">暂停销售</option>
                <option value="archived">已归档</option>
              </select>
            </label>
            <button type="button" className="secondary-button" onClick={() => fetchAdminProducts().then(setCmsProducts).catch(() => undefined)}>
              刷新目录
            </button>
          </div>
          <div className="cms-workspace">
            <div className="cms-product-list">
              {cmsVisibleProducts.map((item, index) => {
                const stock = item.skus.reduce((sum, sku) => sum + sku.stock_quantity, 0);
                return (
                  <article className={item.id === cmsEditingId ? "cms-product-row active" : "cms-product-row"} key={item.id}>
                    <button type="button" className="cms-product-select" onClick={() => openCmsEditor(item)}>
                      <ImageCrop src={item.media[0]?.url} variant={index} label={item.title} />
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.subtitle}</span>
                        <small>{formatMoney(item.price, item.currency)} · 库存 {stock} · {item.skus.length} SKU</small>
                      </div>
                    </button>
                    <div className="cms-row-actions">
                      <span className={`status-pill ${item.status || "published"}`}>{item.status || "published"}</span>
                      <button type="button" onClick={() => openCmsEditor(item)}>编辑</button>
                      <button type="button" onClick={() => quickUpdateCmsStatus(item, (item.status || "published") === "published" ? "paused" : "published")}>
                        {(item.status || "published") === "published" ? "暂停" : "上架"}
                      </button>
                    </div>
                  </article>
                );
              })}
              {!cmsVisibleProducts.length && <div className="empty-account-state">没有匹配的商品</div>}
            </div>
            {cmsEditorOpen && (
              <form className="cms-editor admin-product-form" onSubmit={saveCmsProduct}>
                <div className="admin-wide cms-editor-head">
                  <div>
                    <h4>{cmsEditingProduct.title}</h4>
                    <p>{cmsEditingProduct.slug} · 最后更新 {cmsEditingProduct.updated_at || "当前会话"}</p>
                  </div>
                  <div>
                    <button type="button" className="secondary-button" onClick={() => setCmsEditorOpen(false)}>折叠</button>
                    <button className="primary-button" disabled={submitting}>{submitting ? "保存中..." : "保存 CMS"}</button>
                  </div>
                </div>
                <details className="admin-accordion admin-wide" open>
                  <summary>
                    <span><Store size={18} /><b>基础信息</b><small>标题、URL、分类、价格和上下架状态</small></span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="admin-product-form accordion-grid">
                    <label>
                      产品名称
                      <input required value={cmsForm.title} onChange={(event) => setCmsForm({ ...cmsForm, title: event.target.value })} />
                    </label>
                    <label>
                      副标题 / 核心卖点
                      <input required value={cmsForm.subtitle} onChange={(event) => setCmsForm({ ...cmsForm, subtitle: event.target.value })} />
                    </label>
                    <label>
                      URL 标识
                      <input required value={cmsForm.slug} onChange={(event) => setCmsForm({ ...cmsForm, slug: event.target.value })} />
                    </label>
                    <label>
                      分类
                      <select value={cmsForm.categoryId} onChange={(event) => setCmsForm({ ...cmsForm, categoryId: event.target.value })}>
                        {productCategoryOptions.map((option) => (
                          <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      售价
                      <input type="number" min="0" step="0.01" value={cmsForm.price} onChange={(event) => setCmsForm({ ...cmsForm, price: event.target.value })} />
                    </label>
                    <label>
                      原价
                      <input type="number" min="0" step="0.01" value={cmsForm.compareAt} onChange={(event) => setCmsForm({ ...cmsForm, compareAt: event.target.value })} />
                    </label>
                    <label>
                      库存状态
                      <select value={cmsForm.stockStatus} onChange={(event) => setCmsForm({ ...cmsForm, stockStatus: event.target.value })}>
                        <option value="in_stock">现货</option>
                        <option value="low_stock">低库存</option>
                        <option value="out_of_stock">缺货</option>
                        <option value="preorder">预售</option>
                      </select>
                    </label>
                    <label>
                      发布状态
                      <select value={cmsForm.status} onChange={(event) => setCmsForm({ ...cmsForm, status: event.target.value })}>
                        <option value="published">已上架</option>
                        <option value="draft">草稿</option>
                        <option value="paused">暂停销售</option>
                        <option value="archived">归档</option>
                      </select>
                    </label>
                  </div>
                </details>
                <details className="admin-accordion admin-wide" open>
                  <summary>
                    <span><Boxes size={18} /><b>SKU 与库存</b><small>颜色、规格、SKU 编码、差价和库存数量</small></span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="cms-sku-list">
                    {cmsForm.skus.map((sku, index) => (
                      <div className="cms-sku-row" key={`${sku.id || "new"}-${index}`}>
                        <label>
                          颜色
                          <input value={sku.color} onChange={(event) => updateCmsSku(index, { color: event.target.value })} />
                        </label>
                        <label>
                          色值
                          <input type="color" value={sku.colorHex} onChange={(event) => updateCmsSku(index, { colorHex: event.target.value })} />
                        </label>
                        <label>
                          规格
                          <input value={sku.storage} onChange={(event) => updateCmsSku(index, { storage: event.target.value })} />
                        </label>
                        <label>
                          SKU
                          <input value={sku.sku} onChange={(event) => updateCmsSku(index, { sku: event.target.value })} />
                        </label>
                        <label>
                          差价
                          <input type="number" step="0.01" value={sku.priceDelta} onChange={(event) => updateCmsSku(index, { priceDelta: event.target.value })} />
                        </label>
                        <label>
                          库存
                          <input type="number" min="0" value={sku.stockQuantity} onChange={(event) => updateCmsSku(index, { stockQuantity: event.target.value })} />
                        </label>
                      </div>
                    ))}
                    <button type="button" className="secondary-button" onClick={addCmsSku}>添加 SKU</button>
                  </div>
                </details>
                <details className="admin-accordion admin-wide">
                  <summary>
                    <span><ClipboardCheck size={18} /><b>前台内容与 SEO</b><small>详情页卖点、参数规格、搜索标题和描述</small></span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="admin-product-form accordion-grid">
                    <label>
                      卖点标题
                      <input value={cmsForm.featureTitle} onChange={(event) => setCmsForm({ ...cmsForm, featureTitle: event.target.value })} />
                    </label>
                    <label>
                      SEO 标题
                      <input value={cmsForm.seoTitle} onChange={(event) => setCmsForm({ ...cmsForm, seoTitle: event.target.value })} />
                    </label>
                    <label className="admin-wide">
                      卖点内容
                      <textarea value={cmsForm.featureBody} onChange={(event) => setCmsForm({ ...cmsForm, featureBody: event.target.value })} />
                    </label>
                    <label className="admin-wide">
                      参数规格
                      <textarea value={cmsForm.specs} onChange={(event) => setCmsForm({ ...cmsForm, specs: event.target.value })} />
                    </label>
                    <label className="admin-wide">
                      SEO 描述
                      <textarea value={cmsForm.seoDescription} onChange={(event) => setCmsForm({ ...cmsForm, seoDescription: event.target.value })} />
                    </label>
                  </div>
                </details>
                <details className="admin-accordion admin-wide">
                  <summary>
                    <span><Camera size={18} /><b>媒体资产</b><small>主图缩略图、产品视频和前台预览</small></span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="cms-media-grid">
                    <div className="admin-gallery-preview">
                      <div className="thumb-stack">
                        {cmsEditingProduct.media.filter((media) => media.type !== "video").slice(0, 6).map((media, index) => (
                          <span className={index === 0 ? "thumb active admin-thumb-preview" : "thumb admin-thumb-preview"} key={media.id}>
                            <ImageCrop src={media.url} variant={index} label={media.label} />
                          </span>
                        ))}
                      </div>
                      <div className="product-canvas admin-product-canvas">
                        <SelectedMedia src={cmsEditingProduct.media[0]?.url || "/assets/lao-online-shopping.png"} variant={0} isVideo={false} />
                      </div>
                    </div>
                    <div className="cms-media-actions">
                      <label>
                        替换主图 / 缩略图
                        <input type="file" accept="image/*" multiple onChange={async (event) => {
                          const files = Array.from(event.target.files ?? []);
                          if (!files.length) return;
                          setSubmitting(true);
                          setAdminError("");
                          setAdminSuccess("");
                          try {
                            const updated = await uploadProductImages(cmsEditingId, files);
                            setCmsProducts((items) => items.map((candidate) => (candidate.id === updated.id ? updated : candidate)));
                            setCmsForm(productToCmsForm(updated));
                            onProductPublished(updated);
                            setAdminSuccess("商品图片已替换,前台主图和缩略图已同步。");
                          } catch (error) {
                            setAdminError(error instanceof Error ? error.message : "图片上传失败");
                          } finally {
                            setSubmitting(false);
                          }
                        }} />
                      </label>
                      <label>
                        上传 3 分钟内视频
                        <input type="file" accept="video/*" onChange={(event) => chooseVideo(event.target.files?.[0] ?? null)} />
                      </label>
                      <button type="button" className="secondary-button" disabled={submitting || !videoFile} onClick={async () => {
                        if (!videoFile) return;
                        setSubmitting(true);
                        setAdminError("");
                        setAdminSuccess("");
                        try {
                          const updated = await uploadProductVideo(cmsEditingId, videoFile, videoDuration);
                          setCmsProducts((items) => items.map((candidate) => (candidate.id === updated.id ? updated : candidate)));
                          setCmsForm(productToCmsForm(updated));
                          onProductPublished(updated);
                          setVideoFile(null);
                          setVideoDuration(0);
                          setAdminSuccess("商品视频已上传,详情页播放器已同步。");
                        } catch (error) {
                          setAdminError(error instanceof Error ? error.message : "视频上传失败");
                        } finally {
                          setSubmitting(false);
                        }
                      }}>
                        保存视频
                      </button>
                      <span>{videoFile ? `${videoFile.name} · ${formatDuration(videoDuration)}` : "未选择视频"}</span>
                    </div>
                  </div>
                </details>
                <div className="admin-form-status admin-wide cms-save-bar">
                  <span>保存后会写入数据库,并同步前台商品详情、列表、购物车和店铺目录。</span>
                  <button className="primary-button" disabled={submitting}>{submitting ? "保存中..." : "保存产品 CMS"}</button>
                </div>
              </form>
            )}
          </div>
        </section>
        )}
        {isPlatformScope && (
        <section className="admin-section platform-control-center">
          <div className="admin-section-head">
            <div>
              <h3>平台管理中枢</h3>
              <p className="seller-standard-note">参考 Seller Central 的平台角色:入驻审核、账号健康、商品/订单/物流/售后协同、顾客体验和审计留痕。</p>
            </div>
            <span>{platform?.counts.pendingApplications ?? 0} 个待审核店家</span>
          </div>
          <div className="platform-metrics">
            <Metric label="店家总数" value={String(platform?.counts.merchants ?? 0)} delta="平台供给" />
            <Metric label="顾客账号" value={String(platform?.counts.customers ?? 0)} delta="消费侧" />
            <Metric label="商品数量" value={String(platform?.counts.products ?? 0)} delta="目录池" />
            <Metric label="待处理订单" value={String(platform?.counts.openOrders ?? 0)} delta="履约协作" />
          </div>
          <div className="platform-workflow">
            {[
              ["1", "店家入驻", "提交身份、分类、收款二维码和银行账户"],
              ["2", "平台审核", "风控评分、资料完整度、认证状态"],
              ["3", "店铺开通", "店家主页、上新模板、收款和客服入口"],
              ["4", "交易履约", "订单、付款截图、物流二维码、售后评价闭环"]
            ].map(([step, title, body]) => (
              <article key={step}>
                <span>{step}</span>
                <strong>{title}</strong>
                <small>{body}</small>
              </article>
            ))}
          </div>
          <div className="platform-grid">
            <section className="platform-card seller-application-queue">
              <div className="platform-card-head">
                <h4>店家入驻审核</h4>
                <span>{platform?.counts.pendingApplications ?? 0} pending</span>
              </div>
              <div className="platform-list">
                {(platform?.applications ?? []).slice(0, 6).map((application) => (
                  <article className="platform-application-row" key={application.id}>
                    <div>
                      <strong>{application.store_name}</strong>
                      <span>{application.owner_name} · {application.category} · 风险 {application.risk_score}</span>
                      <small>{application.phone} · {application.email}</small>
                    </div>
                    <b className={`status-pill ${application.status}`}>{application.status}</b>
                    {application.status === "pending" && (
                      <div className="platform-row-actions">
                        <button type="button" onClick={() => reviewApplicationAction(application.id, "rejected")} disabled={submitting}>驳回</button>
                        <button className="primary-button" type="button" onClick={() => reviewApplicationAction(application.id, "approved")} disabled={submitting}>批准</button>
                      </div>
                    )}
                  </article>
                ))}
                {!platform?.applications.length && <p className="empty-cart-note">暂无入驻申请。新店家提交后会出现在这里。</p>}
              </div>
            </section>
            <section className="platform-card">
              <div className="platform-card-head">
                <h4>店家与顾客管理</h4>
                <span>生态目录</span>
              </div>
              <div className="platform-directory">
                <div>
                  <strong>店家</strong>
                  {(platform?.merchants ?? []).slice(0, 5).map((merchant) => (
                    <span key={merchant.id}>{merchant.name} · {merchant.level}</span>
                  ))}
                </div>
                <div>
                  <strong>顾客</strong>
                  {(platform?.customers ?? []).slice(0, 5).map((customer) => (
                    <span key={customer.id}>{customer.name} · {customer.level}</span>
                  ))}
                </div>
              </div>
            </section>
            <section className="platform-card">
              <div className="platform-card-head">
                <h4>平台协作告警</h4>
                <span>运营队列</span>
              </div>
              <div className="platform-alerts">
                <span>付款待卖家确认:{platform?.counts.paymentReviews ?? 0}</span>
                <span>物流待处理:{platform?.counts.logisticsPending ?? 0}</span>
                <span>退款/售后待处理:{platform?.counts.refundsPending ?? 0}</span>
                <span>入驻已批准:{platform?.counts.approvedApplications ?? 0}</span>
                <span>入驻已驳回:{platform?.counts.rejectedApplications ?? 0}</span>
              </div>
            </section>
            <section className="platform-card">
              <div className="platform-card-head">
                <h4>审计日志</h4>
                <span>可追溯</span>
              </div>
              <div className="platform-audit-list">
                {(platform?.auditLogs ?? []).slice(0, 6).map((log) => (
                  <span key={log.id}>{log.action} · {new Date(log.created_at).toLocaleString()}</span>
                ))}
              </div>
            </section>
          </div>
        </section>
        )}
        {!isPlatformScope && (
        <>
        <section className="admin-section merchant-settings-section">
          <div className="admin-section-head">
            <div>
              <h3>店家资料与收款二维码</h3>
              <p className="seller-standard-note">按卖家入驻标准维护。信息按模块折叠,避免后台一次性铺开展示。</p>
            </div>
            <span>{merchantForm.level} · {merchantForm.verification_status === "verified" ? "已认证" : "待审核"}</span>
          </div>
          <form className="admin-product-form merchant-accordion-form" onSubmit={saveMerchant}>
            <details className="admin-accordion" open>
              <summary>
                <span><Store size={18} /><strong>基础资料</strong><small>店铺身份、电话、地址</small></span>
                <ChevronDown size={18} />
              </summary>
              <div className="admin-product-form accordion-grid">
                <label>
                  店家名称
                  <input required value={merchantForm.name} onChange={(event) => setMerchantForm({ ...merchantForm, name: event.target.value })} />
                </label>
                <label>
                  电话
                  <input required value={merchantForm.phone} onChange={(event) => setMerchantForm({ ...merchantForm, phone: event.target.value })} />
                </label>
                <label className="admin-wide">
                  地址
                  <input required value={merchantForm.address} onChange={(event) => setMerchantForm({ ...merchantForm, address: event.target.value })} />
                </label>
                <label className="admin-wide">
                  店家介绍
                  <textarea value={merchantForm.intro} onChange={(event) => setMerchantForm({ ...merchantForm, intro: event.target.value })} />
                </label>
              </div>
            </details>

            <details className="admin-accordion">
              <summary>
                <span><MessageCircle size={18} /><strong>社交联系与主页</strong><small>Facebook、Messenger、WhatsApp、店铺 URL</small></span>
                <ChevronDown size={18} />
              </summary>
              <div className="admin-product-form accordion-grid">
                <label>
                  Facebook
                  <input type="url" value={merchantForm.facebook_url} onChange={(event) => setMerchantForm({ ...merchantForm, facebook_url: event.target.value })} />
                </label>
                <label>
                  Messenger
                  <input type="url" value={merchantForm.messenger_url} onChange={(event) => setMerchantForm({ ...merchantForm, messenger_url: event.target.value })} />
                </label>
                <label>
                  WhatsApp 号码
                  <input value={merchantForm.whatsapp_number} onChange={(event) => setMerchantForm({ ...merchantForm, whatsapp_number: event.target.value })} />
                </label>
                <label>
                  店家主页 URL 标识
                  <input value={merchantForm.store_slug} onChange={(event) => setMerchantForm({ ...merchantForm, store_slug: event.target.value })} />
                </label>
              </div>
            </details>

            <details className="admin-accordion">
              <summary>
                <span><ShieldCheck size={18} /><strong>经营资质</strong><small>分类、等级、经营类型、认证状态</small></span>
                <ChevronDown size={18} />
              </summary>
              <div className="admin-product-form accordion-grid">
                <label>
                  店家分类
                  <select value={merchantForm.category} onChange={(event) => setMerchantForm({ ...merchantForm, category: event.target.value })}>
                    <option value="Digital Store">数码店</option>
                    <option value="Official Reseller">授权经销商</option>
                    <option value="Accessories Specialist">配件专营</option>
                    <option value="Local Service Store">本地服务店</option>
                  </select>
                </label>
                <label>
                  店家等级
                  <select value={merchantForm.level} onChange={(event) => setMerchantForm({ ...merchantForm, level: event.target.value })}>
                    <option value="Verified Seller">认证卖家</option>
                    <option value="Professional Seller">专业卖家</option>
                    <option value="Premium Seller">优选卖家</option>
                    <option value="New Seller">新卖家</option>
                  </select>
                </label>
                <label>
                  经营类型
                  <select value={merchantForm.business_type} onChange={(event) => setMerchantForm({ ...merchantForm, business_type: event.target.value })}>
                    <option value="Corporate">公司</option>
                    <option value="Individual">个人</option>
                    <option value="Brand Owner">品牌方</option>
                    <option value="Distributor">经销商</option>
                  </select>
                </label>
                <label>
                  认证状态
                  <select value={merchantForm.verification_status} onChange={(event) => setMerchantForm({ ...merchantForm, verification_status: event.target.value })}>
                    <option value="verified">已认证</option>
                    <option value="pending">待审核</option>
                    <option value="rejected">需补充资料</option>
                  </select>
                </label>
              </div>
            </details>

            <details className="admin-accordion">
              <summary>
                <span><QrCode size={18} /><strong>收款信息</strong><small>银行账户、收款二维码、购买弹窗同步</small></span>
                <ChevronDown size={18} />
              </summary>
              <div className="admin-product-form accordion-grid">
                <label className="admin-wide">
                  店家银行账户
                  <input required value={merchantForm.bank_account} onChange={(event) => setMerchantForm({ ...merchantForm, bank_account: event.target.value })} />
                </label>
                <label className="admin-wide">
                  上传收款二维码
                  <input type="file" accept="image/*" onChange={(event) => chooseQr(event.target.files?.[0] ?? null)} />
                </label>
                <div className="admin-wide merchant-qr-preview">
                  <img src={qrPreview || merchantForm.payment_qr_url || "/assets/lao-online-shopping.png"} alt="店家收款二维码预览" />
                  <div>
                    <strong>主页购买弹窗将使用此二维码</strong>
                    <span>{qrFile ? qrFile.name : "未选择新二维码"}</span>
                  </div>
                </div>
              </div>
            </details>

            <div className="admin-form-status merchant-save-bar">
              <span>保存后同步店家主页、立即购买收款区和社交咨询入口。</span>
              <button className="primary-button" disabled={submitting}>{submitting ? "保存中..." : "保存店家资料"}</button>
            </div>
          </form>
        </section>
        <section className="admin-section">
          <h3>店家上新</h3>
          <form className="admin-product-form" onSubmit={publishProduct}>
            <div className="admin-wide launch-template-panel">
              <div className="template-panel-head">
                <div>
                  <strong>选择上新模板</strong>
                  <span>不同品类自动带出不同参数、卖点、详情结构和素材清单。</span>
                </div>
                <select value={newProduct.templateId} onChange={(event) => applyLaunchTemplate(event.target.value)} aria-label="上新模板">
                  {productLaunchTemplates.map((template) => (
                    <option value={template.id} key={template.id}>{template.label}</option>
                  ))}
                </select>
              </div>
              <div className="template-card-grid">
                {productLaunchTemplates.map((template) => (
                  <button
                    className={newProduct.templateId === template.id ? "template-card active" : "template-card"}
                    type="button"
                    key={template.id}
                    onClick={() => applyLaunchTemplate(template.id)}
                  >
                    <strong>{template.label}</strong>
                    <span>{productCategoryOptions.find((option) => option.value === template.categoryId)?.label}</span>
                    <small>{template.subtitle}</small>
                  </button>
                ))}
              </div>
              <div className="template-guidance-grid">
                <div>
                  <strong>图片/视频素材</strong>
                  <ul>
                    {selectedLaunchTemplate.mediaChecklist.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
                <div>
                  <strong>详情页内容</strong>
                  <ul>
                    {selectedLaunchTemplate.contentChecklist.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            </div>
            <label>
              产品名称
              <input required value={newProduct.title} onChange={(event) => setNewProduct({ ...newProduct, title: event.target.value })} />
            </label>
            <label>
              副标题
              <input required value={newProduct.subtitle} onChange={(event) => setNewProduct({ ...newProduct, subtitle: event.target.value })} />
            </label>
            <label>
              URL 标识
              <input value={newProduct.slug} onChange={(event) => setNewProduct({ ...newProduct, slug: event.target.value })} placeholder="留空自动生成" />
            </label>
            <label>
              售价
              <input required type="number" min="0" step="0.01" value={newProduct.price} onChange={(event) => setNewProduct({ ...newProduct, price: event.target.value })} />
            </label>
            <label>
              原价
              <input type="number" min="0" step="0.01" value={newProduct.compareAt} onChange={(event) => setNewProduct({ ...newProduct, compareAt: event.target.value })} />
            </label>
            <label>
              分类
              <select value={newProduct.categoryId} onChange={(event) => setNewProduct({ ...newProduct, categoryId: event.target.value })}>
                {productCategoryOptions.map((option) => (
                  <option value={option.value} key={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="admin-wide">
              核心卖点
              <textarea className="launch-selling-points" value={newProduct.sellingPoints} onChange={(event) => setNewProduct({ ...newProduct, sellingPoints: event.target.value })} />
            </label>
            <label className="admin-wide">
              品类参数
              <textarea className="launch-specs-textarea" value={newProduct.specs} onChange={(event) => setNewProduct({ ...newProduct, specs: event.target.value })} />
            </label>
            <label className="admin-wide">
              产品介绍
              <textarea className="launch-description-textarea" value={newProduct.description} onChange={(event) => setNewProduct({ ...newProduct, description: event.target.value })} />
            </label>
            <label className="admin-wide">
              上新照片(主图 + 缩略图)
              <input type="file" accept="image/*" multiple onChange={(event) => chooseImages(event.target.files)} />
            </label>
            <div className="admin-wide admin-gallery-preview">
              <div className="thumb-stack">
                {(imagePreviews.length ? imagePreviews : ["/assets/lao-online-shopping.png", "/assets/lao-online-shopping.png", "/assets/lao-online-shopping.png"]).map((src, index) => (
                  <span className={index === 0 ? "thumb active admin-thumb-preview" : "thumb admin-thumb-preview"} key={`${src}-${index}`}>
                    <ImageCrop src={src} variant={index} label={`上新照片 ${index + 1}`} />
                  </span>
                ))}
              </div>
              <div className="product-canvas admin-product-canvas">
                <SelectedMedia src={imagePreviews[0] || "/assets/lao-online-shopping.png"} variant={0} isVideo={false} />
              </div>
            </div>
            <label className="admin-wide">
              上新视频(最长 3 分钟)
              <input type="file" accept="video/*" onChange={(event) => chooseVideo(event.target.files?.[0] ?? null)} />
            </label>
            <div className="admin-form-status">
              <span>{videoFile ? `${videoFile.name} · ${formatDuration(videoDuration)}` : "未选择视频"}</span>
              <button className="primary-button" disabled={submitting}>{submitting ? "发布中..." : "发布上新"}</button>
            </div>
          </form>
        </section>
        <section className="admin-section batch-new-products">
          <div className="admin-section-head">
            <div>
              <h3>AI 批量上新</h3>
              <p className="seller-standard-note">粘贴商品清单,AI 自动生成可编辑草稿。发布前可逐项修改标题、卖点、价格、分类和介绍。</p>
            </div>
            <span>{batchDrafts.length ? String(batchDrafts.length) + " 条草稿" : "自动化上新"}</span>
          </div>
          <div className="batch-ai-panel">
            <label className="admin-wide">
              批量清单 / AI 上新需求
              <textarea value={batchSeed} onChange={(event) => setBatchSeed(event.target.value)} />
            </label>
            <div className="batch-ai-actions">
              <button type="button" className="secondary-button" onClick={generateBatchDrafts}>AI 生成上新草稿</button>
              <button type="button" className="primary-button" disabled={submitting || !batchDrafts.some((draft) => draft.selected)} onClick={publishBatchProducts}>
                {submitting ? "批量发布中..." : "发布选中商品"}
              </button>
            </div>
          </div>
          {batchDrafts.length > 0 && (
            <div className="batch-draft-list">
              {batchDrafts.map((draft, index) => (
                <article className="batch-draft-card" key={draft.id}>
                  <label className="batch-select">
                    <input type="checkbox" checked={draft.selected} onChange={(event) => updateBatchDraft(draft.id, { selected: event.target.checked })} />
                    草稿 {index + 1}
                  </label>
                  <div className="admin-product-form batch-draft-grid">
                    <label>
                      产品名称
                      <input value={draft.title} onChange={(event) => updateBatchDraft(draft.id, { title: event.target.value })} />
                    </label>
                    <label>
                      副标题
                      <input value={draft.subtitle} onChange={(event) => updateBatchDraft(draft.id, { subtitle: event.target.value })} />
                    </label>
                    <label>
                      售价
                      <input type="number" min="0" step="0.01" value={draft.price} onChange={(event) => updateBatchDraft(draft.id, { price: event.target.value })} />
                    </label>
                    <label>
                      原价
                      <input type="number" min="0" step="0.01" value={draft.compareAt} onChange={(event) => updateBatchDraft(draft.id, { compareAt: event.target.value })} />
                    </label>
                    <label>
                      分类
                      <select value={draft.categoryId} onChange={(event) => updateBatchDraft(draft.id, { categoryId: event.target.value })}>
                        {productCategoryOptions.map((option) => (
                          <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="admin-wide">
                      产品介绍
                      <textarea value={draft.description} onChange={(event) => updateBatchDraft(draft.id, { description: event.target.value })} />
                    </label>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        <section className="admin-section">
          <h3>当前产品视频</h3>
          <form className="admin-product-form" onSubmit={uploadCurrentVideo}>
            <div className="admin-wide current-video-row">
              <span>{currentVideo ? `已上传:${currentVideo.label}` : "当前产品还没有可播放视频"}</span>
              <span>限制:180 秒以内</span>
            </div>
            <label className="admin-wide">
              上传/替换视频
              <input type="file" accept="video/*" onChange={(event) => chooseVideo(event.target.files?.[0] ?? null)} />
            </label>
            <div className="admin-form-status">
              <span>{videoFile ? `${videoFile.name} · ${formatDuration(videoDuration)}` : "未选择视频"}</span>
              <button className="secondary-button" disabled={submitting}>上传到当前产品</button>
            </div>
          </form>
          {adminError && <p className="form-error">{adminError}</p>}
          {adminSuccess && <p className="form-success">{adminSuccess}</p>}
        </section>
        <section className="admin-section material-tool">
          <h3>店家图片生成工具与网页信息</h3>
          <p className="seller-standard-note">根据当前上新内容生成图片提示词和网页信息,方便店家统一主图、详情图、短视频封面和商品页文案。</p>
          <div className="material-grid">
            <label>
              图片生成提示词
              <textarea readOnly value={materialPrompt} />
            </label>
            <label>
              网页信息摘要
              <textarea readOnly value={pageBrief} />
            </label>
          </div>
          <div className="material-actions">
            <button type="button" className="secondary-button" onClick={() => copyMaterial(materialPrompt)}>复制图片提示词</button>
            <button type="button" className="secondary-button" onClick={() => copyMaterial(pageBrief)}>复制网页信息</button>
          </div>
        </section>
        <section className="admin-section fulfillment-center">
          <h3>店家物流信息管理</h3>
          <p className="seller-standard-note">按亚马逊店家履约逻辑管理订单:付款审核、拣货打包、上传物流二维码、运输追踪、签收完成。</p>
          <div className="fulfillment-stats">
            <article><strong>{awaitingPaymentReview.length}</strong><span>待确认收款</span></article>
            <article><strong>{readyToShip.length}</strong><span>待发货</span></article>
            <article><strong>{inTransit.length}</strong><span>运输中</span></article>
            <article><strong>{deliveredOrders.length}</strong><span>已完成</span></article>
          </div>
          <div className="fulfillment-board">
            {[
              ["待确认收款", awaitingPaymentReview, "AI 已通过付款截图,卖家需要复核银行到账。"],
              ["待发货", readyToShip, "打印拣货单,打包商品,上传物流二维码给顾客追踪。"],
              ["运输中", inTransit, "顾客可查看物流二维码,签收后卖家完成物流。"]
            ].map(([title, orders, helper]) => (
              <div className="fulfillment-column" key={String(title)}>
                <h4>{String(title)}</h4>
                <p>{String(helper)}</p>
                {(orders as Order[]).slice(0, 4).map((order) => (
                  <article className="fulfillment-card" key={order.id}>
                    <div>
                      <strong>{order.order_number}</strong>
                      <span>{order.customer_name} · {formatMoney(order.total_amount)}</span>
                      <small>{order.payment_status} · {order.logistics_status}</small>
                    </div>
                    <div className="fulfillment-actions">
                      {order.payment_ai_status === "passed" && order.seller_payment_status !== "confirmed" && (
                        <button type="button" disabled={submitting} onClick={() => confirmOrderPaymentAction(order.id)}>确认收款</button>
                      )}
                      {order.payment_status === "confirmed" && order.logistics_status !== "completed" && (
                        <label>
                          上传物流码
                          <input type="file" accept="image/*" onChange={(event) => uploadOrderLogisticsAction(order.id, event.target.files?.[0] ?? null)} />
                        </label>
                      )}
                      {order.logistics_qr_url && order.logistics_status !== "completed" && (
                        <button type="button" disabled={submitting} onClick={() => completeOrderLogisticsAction(order.id)}>确认签收</button>
                      )}
                    </div>
                  </article>
                ))}
                {!(orders as Order[]).length && <div className="empty-account-state">暂无订单</div>}
              </div>
            ))}
          </div>
        </section>
        <section className="admin-section">
          <h3>最近订单</h3>
          {adminOrders.slice(0, 5).map((order) => (
            <div className="admin-order" key={order.id}>
              <div>
                <span>{order.order_number}</span>
                <small>{order.source_channel} · {order.payment_status} · {order.logistics_status}</small>
              </div>
              <strong>{formatMoney(order.total_amount)}</strong>
              <div className="admin-order-actions">
                {order.payment_proof_url && <a href={order.payment_proof_url} target="_blank" rel="noreferrer">付款截图</a>}
                {order.payment_ai_status === "passed" && order.seller_payment_status !== "confirmed" && (
                  <button type="button" disabled={submitting} onClick={() => confirmOrderPaymentAction(order.id)}>确认收款</button>
                )}
                {order.payment_status === "confirmed" && order.logistics_status !== "completed" && (
                  <label>
                    上传物流码
                    <input type="file" accept="image/*" onChange={(event) => uploadOrderLogisticsAction(order.id, event.target.files?.[0] ?? null)} />
                  </label>
                )}
                {order.logistics_qr_url && order.logistics_status !== "completed" && (
                  <button type="button" disabled={submitting} onClick={() => completeOrderLogisticsAction(order.id)}>完成物流</button>
                )}
              </div>
            </div>
          ))}
          {!adminOrders.length && <p className="muted">暂无订单,提交一笔订单后这里会立即更新。</p>}
        </section>
        </>
        )}
      </aside>
    </div>
  );
}

function MobileMenu({
  onClose,
  onProductSelect,
  onSearchResults,
  onAccountOpen,
  onSupportOpen
}: {
  onClose: () => void;
  onProductSelect: (slug: string) => void;
  onSearchResults: (query: string) => void;
  onAccountOpen: (entry?: AccountEntry) => void;
  onSupportOpen: (channel: "whatsapp" | "messenger" | "phone") => void;
}) {
  const openMenuTarget = (target: MenuTarget | undefined, fallbackQuery: string) => {
    if (!target) {
      onSearchResults(fallbackQuery);
    } else if (target.type === "product") {
      onProductSelect(target.slug);
    } else if (target.type === "account") {
      onAccountOpen(target.entry);
    } else if (target.type === "support") {
      onSupportOpen(target.channel);
    } else {
      onSearchResults(target.query);
    }
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <nav className="mobile-menu">
        <button className="modal-close" onClick={onClose} aria-label="关闭菜单">
          <X size={18} />
        </button>
        {nav.map((item) => (
          <details key={item.label} open>
            <summary>{item.label}</summary>
            {item.columns.flatMap((column) =>
              column.children.map((child) => (
                <details key={child.label}>
                  <summary>
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        openMenuTarget(child.target, child.label);
                      }}
                    >
                      {child.label}
                    </button>
                  </summary>
                  {child.children.map((third) => (
                    <button
                      type="button"
                      className="mobile-menu-leaf"
                      key={third.label}
                      onClick={() => {
                        openMenuTarget(third.target ?? { type: "product", slug: third.slug }, third.label);
                      }}
                    >
                      {third.label}
                    </button>
                  ))}
                </details>
              ))
            )}
          </details>
        ))}
      </nav>
    </div>
  );
}

function OrderResult({ order, onClose }: { order: Order; onClose: () => void }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="订单结果">
      <div className="modal-card">
        <button className="modal-close" onClick={onClose} aria-label="关闭">
          <X size={18} />
        </button>
        <CheckCircle2 className="success-icon" />
        <h2>订单状态</h2>
        <p>订单号:{order.order_number}</p>
        <strong>{formatMoney(order.total_amount)}</strong>
        <section className="order-payment-panel">
          {order.payment_qr_data_url && order.payment_status !== "confirmed" && (
            <div className="order-qr-box">
              <img src={order.payment_qr_data_url} alt="订单动态付款二维码" />
              <div>
                <strong>动态付款二维码</strong>
                <span>金额已绑定:{formatMoney(order.total_amount)}</span>
              </div>
            </div>
          )}
          <div className="order-status-grid">
            <span>付款:{order.payment_status}</span>
            <span>AI:{order.payment_ai_status}</span>
            <span>卖家确认:{order.seller_payment_status}</span>
            <span>物流:{order.logistics_status}</span>
          </div>
          {order.payment_ai_notes && <p>{order.payment_ai_notes}</p>}
          {order.payment_proof_url && <a href={order.payment_proof_url} target="_blank" rel="noreferrer">查看付款截图</a>}
          {order.logistics_qr_url && (
            <div className="order-qr-box">
              <img src={order.logistics_qr_url} alt="物流二维码" />
              <div>
                <strong>物流二维码</strong>
                <span>扫码查看配送、签收与物流状态。</span>
              </div>
            </div>
          )}
          {order.logistics_status === "completed" && <p>订单已完成,可在顾客评价区提交产品与服务评价。</p>}
        </section>
        <div className="timeline">
          {(order.logistics ?? [
            {
              id: "demo",
              status: order.status,
              location: "Vientiane Fulfillment Center",
              message: "订单已创建,等待仓库处理。",
              event_time: new Date().toISOString()
            }
          ]).map((event) => (
            <div key={event.id}>
              <span />
              <div>
                <strong>{event.message}</strong>
                <small>{event.location} · {new Date(event.event_time).toLocaleString()}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <span className="stars" aria-label={`${value} 星`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} size={15} fill={star <= Math.round(value) ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

function Metric({ label, value, delta }: { label: string; value: string; delta: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{delta}</small>
    </div>
  );
}

function LockBadge() {
  return (
    <div className="lock-badge">
      <ClipboardCheck size={18} />
    </div>
  );
}

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDone, 2200);
    return () => window.clearTimeout(timer);
  }, [onDone]);
  return <div className="toast">{message}</div>;
}

function Skeleton() {
  return (
    <div className="app-shell loading-shell">
      <main className="store-board">
        <div className="topbar skeleton-line" />
        <div className="hero-grid">
          <div className="gallery-card skeleton-block" />
          <div className="product-panel skeleton-block" />
        </div>
      </main>
    </div>
  );
}

export default App;
